import React, { useState, useEffect, createContext, useContext, useCallback, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// ---------------------------------------------------------------------------
// Per-tab auth isolation.
//
// The user opens two tabs — one logged in as coy@concretecoats, one as
// coy@crewsyncpro — and expects both to stay alive simultaneously without
// leaking data. The web platform stores auth in `localStorage`, which is
// SHARED across every tab of the same origin, so any login (or
// impersonation) in one tab silently overwrites the identity of every
// other tab.
//
// The fix: intercept the auth-related localStorage keys and route them to
// `sessionStorage`, which is scoped to a single tab. A one-time seed from
// localStorage on first load preserves existing logins for users who were
// already signed in when the app updated.
//
// Trade-off: closing a tab ends that tab's session (user must re-login on
// a brand new tab). That's the same trade-off every multi-account-capable
// web app makes (Google's Gmail "Add account" under the hood uses the same
// technique).
// ---------------------------------------------------------------------------
(function installAuthStoragePartitioner() {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return;
  // Keys that identify *who is logged in* for this tab. Everything else
  // (UI prefs, drafts, heartbeats) stays in regular localStorage so shared
  // state like theme/collapsed-sidebar still syncs across tabs.
  const AUTH_KEYS = new Set([
    'token',
    'user',
    'original_admin_token',
    'original_admin_user',
    'lastActivity',
  ]);
  // Sentinel on sessionStorage so we only seed from localStorage once per
  // tab open. Without this, logging out would cause the next read to
  // re-seed from localStorage and log the user back in.
  const SEEDED_FLAG = '__auth_seeded__';
  if (!sessionStorage.getItem(SEEDED_FLAG)) {
    // 1a. Cross-tab handoff via URL hash. When another authenticated tab
    // opens a new window (e.g. "Print Report" opens /jobs/:id/report in
    // a new tab), it appends `#__auth=<base64>` to the URL. That hash
    // stays client-side and never hits the server. We consume it here to
    // seed sessionStorage before the SPA even boots, then strip it from
    // the URL so it doesn't leak into history.
    try {
      const hash = window.location.hash || '';
      const match = hash.match(/[#&]__auth=([^&]+)/);
      if (match) {
        const decoded = JSON.parse(atob(decodeURIComponent(match[1])));
        AUTH_KEYS.forEach((k) => {
          if (decoded[k] !== undefined && decoded[k] !== null) {
            sessionStorage.setItem(k, decoded[k]);
          }
        });
        // Strip the __auth fragment from the URL so it disappears from
        // history / bookmarks.
        const cleaned = hash.replace(/(^|[#&])__auth=[^&]+/, '$1').replace(/^#&/, '#').replace(/#$/, '');
        try {
          window.history.replaceState(null, '', window.location.pathname + window.location.search + cleaned);
        } catch (_) {}
      }
    } catch (_) {}

    // 1b. First-load seed from localStorage (for users who were already
    // signed in when the partitioner shipped).
    AUTH_KEYS.forEach((k) => {
      const v = window.localStorage.getItem(k);
      if (v !== null && sessionStorage.getItem(k) === null) {
        sessionStorage.setItem(k, v);
      }
    });
    sessionStorage.setItem(SEEDED_FLAG, '1');
  }
  // Patch the three mutating methods — auth keys go through sessionStorage,
  // everything else falls through to the native localStorage.
  const realGet = window.localStorage.getItem.bind(window.localStorage);
  const realSet = window.localStorage.setItem.bind(window.localStorage);
  const realRemove = window.localStorage.removeItem.bind(window.localStorage);
  window.localStorage.getItem = function (k) {
    if (!AUTH_KEYS.has(k)) return realGet(k);
    // Prefer the per-tab identity (sessionStorage). Fall back to the
    // persisted localStorage copy so a browser restart doesn't force a
    // re-login — the first-load seed above normally covers this, but
    // keep the explicit fallback for any read that happens after
    // sessionStorage is cleared mid-session (e.g. extensions, DevTools).
    const sv = sessionStorage.getItem(k);
    return sv !== null ? sv : realGet(k);
  };
  window.localStorage.setItem = function (k, v) {
    if (AUTH_KEYS.has(k)) {
      // Write to sessionStorage so this tab owns its own identity (lets
      // the user open a second tab as a different tenant without the
      // two tabs stomping on each other).
      sessionStorage.setItem(k, v);
      // ALSO write to localStorage so the login survives a tab close or
      // browser restart (users expect to stay signed in for the full
      // 12-hour JWT lifetime, not just until the tab closes).
      // Feb 2026 — user reported: "Login on deployed app needs to keep
      // me logged in longer. I thought we were set up for six or eight
      // hours?" Root cause was this partitioner wiping the localStorage
      // copy on every write, so every tab close = forced re-login.
      try { realSet(k, v); } catch (_) {}
      return;
    }
    realSet(k, v);
  };
  window.localStorage.removeItem = function (k) {
    if (AUTH_KEYS.has(k)) {
      sessionStorage.removeItem(k);
      try { realRemove(k); } catch (_) {}
      return;
    }
    realRemove(k);
  };

  // Expose a globally-available helper for opening SPA routes in a new
  // tab with the current tab's auth. New tabs otherwise start with an
  // empty sessionStorage (see partitioner above) and get redirected to
  // /login — which shows up as "Print Report opens the login screen"
  // reports from users. Usage:
  //     window.openWithAuth('/jobs/xyz/report');
  window.openWithAuth = function (path, target = '_blank') {
    try {
      const payload = {};
      AUTH_KEYS.forEach((k) => {
        const v = sessionStorage.getItem(k);
        if (v !== null) payload[k] = v;
      });
      const encoded = encodeURIComponent(btoa(JSON.stringify(payload)));
      const sep = path.includes('#') ? '&' : '#';
      const url = `${path}${sep}__auth=${encoded}`;
      return window.open(url, target, 'noopener');
    } catch (err) {
      // If anything blows up (e.g. sessionStorage disabled), fall back
      // to a plain open — the new tab will just require a re-login.
      return window.open(path, target, 'noopener');
    }
  };
})();

import {
  LayoutDashboard, Users, Briefcase, Clock, FileText, FileSignature,
  Receipt, CreditCard, Wallet, ScrollText, Settings, LogOut, Menu, X,
  Plus, Search, ChevronRight, ChevronLeft, TrendingUp, DollarSign, Calendar, CheckCircle,
  AlertCircle, AlertTriangle, MoreVertical, Edit, Trash2, Eye, EyeOff, Send, Building2, Calculator,
  Target, PiggyBank, Percent, HardHat, Wrench, Truck, Pencil, Shield, UserPlus, ClipboardCheck,
  Copy, RefreshCw, Crown, Zap, MessageSquare, ExternalLink, Check, XCircle, HelpCircle,
  Mail, Phone, Bell, Image, Upload, Camera, ArrowLeft, Key, Mic, MicOff, Volume2, Lock, Award, Lightbulb, Brain,
  Star, Sparkles, ArrowUp, ArrowDown, GripVertical, User, MapPin, ShoppingCart, Printer, Download, Package, Globe,
  BarChart3, Activity, Database, Beaker, Rocket, RotateCcw, ClipboardList, Save, ListTodo, Store, ShoppingBag, FileDown, FolderOpen, FolderPlus, MessageCircle, Tag, FilePlus,
  FileCheck, CalendarDays, Loader2, Gift, Code2, Share2, Link2, Twitter, Linkedin, History, PlayCircle, Navigation, ShieldCheck, Ruler, ZoomIn, Bug, BookOpen, Car
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import CustomerCorrespondence from "@/components/CustomerCorrespondence";
import EmailComposer from "@/components/EmailComposer";
import { KnowYourNumbersBreakdown, PrintableJobReport } from "@/components/KnowYourNumbersBreakdown";
import EmailTemplateManager from "@/components/EmailTemplateManager";
import FormattedNotes from "@/components/FormattedNotes";
import { SortableSidebarItem, DraggableContainer, SortableContext, verticalListSortingStrategy, arrayMove, useDroppable } from "@/components/DraggableLayout";
import { SMSTab, SMSSendDialog, QuickSMSButton } from "@/components/SMSComponent";
import PoweredByCrewSync from "@/components/PoweredByCrewSync";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import "@/App.css";

// Import extracted page components
import LoginPage from "@/pages/LoginPage";
import LandingPage from "@/pages/LandingPage";

// Shown while a route chunk downloads. Deliberately minimal (and
// matching the app shell) so it reads as "loading", not as a blank page.
function RouteLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-zinc-950" data-testid="route-loader">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-2 border-zinc-700 border-t-primary animate-spin" />
        <p className="text-xs uppercase tracking-widest text-zinc-500">Loading</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Oct 2026 — route-level code splitting. Every page used to ship in
// one 4.7 MB main.js (1.1 MB gzipped), which is what made the app
// crawl on first load / login. Each page is now its own chunk that
// downloads only when that route is opened. Login + Landing stay
// eager so the first screen paints instantly.
// ---------------------------------------------------------------
const PublicBookingPage = lazy(() => import("@/pages/PublicBookingPage"));
const PublicPricingPage = lazy(() => import("@/pages/PublicPricingPage"));
const SalesReadyChecklistPage = lazy(() => import("@/pages/SalesReadyChecklistPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const CustomersPage = lazy(() => import("@/pages/CustomersPage"));
const SiteAssessmentsPage = lazy(() => import("@/pages/SiteAssessmentsPage"));
const JobsPage = lazy(() => import("@/pages/JobsPage"));
const UpcomingMaintenancePage = lazy(() => import("@/pages/UpcomingMaintenancePage"));
const JobReportPage = lazy(() => import("@/pages/JobReportPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const LeadsPage = lazy(() => import("@/pages/LeadsPage"));
const ConfirmAppointmentPage = lazy(() => import("@/pages/ConfirmAppointmentPage"));
const TimeTrackingPage = lazy(() => import("@/pages/TimeTrackingPage"));
const TeamManagementPage = lazy(() => import("@/pages/TeamManagementPage"));
const SignDocumentPage = lazy(() => import("@/pages/SignDocumentPage"));
const SignPolicyDocumentPage = lazy(() => import("@/pages/SignPolicyDocumentPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const DocumentSigningPage = lazy(() => import("@/pages/DocumentSigningPage"));
const ShoppingListActionPage = lazy(() => import("@/pages/ShoppingListActionPage"));
const DataImportPage = lazy(() => import("@/pages/DataImportPage"));
const HelpVideosPage = lazy(() => import("@/pages/HelpVideosPage"));
const HelpCenterPage = lazy(() => import("@/pages/HelpCenterPage"));
const EstimateFollowupTemplatesPage = lazy(() => import("@/pages/EstimateFollowupTemplatesPage"));
const InvoiceReminderTemplatesPage = lazy(() => import("@/pages/InvoiceReminderTemplatesPage"));
const EmailDeliverabilityPage = lazy(() => import("@/pages/EmailDeliverabilityPage"));
const WelcomePage = lazy(() => import("@/pages/WelcomePage"));
const ServiceRegionsPage = lazy(() => import("@/pages/ServiceRegionsPage"));
const CompanyProfitabilityPage = lazy(() => import("@/pages/CompanyProfitabilityPage"));
const FinancialAnalyticsPage = lazy(() => import("@/pages/FinancialAnalyticsPage"));
const RoutesPage = lazy(() => import("@/pages/RoutesPage"));
const TakeoffEditorPage = lazy(() => import("@/pages/TakeoffEditorPage"));
const TakeoffsDashboardPage = lazy(() => import("@/pages/TakeoffsDashboardPage"));
const PublicRoutePage = lazy(() => import("@/pages/PublicRoutePage"));
const PublicTaskPage = lazy(() => import("@/pages/PublicTaskPage"));
const PublicAssessmentPage = lazy(() => import("@/pages/PublicAssessmentPage"));
const AssessmentPrintPage = lazy(() => import("@/pages/AssessmentPrintPage"));
const PublicReviewPage = lazy(() => import("@/pages/PublicReviewPage"));
const ReviewsAdminPage = lazy(() => import("@/pages/ReviewsAdminPage"));
const EmployeeJobsPage = lazy(() => import("@/pages/EmployeeJobsPage"));
const EmployeeTasksPage = lazy(() => import("@/pages/EmployeeTasksPage"));
const EmployeeTimeCardPage = lazy(() => import("@/pages/EmployeeTimeCardPage"));
const EmployeeRewardsPage = lazy(() => import("@/pages/EmployeeRewardsPage"));
const CodeHealthPage = lazy(() => import("@/pages/CodeHealthPage"));
const PlatformAdminPage = lazy(() => import("@/pages/PlatformAdminPage"));
const FrontendErrorsPage = lazy(() => import("@/pages/FrontendErrorsPage"));
const OnboardingChecklistPrintPage = lazy(() => import("@/pages/OnboardingChecklistPrintPage"));
const PlatformOnboardingPlanPage = lazy(() => import("@/pages/PlatformOnboardingPlanPage"));
const VehiclesPage = lazy(() => import("@/pages/VehiclesPage"));
const PlatformBillingPage = lazy(() => import("@/pages/PlatformBillingPage"));
const PlatformTenantRegionsPage = lazy(() => import("@/pages/PlatformTenantRegionsPage"));
const PlatformTenantRoutesPage = lazy(() => import("@/pages/PlatformTenantRoutesPage"));
const PlatformDeliverabilityHealthPage = lazy(() => import("@/pages/PlatformDeliverabilityHealthPage"));
const EstimatesPage = lazy(() => import("@/pages/EstimatesPage"));
const VendorsPage = lazy(() => import("@/pages/VendorsPage"));
const InvoicesPage = lazy(() => import("@/pages/InvoicesPage"));
const CustomerDetailPage = lazy(() => import("@/pages/CustomerDetailPage"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const MaintenanceRemindersPage = lazy(() => import("@/pages/MaintenanceRemindersPage"));
const MaterialsCatalogPage = lazy(() => import("@/pages/MaterialsCatalogPage"));
const RemindersPage = lazy(() => import("@/pages/RemindersPage"));
const WarrantyClaimsPage = lazy(() => import("@/pages/WarrantyClaimsPage"));
const PublicWarrantyForm = lazy(() => import("@/pages/PublicWarrantyForm"));
const SubcontractorsPage = lazy(() => import("@/pages/SubcontractorsPage"));
const CommunicationsPage = lazy(() => import("@/pages/CommunicationsPage"));
const PublicPaymentRequestPage = lazy(() => import("@/pages/PublicPaymentRequestPage"));
const ContractsPage = lazy(() => import("@/pages/ContractsPage"));
const SignedDocumentsPage = lazy(() => import("@/pages/SignedDocumentsPage"));
const PublicContractSignPage = lazy(() => import("@/pages/PublicContractSignPage"));
import FloatingHelpButton from "@/components/FloatingHelpButton";
import NotificationBell from "@/components/shared/NotificationBell";
import { JobAdditionalMaterials } from "@/components/JobAdditionalMaterials";
import { JobLaborSummary } from "@/components/JobLaborSummary";
import { JobCostSummary } from "@/components/JobCostSummary";
import ErrorBoundary from "@/components/ErrorBoundary";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Session timeout — 12 hours of INACTIVITY before auto-logout. Matches
// the backend JWT expiry so the JWT never dies mid-session. Long enough
// to cover a full work-day shift for a field crew.
//
// NOTE: setTimeout stores the delay as a signed 32-bit int. Any value
// > 2,147,483,647 ms (~24.8 days) overflows and fires IMMEDIATELY. 12
// hours is well under that cap, so the clamp is inert here — it's kept
// only as a safety net for anyone who raises this value later.
// Matches JWT_EXPIRATION_HOURS + useSessionTimeout.js so nothing can
// kick a user out before their JWT genuinely expires.
const SESSION_TIMEOUT = 12 * 60 * 60 * 1000; // 12 hours
const SAFE_MAX_TIMEOUT = 2147483647; // 2^31 - 1 ms, browser cap

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Global Edit Customer Context - allows editing customer from anywhere in the app
const EditCustomerContext = createContext(null);

export const useEditCustomer = () => useContext(EditCustomerContext);

// API Client with auth (exported for use in extracted page components)
export const apiClient = axios.create({ baseURL: API });

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

apiClient.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      // The old handler unconditionally wiped the token + redirected on any
      // 401. That's what caused the "job details boots me out" bug — a job
      // detail screen fires 8-10 sub-requests, and if ANY one hit a
      // transient 401 (backend cold start, race, or a per-endpoint
      // permission mismatch), the user got kicked out of the whole app.
      //
      // Guard the redirect so we only force logout when:
      //   1. The user isn't already on /login (avoid infinite reload)
      //   2. The request that failed was hitting an authenticated endpoint
      //      (skip 401s from optional/public endpoints and quick polls)
      //   3. The token itself is truly missing/expired — not just a
      //      per-endpoint 403-with-401-code from a permission mismatch.
      const path = window.location.pathname || '';
      const requestUrl = err.config?.url || '';
      const alreadyOnLogin = path === '/login' || path.startsWith('/login');
      const isPublicOrOptionalEndpoint =
        requestUrl.includes('/public/')
        || requestUrl.includes('/config')
        || requestUrl.includes('/health')
        || requestUrl.includes('/dashboard-stats'); // quiet background poll
      const currentToken = localStorage.getItem('token') || sessionStorage.getItem('token');
      // A 401 on a sub-request while we still have a token often means the
      // specific endpoint has stricter auth (e.g. platform-admin only) and
      // not that the session is dead. Let that request fail and stay put.
      if (currentToken && !alreadyOnLogin && !isPublicOrOptionalEndpoint) {
        // Verify the token is actually invalid by hitting a cheap endpoint
        // once — if it succeeds, don't log the user out. This absorbs the
        // transient 401s that were previously nuking the session.
        return apiClient.get('/auth/me').then(() => Promise.reject(err)).catch((meErr) => {
          if (meErr.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
          return Promise.reject(err);
        });
      }
      if (!currentToken && !alreadyOnLogin) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

// Session Timeout Hook - Auto logout after inactivity
function useSessionTimeout() {
  const { logout } = useAuth() || {};
  const timeoutRef = useRef(null);

  const resetTimeout = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (logout) {
        toast.warning("Session expired due to inactivity. Please login again.");
        logout();
        window.location.href = "/login";
      }
    }, Math.min(SESSION_TIMEOUT, SAFE_MAX_TIMEOUT));
  }, [logout]);

  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    const handleActivity = () => resetTimeout();
    
    events.forEach(event => window.addEventListener(event, handleActivity));
    resetTimeout();
    
    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [resetTimeout]);
}

// Content Protection Component - Prevents copying sensitive data
function ContentProtection({ children }) {
  useEffect(() => {
    const handleContextMenu = (e) => {
      // Allow right-click on inputs and textareas for user convenience
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      e.preventDefault();
    };

    const handleCopy = (e) => {
      // Allow copy in inputs/textareas
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;
      e.preventDefault();
      toast.info("Content is protected", { duration: 2000 });
    };

    const handleKeyDown = (e) => {
      // Prevent Ctrl+U (view source), Ctrl+Shift+I (dev tools), F12
      if ((e.ctrlKey && e.key === 'u') || (e.ctrlKey && e.shiftKey && e.key === 'I') || e.key === 'F12') {
        e.preventDefault();
      }
      // Prevent Ctrl+S (save page)
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
      }
      // Prevent Ctrl+P (print) - optional
      if (e.ctrlKey && e.key === 'p') {
        e.preventDefault();
      }
    };

    const handleDragStart = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('dragstart', handleDragStart);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, []);

  return <>{children}</>;
}

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) setUser(JSON.parse(stored));
    setLoading(false);
  }, []);

  // ---------------------------------------------------------------------
  // NOTE: A previous build had a cross-tab `storage` event listener that
  // force-reloaded any tab whose identity no longer matched localStorage.
  // That's now obsolete — `localStorage` writes for the auth keys are
  // intercepted by the partitioner in App.js and routed to sessionStorage
  // instead, so tabs can't overwrite each other's identity anymore. No
  // listener needed.
  // ---------------------------------------------------------------------

  // ---------------------------------------------------------------------
  // Tab heartbeat. Writes `tab_heartbeats[<tabId>] = Date.now()` to
  // localStorage every 5 seconds. The impersonation handler reads this to
  // warn the admin if more than one tab is open before they swap identity.
  // Stale entries (>10s old) are treated as closed tabs.
  // ---------------------------------------------------------------------
  useEffect(() => {
    const tabId = Math.random().toString(36).slice(2);
    const beat = () => {
      try {
        const map = JSON.parse(localStorage.getItem('tab_heartbeats') || '{}');
        map[tabId] = Date.now();
        // Prune stale entries while we're here so the list doesn't grow unbounded.
        for (const [k, ts] of Object.entries(map)) {
          if (Date.now() - ts > 30000) delete map[k];
        }
        localStorage.setItem('tab_heartbeats', JSON.stringify(map));
      } catch (_) {}
    };
    beat();
    const interval = setInterval(beat, 5000);
    const cleanup = () => {
      try {
        const map = JSON.parse(localStorage.getItem('tab_heartbeats') || '{}');
        delete map[tabId];
        localStorage.setItem('tab_heartbeats', JSON.stringify(map));
      } catch (_) {}
    };
    window.addEventListener('beforeunload', cleanup);
    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', cleanup);
      cleanup();
    };
  }, []);

  const login = async (email, password) => {
    const res = await apiClient.post("/auth/login", { email, password });
    // Clear any stale impersonation tokens on fresh login
    localStorage.removeItem("original_admin_token");
    localStorage.removeItem("original_admin_user");
    localStorage.setItem("token", res.data.access_token);
    localStorage.setItem("user", JSON.stringify(res.data.user));
    localStorage.setItem("lastActivity", Date.now().toString());
    setUser(res.data.user);
    return res.data;
  };

  const requestAccount = async (data) => {
    const res = await apiClient.post("/auth/request-account", data);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("lastActivity");
    // Reset the onboarding gate so a different admin logging in
    // in the same tab is still funnelled to /welcome when appropriate.
    sessionStorage.removeItem("onboarding_gate_checked");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, requestAccount, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

// Edit Customer Provider - Global dialog for editing customers from anywhere
function EditCustomerProvider({ children }) {
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editCustomerForm, setEditCustomerForm] = useState({});
  const [savingCustomer, setSavingCustomer] = useState(false);

  const openEditCustomer = (customer) => {
    setEditCustomerForm({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      zip_code: customer.zip_code || '',
      notes: customer.notes || '',
      tags: customer.tags || []
    });
    setEditingCustomer(customer);
  };

  const closeEditCustomer = () => {
    setEditingCustomer(null);
    setEditCustomerForm({});
  };

  const saveCustomer = async (onSuccess) => {
    if (!editingCustomer) return;
    
    setSavingCustomer(true);
    try {
      await apiClient.put(`/customers/${editingCustomer.id}`, editCustomerForm);
      toast.success("Customer updated successfully!");
      closeEditCustomer();
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update customer");
    }
    setSavingCustomer(false);
  };

  return (
    <EditCustomerContext.Provider value={{ 
      editingCustomer, 
      editCustomerForm, 
      setEditCustomerForm,
      openEditCustomer, 
      closeEditCustomer, 
      saveCustomer,
      savingCustomer 
    }}>
      {children}
      
      {/* Global Edit Customer Dialog */}
      <Dialog open={!!editingCustomer} onOpenChange={(open) => !open && closeEditCustomer()}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" /> Edit Customer
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Update customer information. Changes will sync across all jobs, estimates, and invoices.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">Customer Name *</Label>
              <Input
                value={editCustomerForm.name || ''}
                onChange={(e) => setEditCustomerForm(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1 bg-zinc-800 border-zinc-700"
                placeholder="Full name or company name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-300">Email</Label>
                <Input
                  type="email"
                  value={editCustomerForm.email || ''}
                  onChange={(e) => setEditCustomerForm(prev => ({ ...prev, email: e.target.value }))}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Phone</Label>
                <Input
                  type="tel"
                  value={editCustomerForm.phone || ''}
                  onChange={(e) => setEditCustomerForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-zinc-300">Street Address</Label>
              <Input
                value={editCustomerForm.address || ''}
                onChange={(e) => setEditCustomerForm(prev => ({ ...prev, address: e.target.value }))}
                className="mt-1 bg-zinc-800 border-zinc-700"
                placeholder="123 Main St"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-zinc-300">City</Label>
                <Input
                  value={editCustomerForm.city || ''}
                  onChange={(e) => setEditCustomerForm(prev => ({ ...prev, city: e.target.value }))}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label className="text-zinc-300">State</Label>
                <Input
                  value={editCustomerForm.state || ''}
                  onChange={(e) => setEditCustomerForm(prev => ({ ...prev, state: e.target.value }))}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label className="text-zinc-300">ZIP Code</Label>
                <Input
                  value={editCustomerForm.zip_code || ''}
                  onChange={(e) => setEditCustomerForm(prev => ({ ...prev, zip_code: e.target.value }))}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-zinc-300">Notes</Label>
              <Textarea
                value={editCustomerForm.notes || ''}
                onChange={(e) => setEditCustomerForm(prev => ({ ...prev, notes: e.target.value }))}
                className="mt-1 bg-zinc-800 border-zinc-700"
                rows={3}
                placeholder="Any additional notes about this customer..."
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" className="border-zinc-600" onClick={closeEditCustomer}>
              Cancel
            </Button>
            <Button 
              className="bg-primary hover:bg-primary/90 text-black"
              onClick={() => saveCustomer()}
              disabled={savingCustomer || !editCustomerForm.name}
            >
              {savingCustomer ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </EditCustomerContext.Provider>
  );
}

// Protected Route with Session Timeout
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  useSessionTimeout(); // Enable session timeout for protected routes
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" />;
  return <ContentProtection>{children}</ContentProtection>;
}

// Admin-only route with Session Timeout
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  useSessionTimeout(); // Enable session timeout for admin routes
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'employee') return <Navigate to="/my-jobs" />;
  // Platform admins must NOT land on tenant-scoped screens. When the
  // aggregate endpoints (dashboard analytics, invoices list, etc.) run
  // with no company_id, `get_tenant_filter` returns {} and the platform
  // admin sees every tenant's data rolled up — that's a data leak the
  // user reported Feb 2026 (Concrete Coatings numbers were showing up
  // on the platform admin's dashboard). Force-redirect them into the
  // /platform/* space; those routes have their own explicit platform-
  // level queries and don't leak tenant business data.
  const path = location.pathname || '';
  const isPlatformArea = path.startsWith('/platform');
  if (user.role === 'platform_admin' && !isPlatformArea) {
    return <Navigate to="/platform" replace />;
  }
  // Feb 2026 owner directive: white-label / reseller controls belong
  // to the platform admin only. `/platform/*` routes expose company
  // rosters, feature-flag toggles, revenue analytics and the sales-
  // ready checklist — none of which a tenant company admin should
  // see. Bounce them back to their dashboard.
  if (isPlatformArea && user.role !== 'platform_admin') {
    return <Navigate to="/dashboard" replace />;
  }
  return <ContentProtection>{children}</ContentProtection>;
}

// TenantAdminRoute — like AdminRoute but ALSO redirects platform admins
// away from tenant-scoped screens. Feb 2026: fixes a data leak where
// platform admins hitting /dashboard were seeing aggregate numbers
// pulled from every tenant (get_tenant_filter returns {} for platform
// admin, so aggregate queries return "all tenants" rolled up as if it
// were one company's data). Platform admins have their own screens
// at /platform/* and should never see per-tenant business data on the
// tenant dashboard.
function TenantAdminRoute({ children }) {
  const { user, loading } = useAuth();
  useSessionTimeout();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'employee') return <Navigate to="/my-jobs" />;
  if (user.role === 'platform_admin') return <Navigate to="/platform" replace />;
  return <ContentProtection>{children}</ContentProtection>;
}

// Pro Feature Route - shows upgrade prompt for Essentials users
function ProFeatureRoute({ children, feature, featureName }) {
  const { user, loading } = useAuth();
  
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'employee') return <Navigate to="/my-jobs" />;
  
  // Platform admin has all features
  if (user.role === 'platform_admin') return children;
  
  // Check if user has the required feature
  if (!user.features?.includes(feature)) {
    return <UpgradePrompt featureName={featureName} />;
  }
  
  return children;
}

// Route wrapper that allows both admins and employees (for shared features like Calendar, Materials)
function SharedFeatureRoute({ children, feature, featureName }) {
  const { user, loading } = useAuth();
  
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" />;
  
  // Platform admin has all features
  if (user.role === 'platform_admin') return children;
  
  // Employees have access to shared features
  if (user.role === 'employee') return children;
  
  // Check if user has the required feature
  if (!user.features?.includes(feature)) {
    return <UpgradePrompt featureName={featureName} />;
  }
  
  return children;
}

// Upgrade to Pro Prompt Component
function UpgradePrompt({ featureName }) {
  const navigate = useNavigate();
  
  return (
    <div className="flex items-center justify-center min-h-[80vh]">
      <Card className="bg-zinc-900 border-zinc-800 max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Crown className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl font-heading text-white">Upgrade to Pro</CardTitle>
          <CardDescription className="text-zinc-400">
            <span className="text-primary font-semibold">{featureName}</span> is a Pro feature
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
            <p className="text-white font-medium mb-2">Pro Plan includes:</p>
            <ul className="text-sm text-zinc-400 space-y-2">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" /> Profitable Estimating
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" /> Material Catalog
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" /> Calendar & Scheduling
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" /> Proposals & E-Signatures
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" /> Automated Reminders
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" /> Customer Portal
              </li>
            </ul>
          </div>
          
          <div className="text-center">
            <p className="text-2xl font-bold text-primary">$500<span className="text-sm text-zinc-500 font-normal">/month</span></p>
          </div>
          
          <Button 
            className="w-full bg-primary hover:bg-primary/90 text-black font-bold"
            onClick={() => navigate('/settings?tab=subscription')}
          >
            Upgrade Now
          </Button>
          
          <Button 
            variant="ghost" 
            className="w-full text-zinc-400"
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ============== SIGNATURE PAD COMPONENT ==============
function SignaturePad({ onSave, onClear, existingSignature = null }) {
  const canvasRef = useRef(null);
  const typeCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(!!existingSignature);
  const [mode, setMode] = useState('draw'); // 'draw' or 'type'
  const [typedName, setTypedName] = useState('');

  // Initialize draw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#FFD000';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    if (existingSignature && mode === 'draw') {
      const img = new window.Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
      };
      img.src = existingSignature;
    }
  }, [existingSignature, mode]);

  // Render typed name to canvas
  useEffect(() => {
    if (mode !== 'type' || !typeCanvasRef.current) return;
    
    const canvas = typeCanvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (typedName.trim()) {
      ctx.fillStyle = '#FFD000';
      ctx.font = 'italic 48px "Brush Script MT", "Segoe Script", "Bradley Hand", cursive';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedName, canvas.width / 2, canvas.height / 2);
      setHasSignature(true);
    } else {
      setHasSignature(false);
    }
  }, [typedName, mode]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    if (mode === 'draw') {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    } else {
      setTypedName('');
    }
    setHasSignature(false);
    if (onClear) onClear();
  };

  const switchMode = (newMode) => {
    clearSignature();
    setMode(newMode);
  };

  const saveSignature = () => {
    const canvas = mode === 'draw' ? canvasRef.current : typeCanvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    if (onSave) onSave(dataUrl);
    return dataUrl;
  };

  return (
    <div className="space-y-3">
      {/* Mode Toggle */}
      <div className="flex gap-2 justify-center">
        <Button
          size="sm"
          variant={mode === 'draw' ? 'default' : 'outline'}
          onClick={() => switchMode('draw')}
          className={mode === 'draw' ? 'bg-primary text-black' : 'border-zinc-600 text-zinc-400'}
        >
          ✏️ Draw Signature
        </Button>
        <Button
          size="sm"
          variant={mode === 'type' ? 'default' : 'outline'}
          onClick={() => switchMode('type')}
          className={mode === 'type' ? 'bg-primary text-black' : 'border-zinc-600 text-zinc-400'}
        >
          ⌨️ Type Name
        </Button>
      </div>

      {/* Draw Mode */}
      {mode === 'draw' && (
        <div className="bg-zinc-800 rounded-lg p-1 border border-zinc-600">
          <canvas
            ref={canvasRef}
            width={500}
            height={200}
            className="w-full bg-zinc-900 rounded cursor-crosshair touch-none"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
        </div>
      )}

      {/* Type Mode */}
      {mode === 'type' && (
        <div className="space-y-3">
          <Input
            type="text"
            placeholder="Type your full name..."
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            className="bg-zinc-800 border-zinc-600 text-white text-lg text-center"
            maxLength={50}
          />
          <div className="bg-zinc-800 rounded-lg p-1 border border-zinc-600">
            <canvas
              ref={typeCanvasRef}
              width={500}
              height={150}
              className="w-full bg-zinc-900 rounded"
            />
          </div>
          <p className="text-xs text-zinc-500 text-center">Your typed name will appear as a signature above</p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-xs text-zinc-500">
          {mode === 'draw' ? 'Sign above using your mouse or finger' : 'Type your full legal name'}
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={clearSignature} className="border-zinc-600 text-zinc-400">
            Clear
          </Button>
          {hasSignature && (
            <Button size="sm" onClick={saveSignature} className="bg-primary text-black">
              Confirm Signature
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============== IMAGE UPLOAD COMPONENT ==============
// Exported for use in extracted page components

/**
 * Client-side image compressor. Downscales to a max edge of `maxEdge` and
 * re-encodes to JPEG at `quality`. Handles EXIF orientation implicitly by
 * relying on the browser's `img` decoder (modern browsers apply orientation
 * automatically). Falls back to the original blob if canvas/toBlob fails.
 *
 * Why this exists: iPhone/Android camera photos are typically 3-6MB and were
 * being rejected in production by the Kubernetes ingress body-size limit
 * before ever reaching FastAPI. Compressing here means uploads succeed on
 * mobile networks + stays well under any reasonable ingress cap.
 */
export async function compressImageForUpload(file, {
  maxEdge = 1920,
  quality = 0.82,
} = {}) {
  // GIFs and images already tiny — skip work
  if (file.type === 'image/gif' || file.size < 300 * 1024) {
    return file;
  }
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise((resolve, reject) => {
      // Use window.Image — `Image` is shadowed at module scope by the
      // lucide-react icon of the same name imported at the top of this file.
      const el = new window.Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });

    const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    URL.revokeObjectURL(url);

    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', quality);
    });
    if (!blob) return file;

    // If compression somehow produced a larger file (rare for JPG→JPG when
    // source is already highly compressed), just return the original.
    if (blob.size >= file.size) return file;

    // Preserve original filename (swap ext for .jpg)
    const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
  } catch (err) {
    console.warn('Image compression failed — sending original', err);
    return file;
  }
}

export function ImageUploader({ entityType, entityId, images = [], customerImages = [], onUploadSuccess, isPublic = false, token = null }) {
  const [uploading, setUploading] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const fileInputRef = useRef(null);

  const openLightbox = (url, idx) => {
    setLightboxImage(url);
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  const navigateLightbox = (direction) => {
    const allImages = [...(images || []), ...(customerImages || [])];
    let newIndex = lightboxIndex + direction;
    if (newIndex < 0) newIndex = allImages.length - 1;
    if (newIndex >= allImages.length) newIndex = 0;
    setLightboxIndex(newIndex);
    setLightboxImage(allImages[newIndex]);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        continue;
      }
      // Absolute cap BEFORE compression — 25MB is generous headroom for
      // modern phone RAW / HEIC files. We compress everything below.
      if (file.size > 25 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 25MB)`);
        continue;
      }

      try {
        // Client-side compression. Kubernetes ingress rejected iPhone
        // photos (3-6MB) in production because the ingress body-size limit
        // is well below our backend's 10MB cap. Compressing to a max edge
        // of 1920px + JPEG q=0.82 typically shrinks a 6MB phone photo to
        // ~400KB — well under any reasonable ingress limit and keeps the
        // Photos tab responsive.
        const compressed = await compressImageForUpload(file);

        const formData = new FormData();
        formData.append('file', compressed, compressed.name);
        formData.append('entity_type', entityType);
        formData.append('entity_id', entityId);
        if (isPublic && token) {
          formData.append('token', token);
        }

        const endpoint = isPublic ? '/uploads/image/public' : '/uploads/image';
        const res = await (isPublic 
          ? axios.post(`${API}${endpoint}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
          : apiClient.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
        );
        
        uploadedUrls.push(res.data.url);
        const sizeKb = Math.round(compressed.size / 1024);
        toast.success(`Uploaded ${file.name} (${sizeKb} KB)`);
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 413) {
          toast.error(`${file.name}: file too large after compression. Try a smaller photo.`);
        } else if (detail) {
          toast.error(`Failed to upload ${file.name}: ${detail}`);
        } else if (err?.message?.includes('Network')) {
          toast.error(`Failed to upload ${file.name}: network error. Check your connection and retry.`);
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
        console.error('Image upload failed:', err);
      }
    }

    setUploading(false);
    if (uploadedUrls.length > 0 && onUploadSuccess) {
      onUploadSuccess(uploadedUrls);
    }
    e.target.value = ''; // Reset input
  };

  const allImages = [...(images || []), ...(customerImages || [])];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-zinc-400 text-sm flex items-center gap-2">
          <Camera className="w-4 h-4" /> Photos ({allImages.length})
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="border-zinc-600 text-zinc-300"
        >
          {uploading ? (
            <>
              <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full mr-2" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" /> Add Photo
            </>
          )}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {allImages.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {allImages.map((url, idx) => (
            <div key={idx} className="relative group">
              <img
                src={`${BACKEND_URL}${url}`}
                alt={`Photo ${idx + 1}`}
                className="w-full h-24 object-cover rounded-lg border border-zinc-700 cursor-pointer hover:opacity-80 hover:border-primary transition-all"
                onClick={() => openLightbox(url, idx)}
                data-testid={`photo-thumbnail-${idx}`}
              />
              {customerImages?.includes(url) && (
                <span className="absolute top-1 left-1 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded">Customer</span>
              )}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-lg pointer-events-none">
                <Eye className="w-6 h-6 text-white" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Photo Lightbox Modal */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-4xl p-2 sm:p-4">
          <div className="relative">
            {/* Close button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setLightboxOpen(false)}
              className="absolute top-2 right-2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full"
            >
              <X className="w-5 h-5" />
            </Button>
            
            {/* Navigation arrows */}
            {allImages.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigateLightbox(-1)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full"
                  data-testid="lightbox-prev"
                >
                  <ChevronLeft className="w-6 h-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigateLightbox(1)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full"
                  data-testid="lightbox-next"
                >
                  <ChevronRight className="w-6 h-6" />
                </Button>
              </>
            )}
            
            {/* Main image */}
            {lightboxImage && (
              <img
                src={`${BACKEND_URL}${lightboxImage}`}
                alt="Full size photo"
                className="w-full max-h-[80vh] object-contain rounded-lg"
                data-testid="lightbox-image"
              />
            )}
            
            {/* Image counter */}
            {allImages.length > 1 && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/70 text-white text-sm px-3 py-1 rounded-full">
                {lightboxIndex + 1} / {allImages.length}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {allImages.length === 0 && (
        <div className="bg-zinc-800/50 border border-dashed border-zinc-700 rounded-lg p-4 text-center">
          <Image className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
          <p className="text-zinc-500 text-sm">No photos yet. Click "Add Photo" to upload.</p>
        </div>
      )}
    </div>
  );
}

// PDF Download Helper
const downloadPDF = async (type, id) => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API}/pdf/${type}/${id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (!response.ok) throw new Error('Failed to generate PDF');
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_${id.slice(0, 8).toUpperCase()}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    toast.success('PDF downloaded!');
  } catch (err) {
    toast.error('Failed to download PDF');
  }
};

// Print Estimate Helper - Opens PDF in new window for printing
const printEstimate = async (estimateId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API}/estimates/${estimateId}/pdf`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (!response.ok) throw new Error('Failed to generate PDF');
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    
    // Open PDF in new window and trigger print dialog
    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      printWindow.addEventListener('load', () => {
        printWindow.print();
      });
    } else {
      // If popup blocked, fallback to download
      const a = document.createElement('a');
      a.href = url;
      a.download = `Estimate_${estimateId.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.info('Popup blocked - PDF downloaded instead. Open and print manually.');
    }
  } catch (err) {
    toast.error('Failed to print estimate');
  }
};

// Sidebar Navigation - will be filtered by role and features
// Pro-only features: material_catalog, profitable_estimates, proposals, calendar, customer_portal
const adminNavItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
  { icon: Calendar, label: "Calendar", path: "/calendar", requiredFeature: "calendar" },
  { icon: Bell, label: "Reminders", path: "/reminders" },
  { icon: Users, label: "Customers", path: "/customers" },
  { icon: UserPlus, label: "Leads", path: "/leads", requiredFeature: "leads" },
  { icon: ClipboardCheck, label: "Site Assessments", path: "/site-assessments" },
  { icon: Briefcase, label: "Jobs", path: "/jobs" },
  // Warranty Claims (Feb 2026) — sits under Operations right after
  // Jobs so office staff can flip between "current work" and "warranty
  // work" without leaving the Ops mental model. Icon reuses the
  // Award/shield look already imported elsewhere in App.js.
  { icon: Award, label: "Warranty", path: "/warranty" },
  { icon: Clock, label: "Time Tracking", path: "/time" },
  { icon: TrendingUp, label: "Profitability", path: "/profitability", requiredFeature: "company_profitability" },
  { icon: BarChart3, label: "Financial Analytics", path: "/financial-analytics", adminOnly: true, requiredFeature: 'financial_analytics' },
  { icon: ClipboardList, label: "Reports", path: "/reports", adminOnly: true },
  { icon: Calculator, label: "Pricing", path: "/pricing", requiredFeature: "profitable_estimates" },
  { icon: Wrench, label: "Materials", path: "/materials", requiredFeature: "material_catalog" },
  { icon: Truck, label: "Vendors & POs", path: "/vendors", requiredFeature: "vendor_lists" },
  { icon: Settings, label: "Equipment", path: "/equipment", requiredFeature: "equipment_management" },
  { icon: Car, label: "Vehicles", path: "/vehicles", requiredFeature: "vehicle_service_records" },
  { icon: Wrench, label: "Maintenance", path: "/maintenance-reminders" },
  { icon: Target, label: "Coverage Calculator", path: "/coverage-calculator", requiredFeature: "coverage_calculator" },
  { icon: FileText, label: "Estimates", path: "/estimates", requiredFeature: "basic_estimates" },
  { icon: Receipt, label: "Invoices", path: "/invoices", requiredFeature: "invoices" },
  { icon: CreditCard, label: "Payments", path: "/payments", requiredFeature: "invoices" },
  { icon: Wallet, label: "Deposits", path: "/deposits", requiredFeature: "invoices" },
  { icon: ScrollText, label: "Contracts", path: "/contracts", requiredFeature: "contracts" },
  { icon: ShieldCheck, label: "Signed Docs", path: "/signed-documents" },
  { icon: HardHat, label: "Team", path: "/team" },
  { icon: HardHat, label: "Subcontractors", path: "/subcontractors", requiredFeature: "subcontractors_module" },
  { icon: MessageSquare, label: "Communications", path: "/communications", requiredFeature: "subcontractors_module" },
  { icon: Upload, label: "Import Data", path: "/data-import" },
  { icon: HelpCircle, label: "Support", path: "/support" },
  { icon: BookOpen, label: "Help Center", path: "/help" },
  { icon: PlayCircle, label: "App Features & Tips", path: "/help-videos" },
  { icon: MapPin, label: "Service Regions", path: "/service-regions", adminOnly: true, requiredFeature: 'service_regions' },
  { icon: Navigation, label: "Routes", path: "/routes", adminOnly: true, requiredFeature: 'route_manager' },
  { icon: Ruler, label: "Takeoffs", path: "/takeoffs", adminOnly: true, requiredFeature: 'takeoff_tool' },
  { icon: Star, label: "Reviews", path: "/reviews", adminOnly: true },
  { icon: Lightbulb, label: "Future Ideas", path: "/future-ideas", requiredRole: "admin" },
  { icon: Settings, label: "Settings", path: "/settings" },
];

// Helper function to check if user has a feature
export const hasFeature = (user, feature) => {
  // Platform admin has all features
  if (user?.role === 'platform_admin') return true;
  // Check if feature is in user's features array
  return user?.features?.includes(feature) ?? false;
};

// Platform-only features - these are NEVER shown to paying customers
const PLATFORM_ONLY_FEATURES = [
  'platform_analytics',
  'bulk_operations', 
  'api_access',
  'white_label_admin',
  'revenue_dashboard',
  'tenant_impersonation',
  'system_health',
  'feature_lab',
  'data_export',
  'audit_logs',
  'custom_integrations',
  'ai_insights'
];

// Check if a feature is platform-only
const isPlatformOnlyFeature = (feature) => PLATFORM_ONLY_FEATURES.includes(feature);

// Check if user can access platform-only features
const hasPlatformOwnerAccess = (user) => user?.role === 'platform_admin';

const employeeNavItems = [
  { icon: LayoutDashboard, label: "My Dashboard", path: "/my-jobs" },
  { icon: Calendar, label: "Calendar", path: "/calendar" },
  { icon: Briefcase, label: "Upcoming Jobs", path: "/my-jobs?status=scheduled" },
  { icon: Clock, label: "Current Jobs", path: "/my-jobs?status=in_progress" },
  { icon: CheckCircle, label: "Completed", path: "/my-jobs?status=completed" },
  { icon: AlertCircle, label: "Pending", path: "/my-jobs?status=pending" },
  { icon: Calendar, label: "My Tasks", path: "/my-tasks", requiredFeature: "employee_tasks" },
  { icon: Navigation, label: "My Routes", path: "/routes", requiredFeature: "service_regions" },
  { icon: Clock, label: "Time Card", path: "/my-timecard" },
  { icon: Gift, label: "My Rewards", path: "/my-rewards" },
  { icon: Package, label: "Materials", path: "/materials" },
  { icon: Wrench, label: "Maintenance", path: "/maintenance-reminders" },
];

// ---------------------------------------------------------------------------
// One section in the grouped sidebar. In edit mode the whole section body
// is a drop zone (via `useDroppable`) so users can drop an item into an
// empty section — otherwise empty sections would be un-targetable. Outside
// edit mode this renders as a plain section with a header + nav links.
// ---------------------------------------------------------------------------
function SidebarSection({ section, collapsed, sidebarEditMode, location, handleNavClick }) {
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `__section:${section.name}`,
    disabled: !sidebarEditMode,
  });
  const itemIds = section.items.map((i) => i.path);
  const isEmpty = section.items.length === 0;

  return (
    <div className="mb-1">
      {!collapsed ? (
        <div className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-zinc-600 select-none flex items-center gap-2">
          <span>{section.name}</span>
          {sidebarEditMode && isEmpty && (
            <span className="text-yellow-500 normal-case tracking-normal text-[10px]">(drop here)</span>
          )}
        </div>
      ) : (
        <div className="border-t border-zinc-800 my-2" />
      )}

      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <div
          ref={setDropRef}
          className={`space-y-1 rounded transition-colors ${
            sidebarEditMode
              ? `min-h-[44px] ${isOver ? 'bg-primary/10 ring-1 ring-primary/40' : 'bg-zinc-900/40'}`
              : ''
          }`}
          data-testid={`sidebar-section-${section.name.toLowerCase().replace(/\s+/g, '-').replace('&', '')}`}
        >
          {section.items.map((item) => {
            const isActive = location.pathname === item.path || (item.path && location.pathname.startsWith(item.path));
            const navLink = (
              <Link
                to={item.path}
                onClick={sidebarEditMode ? (e) => e.preventDefault() : handleNavClick}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary border-l-4 border-primary -ml-0.5'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                }`}
              >
                {item.icon && <item.icon className="w-5 h-5 flex-shrink-0" />}
                {!collapsed && <span className="font-medium">{item.label}</span>}
              </Link>
            );
            if (sidebarEditMode) {
              return (
                <SortableSidebarItem key={item.path} id={item.path} disabled={false}>
                  {navLink}
                </SortableSidebarItem>
              );
            }
            return <div key={item.path}>{navLink}</div>;
          })}
        </div>
      </SortableContext>
    </div>
  );
}

function Sidebar({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) {
  const location = useLocation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  
  // Sidebar customization state
  const [sidebarEditMode, setSidebarEditMode] = useState(false);
  const [sidebarOrder, setSidebarOrder] = useState(null);
  // Group-aware layout: { [GROUP_NAME]: [path, path, ...] }. Lets users
  // drag items between sections in edit mode.
  const [sidebarLayout, setSidebarLayout] = useState(null);
  const [savingSidebar, setSavingSidebar] = useState(false);
  // Which item is actively being dragged (used to dim/highlight)
  const [activeDragPath, setActiveDragPath] = useState(null);
  
  // Share App dialog state
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareName, setShareName] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [sendingShare, setSendingShare] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  // SMS invite state (parallel to email invite)
  const [sharePhone, setSharePhone] = useState('');
  const [sharePhoneName, setSharePhoneName] = useState('');
  const [sharePhoneMessage, setSharePhoneMessage] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  
  // Generate referral link with user's info for tracking
  const referralLink = `https://crewsyncpro.app/get-started?ref=${user?.id?.slice(0, 8) || 'friend'}`;
  
  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    setLinkCopied(true);
    toast.success("Link copied to clipboard!");
    setTimeout(() => setLinkCopied(false), 3000);
  };
  
  const shareViaEmail = async () => {
    if (!shareEmail) {
      toast.error("Please enter an email address");
      return;
    }
    
    setSendingShare(true);
    try {
      await apiClient.post('/referrals/send-invite', {
        email: shareEmail,
        recipient_name: shareName || null,
        message: shareMessage || null
      });
      
      setEmailSent(true);
      toast.success(`Invitation sent to ${shareEmail}!`);
      
      // Reset form after a delay
      setTimeout(() => {
        setShareEmail('');
        setShareName('');
        setShareMessage('');
        setEmailSent(false);
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invitation");
    }
    setSendingShare(false);
  };
  
  const shareViaTwitter = () => {
    const text = encodeURIComponent(`I'm using @CrewSyncPro for field service management - it's been a game changer for my business! Check it out: ${referralLink}`);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
  };
  
  const shareViaLinkedIn = () => {
    const url = encodeURIComponent(referralLink);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };
  
  const shareViaSMS = () => {
    const message = encodeURIComponent(`Hey! I've been using CrewSync Pro for my business and thought you might like it too. Check it out: ${referralLink}`);
    window.open(`sms:?body=${message}`, '_blank');
  };

  const sendSmsInvite = async () => {
    if (!sharePhone) {
      toast.error('Please enter a phone number');
      return;
    }
    setSendingSms(true);
    try {
      await apiClient.post('/referrals/send-sms-invite', {
        phone: sharePhone,
        recipient_name: sharePhoneName || null,
        message: sharePhoneMessage || null,
      });
      setSmsSent(true);
      toast.success(`Text invite sent to ${sharePhone}!`);
      setTimeout(() => {
        setSharePhone('');
        setSharePhoneName('');
        setSharePhoneMessage('');
        setSmsSent(false);
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send text invite');
    }
    setSendingSms(false);
  };
  
  // Platform admin nav items
  const platformAdminNavItems = [
    { icon: Crown, label: "Platform Admin", path: "/platform" },
    { icon: Building2, label: "Companies", path: "/platform/companies" },
    { icon: Receipt, label: "Billing", path: "/platform/billing" },
    { icon: TrendingUp, label: "Revenue", path: "/platform/revenue" },
    { icon: HelpCircle, label: "Support Requests", path: "/platform/support" },
    { icon: Code2, label: "Code Health", path: "/platform/code-health" },
    { icon: Bug, label: "Frontend Errors", path: "/platform/frontend-errors" },
    { icon: ShieldCheck, label: "Deliverability Health", path: "/platform/deliverability-health" },
  ];
  
  // Platform Owner EXCLUSIVE nav items - only visible to you
  const platformOwnerNavItems = [
    { icon: BarChart3, label: "Platform Analytics", path: "/owner/analytics", feature: 'platform_analytics' },
    { icon: Database, label: "Data Import/Export", path: "/owner/data-export", feature: 'data_export' },
    { icon: Zap, label: "Feature Lab", path: "/owner/feature-lab", feature: 'feature_lab' },
    { icon: Activity, label: "System Health", path: "/owner/system-health", feature: 'system_health' },
    { icon: Shield, label: "Audit Logs", path: "/owner/audit-logs", feature: 'audit_logs' },
    { icon: Users, label: "Impersonate Tenant", path: "/owner/impersonate", feature: 'tenant_impersonation' },
  ];
  
  // Filter admin nav items based on user's features (feature gating) and role requirements
  const filteredAdminNavItems = adminNavItems.filter(item => {
    // Check role requirement (for admin-only items like Future Ideas)
    if (item.requiredRole === 'admin') {
      // Hide from trial accounts - only show for active subscriptions with admin roles
      const isTrialAccount = user?.subscription_status === 'trial';
      const isAdminRole = ['company_admin', 'admin', 'super_admin', 'platform_admin'].includes(user?.role);
      
      // Platform admin always sees everything
      if (user?.role === 'platform_admin') return true;
      
      // Hide from trial accounts even if they are admin
      if (isTrialAccount) return false;
      
      // Show only to admin roles with active subscriptions
      if (!isAdminRole) return false;
    }
    // If no feature required, show the item
    if (!item.requiredFeature) return true;
    // Check if user has the required feature
    return hasFeature(user, item.requiredFeature);
  });
  
  // Add Trial Management for super_admin users, Platform Admin for platform_admin
  const baseNavItems = user?.role === 'employee' ? employeeNavItems : filteredAdminNavItems;
  let navItems = baseNavItems;

  // Default section layout. Any new nav-items added later default to the
  // "OTHER" bucket at the bottom so they don't silently disappear.
  const DEFAULT_SECTIONS = [
    { name: 'OPERATIONS',        paths: ['/dashboard', '/calendar', '/reminders', '/jobs', '/warranty'] },
    { name: 'SALES',             paths: ['/leads', '/customers', '/site-assessments', '/estimates', '/contracts', '/signed-documents', '/reviews'] },
    { name: 'FIELD & CREW',      paths: ['/team', '/subcontractors', '/communications', '/time', '/service-regions', '/routes'] },
    { name: 'FINANCE',           paths: ['/invoices', '/payments', '/deposits', '/profitability', '/financial-analytics'] },
    { name: 'CATALOG & PRICING', paths: ['/materials', '/equipment', '/coverage-calculator', '/pricing'] },
    { name: 'GROWTH',            paths: ['/future-ideas', '/help-videos'] },
    { name: 'TOOLS',             paths: ['/data-import', '/support', '/settings'] },
    { name: 'OTHER',             paths: [] },
  ];

  // Build the final ordered sections list. If the user has a saved layout,
  // use that; otherwise fall back to defaults. Filter by actually-available
  // items (feature-gated / role-gated) so we never render a link the user
  // can't access.
  const sections = React.useMemo(() => {
    // Platform admin keeps its own curated list — don't regroup it.
    if (user?.role === 'platform_admin') return null;
    if (user?.role === 'employee') return null;
    if (user?.role === 'super_admin') return null;

    const itemByPath = {};
    filteredAdminNavItems.forEach((item) => {
      if (item.path) itemByPath[item.path] = item;
    });

    const savedSections = sidebarLayout
      ? Object.entries(sidebarLayout).map(([name, paths]) => ({ name, paths: paths || [] }))
      : null;
    const baseSections = savedSections || DEFAULT_SECTIONS.map((s) => ({ ...s }));

    // Also support the legacy flat `sidebarOrder` by ordering OPERATIONS items
    // the same way the saved list intended.
    if (!savedSections && sidebarOrder && sidebarOrder.length > 0) {
      const orderMap = Object.fromEntries(sidebarOrder.map((p, i) => [p, i]));
      baseSections.forEach((sec) => {
        sec.paths.sort((a, b) => (orderMap[a] ?? 999) - (orderMap[b] ?? 999));
      });
    }

    // Drop any paths that aren't in the filtered list (feature missing).
    const assigned = new Set();
    const out = baseSections.map((sec) => {
      const items = sec.paths.filter((p) => itemByPath[p]).map((p) => (assigned.add(p), itemByPath[p]));
      return { name: sec.name, items };
    });
    // Catch any filtered items not assigned to any section → dump in OTHER.
    const orphans = filteredAdminNavItems.filter((i) => i.path && !assigned.has(i.path));
    if (orphans.length > 0) {
      const other = out.find((s) => s.name === 'OTHER') || { name: 'OTHER', items: [] };
      other.items = [...(other.items || []), ...orphans];
      if (!out.find((s) => s.name === 'OTHER')) out.push(other);
    }
    // Hide empty sections unless in edit mode (edit mode keeps them as
    // drop targets so users can drag items INTO an empty section).
    return sidebarEditMode ? out : out.filter((s) => s.items.length > 0);
  }, [filteredAdminNavItems, sidebarLayout, sidebarOrder, sidebarEditMode, user?.role]);
  
  if (user?.role === 'platform_admin') {
    // Platform admin = SaaS-owner view. Intentionally NOT seeing the
    // operational menus (customers, jobs, materials, payments, etc.) — those
    // belong to the tenants, not the SaaS operator. To peek into a tenant's
    // data, use "Impersonate Tenant" / "View as" instead. Keep Service
    // Regions + Routes here because they're repurposed for tracking app
    // customers' geography & visit planning.
    navItems = [
      { type: 'header', label: 'PLATFORM' },
      ...platformAdminNavItems,
      { type: 'header', label: 'OWNER TOOLS' },
      ...platformOwnerNavItems,
      { icon: CheckCircle, label: 'Sales-Ready Checklist', path: '/platform/sales-checklist' },
      { type: 'header', label: 'FIELD' },
      { icon: MapPin, label: 'Tenant Regions', path: '/platform/tenant-regions' },
      { icon: Navigation, label: 'Tenant Routes', path: '/platform/tenant-routes' },
      { type: 'header', label: 'TOOLS' },
      { icon: PlayCircle, label: 'App Features & Tips', path: '/help-videos' },
      { icon: HelpCircle, label: 'Support', path: '/support' },
      { icon: Settings, label: 'Settings', path: '/settings' },
    ];
  } else if (user?.role === 'super_admin') {
    navItems = [{ icon: Crown, label: "Trial Management", path: "/trial-management" }, ...baseNavItems];
  }

  // Load sidebar order from API
  useEffect(() => {
    const loadSidebarOrder = async () => {
      try {
        const res = await apiClient.get('/company/layout');
        if (res.data?.sidebar_layout && Object.keys(res.data.sidebar_layout).length > 0) {
          setSidebarLayout(res.data.sidebar_layout);
        } else if (res.data?.sidebar_order && res.data.sidebar_order.length > 0) {
          // Legacy flat order — still respected; we layer sections on top
          // via the default grouping below.
          setSidebarOrder(res.data.sidebar_order);
        }
      } catch (err) {
        // Silent fail - use default order
      }
    };
    if (user?.role && ['company_admin', 'admin', 'super_admin', 'platform_admin'].includes(user.role)) {
      loadSidebarOrder();
    }
  }, [user?.role]);

  // Apply custom order to nav items
  const orderedNavItems = React.useMemo(() => {
    // Platform admin uses a curated, grouped layout — don't let saved custom
    // ordering scramble the section headers.
    if (user?.role === 'platform_admin') return navItems;
    if (!sidebarOrder || sidebarOrder.length === 0) return navItems;
    
    // Create a map of path to nav item
    const itemMap = {};
    navItems.forEach(item => {
      if (item.path) {
        itemMap[item.path] = item;
      }
    });
    
    // Build ordered list
    const ordered = [];
    const usedPaths = new Set();
    
    // First add items in the custom order
    sidebarOrder.forEach(path => {
      if (itemMap[path] && !usedPaths.has(path)) {
        ordered.push(itemMap[path]);
        usedPaths.add(path);
      }
    });
    
    // Then add any remaining items not in the custom order
    navItems.forEach(item => {
      if (item.path && !usedPaths.has(item.path)) {
        ordered.push(item);
        usedPaths.add(item.path);
      } else if (!item.path && (item.label === '---' || item.type === 'header')) {
        // Keep separators and group headers in platform admin view
        ordered.push(item);
      }
    });
    
    return ordered;
  }, [navItems, sidebarOrder, user?.role]);

  // Handle drag end — supports BOTH same-section reordering AND moving
  // items between sections. `over.id` here is either another item's path
  // or `__section:<NAME>` when the user drops in a section's empty area.
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragPath(null);
    if (!over || active.id === over.id) return;

    // Legacy flat-list fallback (when sections aren't in use, e.g. platform admin)
    if (!sections) {
      const oldIndex = orderedNavItems.findIndex((item) => item.path === active.id);
      const newIndex = orderedNavItems.findIndex((item) => item.path === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(orderedNavItems, oldIndex, newIndex);
        const newOrderPaths = newOrder.filter((item) => item.path).map((item) => item.path);
        setSidebarOrder(newOrderPaths);
      }
      return;
    }

    // Build current layout object from sections state
    const current = {};
    sections.forEach((s) => {
      current[s.name] = s.items.map((i) => i.path);
    });
    // Locate the active (source) section
    const srcSection = sections.find((s) => s.items.some((i) => i.path === active.id));
    if (!srcSection) return;

    // Resolve target section + target index
    let dstSectionName;
    let dstIndex;
    if (typeof over.id === 'string' && over.id.startsWith('__section:')) {
      dstSectionName = over.id.replace('__section:', '');
      dstIndex = (current[dstSectionName] || []).length; // drop at end of section
    } else {
      const dstSection = sections.find((s) => s.items.some((i) => i.path === over.id));
      if (!dstSection) return;
      dstSectionName = dstSection.name;
      dstIndex = dstSection.items.findIndex((i) => i.path === over.id);
    }

    // Remove from source
    const srcList = [...current[srcSection.name]];
    const activeIdx = srcList.indexOf(active.id);
    srcList.splice(activeIdx, 1);
    current[srcSection.name] = srcList;

    // Insert at target
    const dstList = [...(current[dstSectionName] || [])];
    const insertAt = Math.max(0, Math.min(dstIndex, dstList.length));
    dstList.splice(insertAt, 0, active.id);
    current[dstSectionName] = dstList;

    setSidebarLayout(current);
  };

  // Save sidebar order
  const saveSidebarOrder = async () => {
    setSavingSidebar(true);
    try {
      // Prefer saving as `sidebar_layout` (group-aware) when sections exist.
      const payload = sections
        ? { sidebar_layout: Object.fromEntries(sections.map((s) => [s.name, s.items.map((i) => i.path)])) }
        : { sidebar_order: sidebarOrder };
      await apiClient.put('/company/layout', payload);
      toast.success('Sidebar layout saved!');
      setSidebarEditMode(false);
    } catch (err) {
      toast.error('Failed to save sidebar layout');
    }
    setSavingSidebar(false);
  };

  const handleNavClick = () => {
    if (mobileOpen) setMobileOpen(false);
  };

  const isAdmin = ['company_admin', 'admin', 'super_admin', 'platform_admin'].includes(user?.role);

  return (
    <>
      {/* Mobile Overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden" 
          onClick={() => setMobileOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed left-0 top-0 h-full bg-zinc-950 border-r border-zinc-800 z-50 transition-all duration-300 flex flex-col
        ${collapsed ? "w-16" : "w-64"}
        ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
      `}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-800 flex-shrink-0">
          {!collapsed && (
            <div className="flex flex-col items-start gap-1">
              {/* Platform UI branding — ALWAYS shows CrewSync Pro on the
                  platform chrome (sidebar / login / dashboard header) so
                  every tenant sees a consistent product identity. Tenant
                  logos still appear on CUSTOMER-facing surfaces (email
                  headers, invoice PDFs, share links, letterheads) — see
                  branding_service + email_templates. */}
              <div className="flex items-start gap-0.5">
                <img src="https://customer-assets.emergentagent.com/job_crewsync-business/artifacts/srw9khxz_CrewSync%20Pro%20Logo.png" alt="CrewSync Pro" className="h-14 w-auto" />
                <span className="text-xs text-zinc-400 font-medium mt-1">™</span>
              </div>
            </div>
          )}
          <Button variant="ghost" size="icon" onClick={() => { setCollapsed(!collapsed); if (mobileOpen) setMobileOpen(false); }} className="text-zinc-400 hover:text-white hidden lg:flex">
            {collapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} className="text-zinc-400 hover:text-white lg:hidden">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <ScrollArea className="flex-1 min-h-0">
          {/* Edit mode banner */}
          {sidebarEditMode && !collapsed && (
            <div className="mx-2 mt-2 p-2 bg-yellow-500/20 border border-yellow-500/50 rounded-lg">
              <p className="text-yellow-400 text-xs font-medium text-center">
                Drag items between sections
              </p>
            </div>
          )}
          
          {/* Edit button at top */}
          {isAdmin && !collapsed && (
            <div className="px-2 pt-2 pb-1 flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (sidebarEditMode) {
                    saveSidebarOrder();
                  } else {
                    setSidebarEditMode(true);
                  }
                }}
                disabled={savingSidebar}
                className={`flex-1 text-xs ${
                  sidebarEditMode 
                    ? "bg-green-600 hover:bg-green-700 text-white" 
                    : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50"
                }`}
                data-testid="sidebar-customize-btn"
              >
                {sidebarEditMode ? (
                  savingSidebar ? 'Saving...' : '✓ Save Layout'
                ) : (
                  <>
                    <GripVertical className="w-3 h-3 mr-1" />
                    Customize Menu
                  </>
                )}
              </Button>
              {sidebarEditMode && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    if (!window.confirm('Reset sidebar layout to defaults?')) return;
                    setSidebarLayout(null);
                    setSidebarOrder(null);
                    try {
                      await apiClient.put('/company/layout', { sidebar_layout: Object.fromEntries(DEFAULT_SECTIONS.map(s => [s.name, s.paths])) });
                      toast.success('Layout reset to default');
                    } catch (_) { /* silent */ }
                  }}
                  className="px-2 text-xs text-zinc-400 hover:text-white"
                  data-testid="sidebar-reset-btn"
                >
                  Reset
                </Button>
              )}
            </div>
          )}
          
          {sections ? (
            // Grouped sidebar (company admins). Multi-container DnD:
            // one DndContext wraps every section; each section is its
            // own SortableContext plus a droppable zone so users can
            // drop items into an empty section.
            <DraggableContainer
              onDragEnd={handleDragEnd}
              disabled={!sidebarEditMode}
            >
              <nav className="p-2 space-y-1">
                {sections.map((section) => (
                  <SidebarSection
                    key={section.name}
                    section={section}
                    collapsed={collapsed}
                    sidebarEditMode={sidebarEditMode}
                    location={location}
                    handleNavClick={handleNavClick}
                  />
                ))}
              </nav>
            </DraggableContainer>
          ) : (
            <DraggableContainer onDragEnd={handleDragEnd} disabled={!sidebarEditMode}>
              <SortableContext 
                items={orderedNavItems.filter(item => item.path).map(item => item.path)}
                strategy={verticalListSortingStrategy}
              >
                <nav className="p-2 space-y-1">
                  {orderedNavItems.map((item, index) => {
                    if (item.type === 'header') {
                      if (collapsed) {
                        return <div key={`hdr-${index}`} className="border-t border-zinc-800 my-2" />;
                      }
                      return (
                        <div
                          key={`hdr-${index}`}
                          className="px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-wider text-zinc-600 select-none"
                        >
                          {item.label}
                        </div>
                      );
                    }
                    if (item.label === '---') {
                      return <div key={`sep-${index}`} className="border-t border-zinc-700 my-2" />;
                    }
                    const isActive = location.pathname === item.path || (item.path && location.pathname.startsWith(item.path));
                    const navLink = (
                      <Link
                        to={item.path}
                        onClick={sidebarEditMode ? (e) => e.preventDefault() : handleNavClick}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                          isActive
                            ? "bg-primary/10 text-primary border-l-4 border-primary -ml-0.5"
                            : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                        }`}
                      >
                        {item.icon && <item.icon className="w-5 h-5 flex-shrink-0" />}
                        {!collapsed && <span className="font-medium">{item.label}</span>}
                      </Link>
                    );
                    if (sidebarEditMode && item.path) {
                      return (
                        <SortableSidebarItem key={item.path} id={item.path} disabled={!sidebarEditMode}>
                          {navLink}
                        </SortableSidebarItem>
                      );
                    }
                    return <div key={item.path}>{navLink}</div>;
                  })}
                </nav>
              </SortableContext>
            </DraggableContainer>
          )}
        </ScrollArea>

        <div className="flex-shrink-0 border-t border-zinc-800">
          {/* Powered by CrewSync Pro with Share Button */}
          {!collapsed && (
            <div className="px-3 py-2 border-b border-zinc-800/50 bg-zinc-900/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Powered by</span>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_crewsync-business/artifacts/srw9khxz_CrewSync%20Pro%20Logo.png" 
                    alt="CrewSync Pro" 
                    className="h-4 w-auto opacity-70" 
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowShareDialog(true)}
                  className="h-6 px-2 text-[10px] text-primary hover:text-primary hover:bg-primary/10 gap-1"
                  data-testid="share-app-btn"
                >
                  <Share2 className="w-3 h-3" />
                  Share
                </Button>
              </div>
            </div>
          )}
          {collapsed && (
            <div className="py-2 flex flex-col items-center gap-1 border-b border-zinc-800/50 bg-zinc-900/50">
              <img 
                src="https://customer-assets.emergentagent.com/job_crewsync-business/artifacts/srw9khxz_CrewSync%20Pro%20Logo.png" 
                alt="CrewSync Pro" 
                className="h-4 w-auto opacity-70" 
                title="Powered by CrewSync Pro"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowShareDialog(true)}
                className="h-6 w-6 text-primary hover:text-primary hover:bg-primary/10"
                title="Share the App"
              >
                <Share2 className="w-3 h-3" />
              </Button>
            </div>
          )}
          
          <div className="p-4">
            {!collapsed && user && (
              <div className="mb-3 text-sm">
                <p className="font-medium truncate">{user.name}</p>
                <p className="text-zinc-400 text-xs truncate">{user.email}</p>
              </div>
            )}
            <Button
              variant="ghost"
              size={collapsed ? "icon" : "default"}
              onClick={() => { logout(); navigate("/login"); }}
              className="w-full text-zinc-400 hover:text-white hover:bg-zinc-800"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              {!collapsed && <span className="ml-2">Logout</span>}
            </Button>
          </div>
        </div>
        
        {/* Share App Dialog */}
        <Dialog open={showShareDialog} onOpenChange={(open) => {
          setShowShareDialog(open);
          if (!open) {
            setShareEmail('');
            setShareName('');
            setShareMessage('');
            setEmailSent(false);
            setSharePhone('');
            setSharePhoneName('');
            setSharePhoneMessage('');
            setSmsSent(false);
          }
        }}>
          <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Share2 className="w-5 h-5 text-primary" />
                Share CrewSync Pro
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Know someone who could benefit from CrewSync Pro? Share it with them!
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Referral Link - Copy */}
              <div className="space-y-2">
                <Label className="text-zinc-300 text-sm">Your Referral Link</Label>
                <div className="flex gap-2">
                  <Input
                    value={referralLink}
                    readOnly
                    className="bg-zinc-800 border-zinc-700 text-zinc-300 text-sm font-mono"
                  />
                  <Button
                    onClick={copyReferralLink}
                    className={`px-4 ${linkCopied ? 'bg-green-600 hover:bg-green-700' : 'bg-primary hover:bg-primary/90'} text-black`}
                  >
                    {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              {/* Quick Share Row */}
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant="outline"
                  onClick={shareViaTwitter}
                  className="border-zinc-700 hover:bg-zinc-800 text-zinc-300 flex-col h-auto py-3"
                >
                  <Twitter className="w-5 h-5 text-blue-400 mb-1" />
                  <span className="text-xs">Twitter</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={shareViaLinkedIn}
                  className="border-zinc-700 hover:bg-zinc-800 text-zinc-300 flex-col h-auto py-3"
                >
                  <Linkedin className="w-5 h-5 text-blue-500 mb-1" />
                  <span className="text-xs">LinkedIn</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={shareViaSMS}
                  className="border-zinc-700 hover:bg-zinc-800 text-zinc-300 flex-col h-auto py-3"
                >
                  <MessageSquare className="w-5 h-5 text-green-400 mb-1" />
                  <span className="text-xs">Text</span>
                </Button>
              </div>
              
              {/* Email Invite - Direct Send */}
              <div className="space-y-3 pt-3 border-t border-zinc-800">
                <Label className="text-zinc-300 text-sm flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Send Email Invitation
                </Label>
                
                {emailSent ? (
                  <div className="bg-green-900/30 border border-green-700 rounded-lg p-4 text-center">
                    <Check className="w-8 h-8 text-green-400 mx-auto mb-2" />
                    <p className="text-green-400 font-medium">Invitation Sent!</p>
                    <p className="text-zinc-400 text-sm">We've sent an invite to {shareEmail}</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="email"
                        placeholder="Email address *"
                        value={shareEmail}
                        onChange={(e) => setShareEmail(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                      <Input
                        type="text"
                        placeholder="Their name (optional)"
                        value={shareName}
                        onChange={(e) => setShareName(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <Textarea
                      placeholder="Add a personal note (optional)..."
                      value={shareMessage}
                      onChange={(e) => setShareMessage(e.target.value)}
                      rows={2}
                      className="bg-zinc-800 border-zinc-700 text-white resize-none"
                    />
                    <Button
                      onClick={shareViaEmail}
                      disabled={!shareEmail || sendingShare}
                      className="w-full bg-primary hover:bg-primary/90 text-black"
                    >
                      {sendingShare ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Invitation
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>

              {/* SMS Invite — Direct Send via Twilio */}
              <div className="space-y-3 pt-3 border-t border-zinc-800">
                <Label className="text-zinc-300 text-sm flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-green-400" />
                  Send Text Invitation
                </Label>
                {smsSent ? (
                  <div className="bg-green-900/30 border border-green-700 rounded-lg p-4 text-center" data-testid="share-sms-sent-confirmation">
                    <Check className="w-8 h-8 text-green-400 mx-auto mb-2" />
                    <p className="text-green-400 font-medium">Text Invite Sent!</p>
                    <p className="text-zinc-400 text-sm">We've texted {sharePhone}</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="tel"
                        placeholder="Phone number *"
                        value={sharePhone}
                        onChange={(e) => setSharePhone(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="share-sms-phone-input"
                      />
                      <Input
                        type="text"
                        placeholder="Their name (optional)"
                        value={sharePhoneName}
                        onChange={(e) => setSharePhoneName(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="share-sms-name-input"
                      />
                    </div>
                    <Textarea
                      placeholder="Add a short note (optional)..."
                      value={sharePhoneMessage}
                      onChange={(e) => setSharePhoneMessage(e.target.value)}
                      rows={2}
                      className="bg-zinc-800 border-zinc-700 text-white resize-none"
                      data-testid="share-sms-message-input"
                    />
                    <Button
                      onClick={sendSmsInvite}
                      disabled={!sharePhone || sendingSms}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      data-testid="share-sms-send-btn"
                    >
                      {sendingSms ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Texting...
                        </>
                      ) : (
                        <>
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Send Text Invite
                        </>
                      )}
                    </Button>
                    <p className="text-[11px] text-zinc-500">
                      SMS is sent from CrewSync Pro via Twilio. Standard rates may apply for the recipient.
                    </p>
                  </>
                )}
              </div>
              
              {/* Referral Note */}
              <div className="bg-primary/10 border border-primary/30 rounded-lg p-3">
                <p className="text-primary text-xs flex items-start gap-2">
                  <Gift className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Help fellow contractors discover a better way to manage their business. Your referrals help us grow!</span>
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </aside>
    </>
  );
}

// Voice Assistant Component - Floating button with speech recognition
function VoiceAssistant() {
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const location = useLocation();

  // Persist transcript + recognition across renders so onend reads the
  // latest value (the previous closure captured `transcript===''` and
  // silently swallowed every utterance).
  const transcriptRef = React.useRef('');
  const recognitionRef = React.useRef(null);
  const startedAtRef = React.useRef(0);

  // Lazily build the recognition instance once. Don't recreate on every
  // render — that detached the handlers we just attached.
  const getRecognition = React.useCallback(() => {
    if (recognitionRef.current) return recognitionRef.current;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return null;
    const rec = new SpeechRecognition();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = 'en-US';
    recognitionRef.current = rec;
    return rec;
  }, []);

  const processMessage = React.useCallback(async (message) => {
    const text = (message || '').trim();
    if (!text) return;

    setIsProcessing(true);
    try {
      const currentPage = location.pathname.split('/').filter(Boolean).join(' > ') || 'Dashboard';
      const res = await apiClient.post('/voice-assistant/chat', {
        message: text,
        context: currentPage
      });
      const reply = res?.data?.response || "I couldn't generate a response.";
      setResponse(reply);

      if ('speechSynthesis' in window) {
        try {
          setIsSpeaking(true);
          const utterance = new SpeechSynthesisUtterance(reply);
          utterance.rate = 1.0;
          utterance.pitch = 1.0;
          utterance.onend = () => setIsSpeaking(false);
          utterance.onerror = () => setIsSpeaking(false);
          speechSynthesis.speak(utterance);
        } catch (_) {
          setIsSpeaking(false);
        }
      }
    } catch (err) {
      console.error('Voice assistant error:', err);
      setResponse(err?.response?.data?.detail || "Sorry, I couldn't reach the assistant. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  }, [location.pathname]);

  const startListening = () => {
    const recognition = getRecognition();
    if (!recognition) {
      toast.error("Speech recognition is not supported in this browser. Try Chrome, Edge, or Safari.");
      return;
    }
    // Mic API only works in secure contexts
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      toast.error("Microphone requires HTTPS. Please use the deployed/secure URL.");
      return;
    }

    setShowDialog(true);
    setIsListening(true);
    setTranscript('');
    setResponse('');
    transcriptRef.current = '';
    startedAtRef.current = Date.now();

    // Reattach handlers each start (so the latest closures are used).
    recognition.onresult = (event) => {
      let combined = '';
      for (let i = 0; i < event.results.length; i++) {
        combined += event.results[i][0].transcript;
      }
      transcriptRef.current = combined;
      setTranscript(combined);
    };

    recognition.onend = () => {
      setIsListening(false);
      const final = transcriptRef.current;
      if (final && final.trim()) {
        processMessage(final);
      } else {
        // Differentiate "user opened mic and said nothing" vs "permission denied
        // before any audio". onerror handles permission/no-speech; if we got
        // here with no error and no text, just stay quiet.
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      const err = event.error || '';
      if (err === 'no-speech') {
        toast.info("I didn't catch that — tap the mic and try again.");
      } else if (err === 'not-allowed' || err === 'service-not-allowed') {
        toast.error("Microphone permission was denied. Enable it in your browser settings.");
      } else if (err === 'audio-capture') {
        toast.error("No microphone detected on this device.");
      } else if (err === 'network') {
        toast.error("Speech recognition needs an internet connection.");
      } else if (err === 'aborted') {
        // user stopped — silent
      } else {
        toast.error(`Couldn't hear you (${err || 'unknown'}). Please try again.`);
      }
    };

    try {
      recognition.start();
    } catch (e) {
      // Calling start() while already started throws InvalidStateError.
      // Reset and try once more.
      try { recognition.stop(); } catch (_) {}
      setIsListening(false);
    }
  };

  const stopListening = () => {
    const recognition = recognitionRef.current;
    if (recognition) {
      try { recognition.stop(); } catch (_) {}
    }
    setIsListening(false);
  };

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  return (
    <>
      {/* Floating Voice Button */}
      <button
        onClick={() => isListening ? stopListening() : startListening()}
        data-testid="voice-assistant-mic-button"
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all ${
          isListening 
            ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
            : 'bg-primary hover:bg-primary/90'
        }`}
        title="Voice Assistant"
      >
        {isListening ? (
          <MicOff className="w-6 h-6 text-white" />
        ) : (
          <Mic className="w-6 h-6 text-black" />
        )}
      </button>

      {/* Voice Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-[95vw] sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Mic className="w-5 h-5 text-primary" /> Voice Assistant
            </DialogTitle>
            <DialogDescription>Ask me anything about using CrewSync Pro™</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Status Indicator */}
            <div className="flex items-center justify-center py-4">
              {isListening ? (
                <div className="flex flex-col items-center">
                  <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center animate-pulse">
                    <Mic className="w-10 h-10 text-red-400" />
                  </div>
                  <p className="mt-3 text-zinc-400">Listening...</p>
                </div>
              ) : isProcessing ? (
                <div className="flex flex-col items-center">
                  <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                  <p className="mt-3 text-zinc-400">Processing...</p>
                </div>
              ) : isSpeaking ? (
                <div className="flex flex-col items-center">
                  <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Volume2 className="w-10 h-10 text-green-400 animate-pulse" />
                  </div>
                  <p className="mt-3 text-zinc-400">Speaking...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="w-20 h-20 rounded-full bg-zinc-800 flex items-center justify-center">
                    <Mic className="w-10 h-10 text-zinc-500" />
                  </div>
                  <p className="mt-3 text-zinc-400">Tap to speak</p>
                </div>
              )}
            </div>

            {/* Transcript */}
            {transcript && (
              <div className="bg-zinc-800/50 rounded-lg p-3">
                <p className="text-xs text-zinc-500 mb-1">You said:</p>
                <p className="text-white">{transcript}</p>
              </div>
            )}

            {/* Response */}
            {response && (
              <div className="bg-primary/10 border border-primary/30 rounded-lg p-3">
                <p className="text-xs text-primary mb-1">Assistant:</p>
                <p className="text-zinc-200">{response}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2">
              {isSpeaking ? (
                <Button onClick={stopSpeaking} variant="outline" className="flex-1 border-zinc-600">
                  <MicOff className="w-4 h-4 mr-2" /> Stop Speaking
                </Button>
              ) : (
                <Button 
                  onClick={startListening} 
                  disabled={isListening || isProcessing}
                  className="flex-1 bg-primary text-black font-bold"
                >
                  <Mic className="w-4 h-4 mr-2" /> {isListening ? 'Listening...' : 'Ask Again'}
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowDialog(false)} className="border-zinc-600">
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Main Layout
function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ── Onboarding gate ────────────────────────────────────────────────
  // Fires once per full-page load: if the tenant admin still has fewer
  // than 3 checklist items done, we bounce them to /welcome. After
  // that they can roam freely and the persistent Dashboard card takes
  // over. `sessionStorage` avoids re-firing on every route change and
  // lets the admin escape via the "Take me to the dashboard" button.
  useEffect(() => {
    if (!user || user.role === 'employee' || user.role === 'platform_admin') return;
    if (location.pathname === '/welcome') return;
    if (sessionStorage.getItem('onboarding_gate_checked')) return;
    sessionStorage.setItem('onboarding_gate_checked', '1');
    apiClient.get('/onboarding/status').then((res) => {
      if (res.data?.should_show_welcome) {
        navigate('/welcome', { replace: true });
      }
    }).catch(() => {});
  }, [user, location.pathname, navigate]);

  // Calculate days remaining for trial
  const getTrialInfo = () => {
    if (!user || user.subscription_status === 'active' || user.role === 'super_admin') return null;
    if (!user.trial_expires_at) return null;
    
    try {
      const expires = new Date(user.trial_expires_at);
      const now = new Date();
      const daysRemaining = Math.max(0, Math.ceil((expires - now) / (1000 * 60 * 60 * 24)));
      return { daysRemaining, expired: daysRemaining === 0 };
    } catch {
      return null;
    }
  };

  const trialInfo = getTrialInfo();

  return (
    <div className="min-h-screen bg-zinc-950">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      {/* Global notification bell (top-right floating) */}
      <NotificationBell />

      {/* Global help button (bottom-right floating). Hidden on
          login/public pages via its own guard. See FloatingHelpButton. */}
      <FloatingHelpButton />
      
      {/* Mobile Header */}
      {/* Mobile top bar. Oct 2026: was h-16 while <main> only padded pt-14,
          so the first 8px of every page sat underneath it. Bar and padding
          now match, and the logo is sized for a 56px bar. */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-zinc-950 border-b border-zinc-800 z-30 flex items-center justify-between px-3">
        <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} className="text-zinc-400 shrink-0">
          <Menu className="w-6 h-6" />
        </Button>
        <div className="flex items-start gap-0.5 min-w-0">
          <img src="https://customer-assets.emergentagent.com/job_crewsync-business/artifacts/srw9khxz_CrewSync%20Pro%20Logo.png" alt="CrewSync Pro" className="h-8 w-auto max-w-[180px] object-contain" />
          <span className="text-[8px] text-zinc-500">™</span>
        </div>
        <div className="w-10 shrink-0" /> {/* Spacer for centering */}
      </div>

      <main className={`transition-all duration-300 pt-14 lg:pt-0 ${collapsed ? "lg:ml-16" : "lg:ml-64"}`}>
        {/* Impersonation Banner — unmissable when Coy is viewing a tenant's
            account. Uses the session-stashed `original_admin_user` set by
            handleImpersonate so this only renders for platform admins
            actively impersonating. Exit button returns to the original
            admin identity atomically. */}
        {localStorage.getItem('original_admin_token') && (
          <div className="bg-yellow-400 text-black px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-2 border-b-4 border-yellow-600 shadow-md" data-testid="impersonation-banner">
            <div className="flex items-center gap-3">
              <div className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-600 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
              </div>
              <span className="font-heading font-black text-sm sm:text-base uppercase tracking-wider">
                Impersonating:
              </span>
              <span className="font-bold text-sm sm:text-base">
                {user?.company_name || user?.name || user?.email || 'Tenant Account'}
              </span>
              <span className="hidden md:inline text-xs bg-black/20 px-2 py-0.5 rounded">
                viewing as {user?.email}
              </span>
            </div>
            <Button
              size="sm"
              onClick={() => {
                const originalToken = localStorage.getItem('original_admin_token');
                const originalUser = localStorage.getItem('original_admin_user');
                if (originalToken) localStorage.setItem('token', originalToken);
                if (originalUser) localStorage.setItem('user', originalUser);
                localStorage.removeItem('original_admin_token');
                localStorage.removeItem('original_admin_user');
                window.location.href = '/platform/companies';
              }}
              className="bg-black text-yellow-300 hover:bg-zinc-900 font-bold px-4 h-8"
              data-testid="impersonation-exit-btn"
            >
              <X className="w-4 h-4 mr-1" /> Exit & Return to Admin
            </Button>
          </div>
        )}

        {/* Preview Environment Banner for Platform Admin */}
        {user?.role === 'platform_admin' && (
          <div className="bg-amber-500 text-black px-4 py-2 text-center text-sm font-bold flex items-center justify-center gap-2">
            <AlertCircle className="w-4 h-4" />
            PREVIEW ENVIRONMENT - Changes require deployment to go live
          </div>
        )}
        
        {/* Trial Banner */}
        {trialInfo && (
          <div className={`px-4 lg:px-6 py-2 lg:py-3 ${trialInfo.expired ? 'bg-red-600' : trialInfo.daysRemaining <= 3 ? 'bg-orange-600' : 'bg-blue-600'} text-white flex flex-col sm:flex-row items-center justify-between gap-2`}>
            <div className="flex items-center gap-2 text-sm lg:text-base">
              {trialInfo.expired ? (
                <>
                  <AlertCircle className="w-4 h-4 lg:w-5 lg:h-5" />
                  <span className="font-medium">Trial expired. Upgrade now!</span>
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 lg:w-5 lg:h-5" />
                  <span className="font-medium">
                    {trialInfo.daysRemaining === 1 ? '1 day' : `${trialInfo.daysRemaining} days`} left
                  </span>
                </>
              )}
            </div>
            <Button size="sm" className="bg-white text-black hover:bg-zinc-100 font-bold text-xs lg:text-sm">
              <Crown className="w-3 h-3 lg:w-4 lg:h-4 mr-1" /> Upgrade
            </Button>
          </div>
        )}
        <div className="p-4 lg:p-6 xl:p-8">{children}</div>
      </main>
      
      {/* Voice Assistant - Floating Button */}
      <VoiceAssistant />
    </div>
  );
}


// LoginPage is now imported from @/pages/LoginPage

// Platform Admin Dashboard

// Equipment Catalog Page
function EquipmentCatalogPage() {
  const { user } = useAuth();
  const isEmployee = user?.role === 'employee';
  const [equipment, setEquipment] = useState([]);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  const [form, setForm] = useState({
    name: "", description: "", category: "", equipment_type: "rental",
    unit: "day", unit_cost: 0, default_markup_percent: 10, 
    supplier: "", model_number: "", serial_number: "", purchase_cost: null
  });

  const unitOptions = [
    { value: "hour", label: "Per Hour" },
    { value: "day", label: "Per Day" },
    { value: "week", label: "Per Week" },
    { value: "month", label: "Per Month" },
    { value: "job", label: "Per Job" },
    { value: "each", label: "Each" },
  ];

  const typeOptions = [
    { value: "rental", label: "Rental Equipment" },
    { value: "owned", label: "Owned Equipment" },
    { value: "consumable", label: "Consumable/Disposable" },
  ];

  useEffect(() => {
    fetchEquipment();
    fetchCategories();
    fetchTypes();
  }, []);

  const fetchEquipment = async () => {
    try {
      const res = await apiClient.get("/equipment-catalog");
      setEquipment(res.data);
    } catch (err) {
      toast.error("Failed to load equipment");
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const res = await apiClient.get("/equipment-catalog/categories");
      setCategories(res.data);
    } catch (err) {}
  };

  const fetchTypes = async () => {
    try {
      const res = await apiClient.get("/equipment-catalog/types");
      setTypes(res.data);
    } catch (err) {}
  };

  const resetForm = () => {
    setForm({
      name: "", description: "", category: "", equipment_type: "rental",
      unit: "day", unit_cost: 0, default_markup_percent: 10,
      supplier: "", model_number: "", serial_number: "", purchase_cost: null
    });
    setEditingEquipment(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEquipment) {
        await apiClient.put(`/equipment-catalog/${editingEquipment.id}`, form);
        toast.success("Equipment updated!");
      } else {
        await apiClient.post("/equipment-catalog", form);
        toast.success("Equipment added to catalog!");
      }
      setDialogOpen(false);
      resetForm();
      fetchEquipment();
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save equipment");
    }
  };

  const handleEdit = (item) => {
    setForm({
      name: item.name || "",
      description: item.description || "",
      category: item.category || "",
      equipment_type: item.equipment_type || "rental",
      unit: item.unit || "day",
      unit_cost: item.unit_cost || 0,
      default_markup_percent: item.default_markup_percent || 10,
      supplier: item.supplier || "",
      model_number: item.model_number || "",
      serial_number: item.serial_number || "",
      purchase_cost: item.purchase_cost || null
    });
    setEditingEquipment(item);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this equipment?")) return;
    try {
      await apiClient.delete(`/equipment-catalog/${id}`);
      toast.success("Equipment deleted!");
      fetchEquipment();
      fetchCategories();
    } catch (err) {
      toast.error("Failed to delete equipment");
    }
  };

  const filteredEquipment = equipment.filter(e => {
    const matchesCategory = filterCategory === "all" || e.category === filterCategory;
    const matchesType = filterType === "all" || e.equipment_type === filterType;
    const matchesSearch = !searchTerm || 
      e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.model_number?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesType && matchesSearch;
  });

  const groupedEquipment = filteredEquipment.reduce((acc, e) => {
    const cat = e.category || "Uncategorized";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(e);
    return acc;
  }, {});

  const getTypeColor = (type) => {
    switch(type) {
      case 'rental': return 'bg-blue-500/20 text-blue-400';
      case 'owned': return 'bg-green-500/20 text-green-400';
      case 'consumable': return 'bg-orange-500/20 text-orange-400';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'rental': return 'Rental';
      case 'owned': return 'Owned';
      case 'consumable': return 'Consumable';
      default: return type;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="equipment-catalog-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase text-white">Equipment Catalog</h1>
          <p className="text-zinc-400 mt-1">
            {isEmployee ? 'View available equipment for jobs' : 'Manage rental equipment, owned tools, and consumables'}
          </p>
        </div>
        {!isEmployee && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-orange-600 hover:bg-orange-700 text-white font-heading font-bold" data-testid="add-equipment-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Equipment
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-600 max-w-xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase flex items-center gap-2 text-white">
                  <Settings className="w-5 h-5 text-orange-500" /> {editingEquipment ? 'Edit Equipment' : 'Add Equipment to Catalog'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="text-zinc-200 text-sm">Equipment Name *</Label>
                    <Input 
                      value={form.name} 
                      onChange={(e) => setForm({ ...form, name: e.target.value })} 
                      className="mt-1 bg-zinc-800 border-zinc-600 text-white h-9" 
                      placeholder="e.g., Concrete Grinder" 
                      required 
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-zinc-200 text-sm">Description</Label>
                    <Input 
                      value={form.description} 
                      onChange={(e) => setForm({ ...form, description: e.target.value })} 
                      className="mt-1 bg-zinc-800 border-zinc-600 text-white h-9" 
                      placeholder="Optional description or notes" 
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-200 text-sm">Equipment Type *</Label>
                    <Select value={form.equipment_type} onValueChange={(v) => setForm({ ...form, equipment_type: v })}>
                      <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-600 text-white h-9">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-600">
                        {typeOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-200 text-sm">Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-600 text-white h-9">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-600 max-h-64">
                        <SelectItem value="Power Tools">⚡ Power Tools</SelectItem>
                        <SelectItem value="Hand Tools">🔧 Hand Tools</SelectItem>
                        <SelectItem value="Floor Equipment">🏗️ Floor Equipment</SelectItem>
                        <SelectItem value="Concrete Equipment">🪨 Concrete Equipment</SelectItem>
                        <SelectItem value="Safety Equipment">🦺 Safety Equipment</SelectItem>
                        <SelectItem value="Vehicles">🚗 Vehicles</SelectItem>
                        <SelectItem value="Trailers">🚛 Trailers</SelectItem>
                        <SelectItem value="Lifting Equipment">⬆️ Lifting Equipment</SelectItem>
                        <SelectItem value="Measuring & Layout">📐 Measuring & Layout</SelectItem>
                        <SelectItem value="Cleaning Equipment">🧹 Cleaning Equipment</SelectItem>
                        <SelectItem value="Generators & Compressors">🔌 Generators & Compressors</SelectItem>
                        <SelectItem value="Scaffolding & Ladders">🪜 Scaffolding & Ladders</SelectItem>
                        <SelectItem value="Other">📦 Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-200 text-sm">Rate Unit</Label>
                    <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                      <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-600 text-white h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-600">
                        {unitOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-200 text-sm">{form.equipment_type === 'rental' ? 'Rental Rate' : 'Cost'} ($)</Label>
                    <Input 
                      type="number" 
                      step="0.01" 
                      value={form.unit_cost} 
                      onChange={(e) => setForm({ ...form, unit_cost: parseFloat(e.target.value) || 0 })} 
                      className="mt-1 bg-zinc-800 border-zinc-600 text-white h-9" 
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-200 text-sm">Default Markup %</Label>
                    <Input 
                      type="number" 
                      value={form.default_markup_percent} 
                      onChange={(e) => setForm({ ...form, default_markup_percent: parseFloat(e.target.value) || 0 })} 
                      className="mt-1 bg-zinc-800 border-zinc-600 text-white h-9" 
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-200 text-sm">Supplier/Rental Company</Label>
                    <Input 
                      value={form.supplier} 
                      onChange={(e) => setForm({ ...form, supplier: e.target.value })} 
                      className="mt-1 bg-zinc-800 border-zinc-600 text-white h-9" 
                      placeholder="e.g., United Rentals" 
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-200 text-sm">Model Number</Label>
                    <Input 
                      value={form.model_number} 
                      onChange={(e) => setForm({ ...form, model_number: e.target.value })} 
                      className="mt-1 bg-zinc-800 border-zinc-600 text-white h-9" 
                      placeholder="e.g., HTC 800" 
                    />
                  </div>
                  {form.equipment_type === 'owned' && (
                    <>
                      <div>
                        <Label className="text-zinc-200 text-sm">Serial Number</Label>
                        <Input 
                          value={form.serial_number} 
                          onChange={(e) => setForm({ ...form, serial_number: e.target.value })} 
                          className="mt-1 bg-zinc-800 border-zinc-600 text-white h-9" 
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-200 text-sm">Purchase Cost ($)</Label>
                        <Input 
                          type="number" 
                          step="0.01" 
                          value={form.purchase_cost || ''} 
                          onChange={(e) => setForm({ ...form, purchase_cost: e.target.value ? parseFloat(e.target.value) : null })} 
                          className="mt-1 bg-zinc-800 border-zinc-600 text-white h-9" 
                        />
                      </div>
                    </>
                  )}
                </div>
                <div className="flex justify-end gap-2 pt-3">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="border-zinc-600 text-zinc-300">Cancel</Button>
                  <Button type="submit" className="bg-orange-600 hover:bg-orange-700 text-white">{editingEquipment ? 'Update' : 'Add Equipment'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 w-4 h-4" />
          <Input
            placeholder="Search equipment..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 bg-zinc-800 border-zinc-600 text-white"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[180px] bg-zinc-800 border-zinc-600 text-white">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-600">
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => (
              <SelectItem key={c._id || c} value={c._id || c}>{c._id || c} ({c.count || 0})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[150px] bg-zinc-800 border-zinc-600 text-white">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-600">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="rental">Rental</SelectItem>
            <SelectItem value="owned">Owned</SelectItem>
            <SelectItem value="consumable">Consumable</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-zinc-800 border-zinc-700 p-4">
          <p className="text-xs text-zinc-400 uppercase">Total Items</p>
          <p className="text-2xl font-bold text-white">{equipment.length}</p>
        </Card>
        <Card className="bg-zinc-800 border-zinc-700 p-4">
          <p className="text-xs text-zinc-400 uppercase">Rentals</p>
          <p className="text-2xl font-bold text-blue-400">{equipment.filter(e => e.equipment_type === 'rental').length}</p>
        </Card>
        <Card className="bg-zinc-800 border-zinc-700 p-4">
          <p className="text-xs text-zinc-400 uppercase">Owned</p>
          <p className="text-2xl font-bold text-green-400">{equipment.filter(e => e.equipment_type === 'owned').length}</p>
        </Card>
        <Card className="bg-zinc-800 border-zinc-700 p-4">
          <p className="text-xs text-zinc-400 uppercase">Categories</p>
          <p className="text-2xl font-bold text-orange-400">{Object.keys(groupedEquipment).length}</p>
        </Card>
      </div>

      {/* Equipment List */}
      {loading ? (
        <div className="text-center py-12 text-zinc-400">Loading equipment...</div>
      ) : filteredEquipment.length === 0 ? (
        <Card className="bg-zinc-800/50 border-zinc-700 p-12 text-center">
          <Settings className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Equipment Found</h3>
          <p className="text-zinc-400 mb-4">
            {equipment.length === 0 
              ? "Add your rental equipment, owned tools, and consumables to quickly include them in estimates."
              : "No equipment matches your filters."}
          </p>
          {!isEmployee && equipment.length === 0 && (
            <Button onClick={() => setDialogOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white">
              <Plus className="w-4 h-4 mr-2" /> Add Your First Equipment
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedEquipment).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => (
            <div key={category}>
              <h3 className="text-lg font-heading font-bold text-orange-400 mb-3 flex items-center gap-2">
                <Settings className="w-5 h-5" /> {category}
                <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-400">{items.length}</Badge>
              </h3>
              <div className="space-y-2">
                {items.map(item => (
                  <Card key={item.id} className="bg-zinc-800/50 border-zinc-700 p-4 hover:border-orange-500/30 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-500/10 rounded-sm flex items-center justify-center">
                          <Settings className="w-5 h-5 text-orange-500" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-white">{item.name}</p>
                            <Badge className={`text-xs ${getTypeColor(item.equipment_type)}`}>
                              {getTypeLabel(item.equipment_type)}
                            </Badge>
                          </div>
                          <p className="text-sm text-zinc-400">
                            {item.description && `${item.description} • `}
                            {item.supplier && `${item.supplier} • `}
                            {item.model_number && `Model: ${item.model_number}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        {isEmployee ? (
                          <div className="text-right">
                            <p className="text-xs text-zinc-400">Unit</p>
                            <p className="text-lg font-bold tabular-nums text-white">/{item.unit}</p>
                          </div>
                        ) : (
                          <>
                            <div className="text-right">
                              <p className="text-xs text-zinc-400">{item.equipment_type === 'rental' ? 'Rate' : 'Cost'}</p>
                              <p className="text-lg font-bold tabular-nums text-white">${item.unit_cost?.toFixed(2)}/{item.unit}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-zinc-400">Markup</p>
                              <p className="text-lg font-bold tabular-nums text-orange-400">{item.default_markup_percent}%</p>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => handleEdit(item)} className="h-8 w-8 text-zinc-400 hover:text-white">
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="h-8 w-8 text-zinc-400 hover:text-red-400">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// Coverage Calculator Page (Pro Feature)
function CoverageCalculatorPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkedMaterialId = searchParams.get('material');
  const [trades, setTrades] = useState([]);
  const [selectedTrade, setSelectedTrade] = useState('');
  const [formulas, setFormulas] = useState([]);
  const [calculations, setCalculations] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingFormula, setEditingFormula] = useState(null);
  const [materialCatalog, setMaterialCatalog] = useState([]);
  const [formulaForm, setFormulaForm] = useState({
    name: '', description: '', trade: '', unit_in: 'sq_ft', unit_out: 'gallons',
    coverage_rate: 100, waste_factor: 10, cost_per_unit: '', markup_percent: '', 
    pricing_unit: 'per_unit', notes: '', linked_material_id: ''
  });
  
  // State for adding materials to catalog
  const [showAddMaterialDialog, setShowAddMaterialDialog] = useState(false);
  const [addingMaterialFor, setAddingMaterialFor] = useState(null); // Store the result we're adding material for
  const [materialForm, setMaterialForm] = useState({
    name: '', description: '', category: '', unit: 'gallon',
    unit_cost: 0, default_markup_percent: 10, supplier: '', sku: '',
    coverage_rate: null, waste_factor: 10
  });
  
  // State for "Use in Estimate" functionality
  const [showUseInEstimateDialog, setShowUseInEstimateDialog] = useState(false);
  const [estimates, setEstimates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedEstimateId, setSelectedEstimateId] = useState('');
  const [createNewEstimate, setCreateNewEstimate] = useState(false);
  const [newEstimateForm, setNewEstimateForm] = useState({
    customer_id: '', title: '', description: ''
  });

  const unitOptions = {
    input: [
      { value: 'sq_ft', label: 'Square Feet' },
      { value: 'linear_ft', label: 'Linear Feet' },
      { value: 'cubic_ft', label: 'Cubic Feet' },
      { value: 'sq_yards', label: 'Square Yards' },
      { value: 'linear_yards', label: 'Linear Yards' },
      { value: 'cubic_yards', label: 'Cubic Yards' },
      { value: 'sq_meters', label: 'Square Meters' },
      { value: 'linear_meters', label: 'Linear Meters' },
      { value: 'sheets', label: 'Sheets' },
      { value: 'posts', label: 'Posts' },
      { value: 'linear_ft_wall', label: 'Linear Ft (Wall)' },
      { value: 'each', label: 'Each / Unit' },
      { value: 'slabs', label: 'Slabs' },
      { value: 'panels', label: 'Panels' },
    ],
    output: [
      { value: 'gallons', label: 'Gallons' },
      { value: 'lbs', label: 'Pounds (lbs)' },
      { value: 'tons', label: 'Tons' },
      { value: 'bags_50lb', label: 'Bags (50 lb)' },
      { value: 'bags_25lb', label: 'Bags (25 lb)' },
      { value: 'bags_80lb', label: 'Bags (80 lb)' },
      { value: 'bags', label: 'Bags' },
      { value: 'cubic_yards', label: 'Cubic Yards' },
      { value: 'cubic_ft', label: 'Cubic Feet' },
      { value: 'sq_ft', label: 'Square Feet' },
      { value: 'sq_yards', label: 'Square Yards' },
      { value: 'linear_ft', label: 'Linear Feet' },
      { value: 'linear_yards', label: 'Linear Yards' },
      { value: 'sheets_4x8', label: 'Sheets (4x8 ft)' },
      { value: 'sheets_3x5', label: 'Sheets (3x5 ft)' },
      { value: 'sheets', label: 'Sheets' },
      { value: 'rolls', label: 'Rolls' },
      { value: 'rolls_500ft', label: 'Rolls (500 ft)' },
      { value: 'rolls_50ft', label: 'Rolls (50 ft)' },
      { value: 'bundles', label: 'Bundles' },
      { value: 'panels', label: 'Panels' },
      { value: 'posts', label: 'Posts' },
      { value: 'pieces_8ft', label: 'Pieces (8 ft)' },
      { value: 'pieces_10ft', label: 'Pieces (10 ft)' },
      { value: 'pieces_12ft', label: 'Pieces (12 ft)' },
      { value: 'buckets_5gal', label: 'Buckets (5 gal)' },
      { value: 'buckets_1gal', label: 'Buckets (1 gal)' },
      { value: 'board_feet', label: 'Board Feet' },
      { value: 'pallets', label: 'Pallets' },
      { value: 'kits', label: 'Kits' },
      { value: 'each', label: 'Each / Unit' },
    ],
    // Pricing units - what unit you're charging the customer per
    pricing: [
      { value: 'per_sq_ft', label: 'Per Square Foot' },
      { value: 'per_linear_ft', label: 'Per Linear Foot' },
      { value: 'per_sq_yard', label: 'Per Square Yard' },
      { value: 'per_linear_yard', label: 'Per Linear Yard' },
      { value: 'per_cubic_yard', label: 'Per Cubic Yard' },
      { value: 'per_ton', label: 'Per Ton' },
      { value: 'per_lb', label: 'Per Pound' },
      { value: 'per_gallon', label: 'Per Gallon' },
      { value: 'per_bag', label: 'Per Bag' },
      { value: 'per_sheet', label: 'Per Sheet' },
      { value: 'per_panel', label: 'Per Panel' },
      { value: 'per_roll', label: 'Per Roll' },
      { value: 'per_pallet', label: 'Per Pallet' },
      { value: 'per_unit', label: 'Per Unit/Each' },
      { value: 'flat_rate', label: 'Flat Rate' },
    ]
  };

  useEffect(() => {
    fetchTrades();
    fetchMaterialCatalog();
  }, []);

  const fetchMaterialCatalog = async () => {
    try {
      const res = await apiClient.get('/material-catalog');
      setMaterialCatalog(res.data);
    } catch (err) {
      console.log('Could not load material catalog');
    }
  };

  useEffect(() => {
    if (selectedTrade) {
      fetchFormulas(selectedTrade);
      setCalculations([]);
      setResults([]);
    }
  }, [selectedTrade]);

  const fetchTrades = async () => {
    try {
      const res = await apiClient.get('/coverage-calculator/trades');
      setTrades(res.data);
      if (res.data.length > 0) {
        // Try to match company's industry type
        const companyTrade = user?.company?.industry_type;
        if (companyTrade && res.data.find(t => t.id === companyTrade)) {
          setSelectedTrade(companyTrade);
        } else {
          setSelectedTrade(res.data[0].id);
        }
      }
    } catch (err) {
      toast.error('Failed to load trades');
    }
    setLoading(false);
  };

  const fetchFormulas = async (trade) => {
    try {
      const res = await apiClient.get(`/coverage-calculator/formulas?trade=${trade}`);
      setFormulas(res.data);
      // Initialize calculations array
      setCalculations(res.data.map(f => ({ formula_id: f.id, input_value: 0, custom_waste_factor: null })));
    } catch (err) {
      toast.error('Failed to load formulas');
    }
  };

  // Handle when a material is linked via URL parameter
  useEffect(() => {
    if (linkedMaterialId && materialCatalog.length > 0 && !loading) {
      const material = materialCatalog.find(m => m.id === linkedMaterialId);
      if (material) {
        // Check if there's already a formula linked to this material
        const existingFormula = formulas.find(f => f.linked_material_id === linkedMaterialId);
        if (existingFormula) {
          toast.success(`Material "${material.name}" is already linked to a formula`);
        } else {
          // Open the add dialog pre-filled with material data
          setFormulaForm({
            name: material.name,
            description: material.description || '',
            trade: material.category || selectedTrade,
            unit_in: 'sq_ft',
            unit_out: 'gallons',
            coverage_rate: material.coverage_rate || 100,
            waste_factor: material.waste_factor || 10,
            cost_per_unit: material.unit_cost || '',
            markup_percent: material.default_markup_percent || '',
            pricing_unit: 'per_unit',
            notes: '',
            linked_material_id: linkedMaterialId
          });
          setShowAddDialog(true);
          toast.info(`Creating formula for "${material.name}"`);
        }
      }
    }
  }, [linkedMaterialId, materialCatalog, loading]);

  const handleCalculate = async () => {
    const activeCalcs = calculations.filter(c => c.input_value > 0);
    if (activeCalcs.length === 0) {
      toast.error('Enter at least one measurement');
      return;
    }
    
    setCalculating(true);
    try {
      const res = await apiClient.post(`/coverage-calculator/calculate?trade=${selectedTrade}`, activeCalcs);
      setResults(res.data);
      toast.success('Calculations complete!');
    } catch (err) {
      toast.error('Calculation failed');
    }
    setCalculating(false);
  };

  const updateCalculation = (formulaId, value) => {
    setCalculations(prev => prev.map(c => 
      c.formula_id === formulaId ? { ...c, input_value: parseFloat(value) || 0 } : c
    ));
  };

  const handleSaveFormula = async () => {
    if (!formulaForm.name || !formulaForm.trade) {
      toast.error('Name and trade are required');
      return;
    }
    
    // Prepare payload with proper number conversion
    const payload = {
      ...formulaForm,
      cost_per_unit: formulaForm.cost_per_unit !== '' ? parseFloat(formulaForm.cost_per_unit) : null,
      markup_percent: formulaForm.markup_percent !== '' ? parseFloat(formulaForm.markup_percent) : null
    };
    
    try {
      if (editingFormula) {
        await apiClient.put(`/coverage-calculator/formulas/${editingFormula.id}`, payload);
        toast.success('Formula updated');
      } else {
        await apiClient.post('/coverage-calculator/formulas', payload);
        toast.success('Formula created');
      }
      setShowAddDialog(false);
      setEditingFormula(null);
      setFormulaForm({ name: '', description: '', trade: selectedTrade, unit_in: 'sq_ft', unit_out: 'gallons', coverage_rate: 100, waste_factor: 10, cost_per_unit: '', markup_percent: '', pricing_unit: 'per_unit', notes: '', linked_material_id: '' });
      fetchFormulas(selectedTrade);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save formula');
    }
  };

  const handleDeleteFormula = async (formulaId) => {
    if (!window.confirm('Delete this custom formula?')) return;
    try {
      await apiClient.delete(`/coverage-calculator/formulas/${formulaId}`);
      toast.success('Formula deleted');
      fetchFormulas(selectedTrade);
    } catch (err) {
      toast.error('Failed to delete formula');
    }
  };

  const handleSyncToCatalog = async () => {
    if (!selectedTrade) {
      toast.error('Please select a trade first');
      return;
    }
    try {
      setSyncing(true);
      const res = await apiClient.post(`/coverage-calculator/sync-to-catalog?trade=${selectedTrade}`);
      const { created, updated, errors } = res.data;
      if (errors > 0) {
        toast.warning(`Synced ${created + updated} formulas (${errors} errors)`);
      } else {
        toast.success(`Synced ${created} new, ${updated} updated materials to catalog`);
      }
      // Refresh material catalog
      fetchMaterialCatalog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sync to catalog');
    } finally {
      setSyncing(false);
    }
  };

  const openEditDialog = (formula) => {
    setEditingFormula(formula);
    setFormulaForm({
      name: formula.name,
      description: formula.description || '',
      trade: formula.trade,
      unit_in: formula.unit_in,
      unit_out: formula.unit_out,
      coverage_rate: formula.coverage_rate,
      waste_factor: formula.waste_factor,
      cost_per_unit: formula.cost_per_unit || '',
      markup_percent: formula.markup_percent || '',
      pricing_unit: formula.pricing_unit || 'per_unit',
      notes: formula.notes || '',
      linked_material_id: formula.linked_material_id || ''
    });
    setShowAddDialog(true);
  };

  const clearResults = () => {
    setResults([]);
    setCalculations(formulas.map(f => ({ formula_id: f.id, input_value: 0, custom_waste_factor: null })));
  };

  const getUnitLabel = (unit) => {
    const all = [...unitOptions.input, ...unitOptions.output, ...unitOptions.pricing];
    return all.find(u => u.value === unit)?.label || unit;
  };

  const getPricingUnitLabel = (unit) => {
    return unitOptions.pricing.find(u => u.value === unit)?.label || unit;
  };

  // Handle linking a material from the catalog to a formula
  const handleLinkMaterial = (materialId) => {
    const material = materialCatalog.find(m => m.id === materialId);
    if (material) {
      // Auto-fill formula fields from the linked material
      setFormulaForm(prev => ({
        ...prev,
        linked_material_id: materialId,
        name: material.name,
        cost_per_unit: material.unit_cost || '',
        markup_percent: material.default_markup_percent || '',
        coverage_rate: material.coverage_rate || prev.coverage_rate,
        waste_factor: material.waste_factor || prev.waste_factor,
        notes: material.description || prev.notes
      }));
    } else if (materialId === '') {
      setFormulaForm(prev => ({ ...prev, linked_material_id: '' }));
    }
  };

  // Open dialog to add material to catalog from a result
  const openAddMaterialDialog = (result) => {
    // Get the human-readable trade name for the category
    const tradeName = trades.find(t => t.id === selectedTrade)?.name || selectedTrade;
    
    setAddingMaterialFor(result);
    setMaterialForm({
      name: result.formula_name,
      description: result.notes || `Material for ${result.formula_name}`,
      category: tradeName,  // Use human-readable name, not ID
      unit: result.output_unit || 'gallon',
      unit_cost: result.cost_per_unit || 0,
      default_markup_percent: result.markup_percent || 10,
      supplier: '',
      sku: '',
      coverage_rate: result.coverage_rate || null,
      waste_factor: result.waste_factor || 10
    });
    setShowAddMaterialDialog(true);
  };

  // Save new material to catalog
  const handleSaveMaterial = async () => {
    if (!materialForm.name || materialForm.unit_cost <= 0) {
      toast.error('Name and unit cost are required');
      return;
    }
    
    try {
      await apiClient.post('/material-catalog', materialForm);
      toast.success(`"${materialForm.name}" added to Material Catalog!`);
      setShowAddMaterialDialog(false);
      setAddingMaterialFor(null);
      // Refresh material catalog to update matches
      fetchMaterialCatalog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add material');
    }
  };

  // Fetch estimates and customers for "Use in Estimate" dialog
  const fetchEstimatesAndCustomers = async () => {
    try {
      const [estRes, custRes] = await Promise.all([
        apiClient.get('/profitable-estimates'),
        apiClient.get('/customers')
      ]);
      setEstimates(estRes.data.filter(e => e.status === 'draft' || e.status === 'sent'));
      setCustomers(custRes.data);
    } catch (err) {
      console.log('Could not fetch estimates/customers');
    }
  };

  // Open "Use in Estimate" dialog
  const openUseInEstimateDialog = () => {
    if (results.length === 0) {
      toast.error('Calculate quantities first');
      return;
    }
    fetchEstimatesAndCustomers();
    setSelectedEstimateId('');
    setCreateNewEstimate(false);
    setNewEstimateForm({ customer_id: '', title: '', description: '' });
    setShowUseInEstimateDialog(true);
  };

  // Handle adding results to an existing or new estimate
  const handleUseInEstimate = async () => {
    const materialItems = results.map(r => {
      const catalogMatch = materialCatalog.find(m => {
        const catalogName = (m.name || '').toLowerCase().trim();
        const formulaName = r.formula_name.toLowerCase().trim();
        return catalogName === formulaName || 
               catalogName.startsWith(formulaName) || 
               formulaName.startsWith(catalogName);
      });
      return {
        description: r.formula_name,
        quantity: r.recommended_quantity,
        unit: r.output_unit || 'each',
        unit_cost: catalogMatch?.unit_cost || r.cost_per_unit || 0,
        markup_percent: catalogMatch?.default_markup_percent || r.markup_percent || 20,
        from_coverage_calc: true,
        coverage_notes: `Coverage: ${r.coverage_rate} ${r.input_unit} per ${r.output_unit}, Waste: ${r.waste_factor}%`
      };
    });

    try {
      if (createNewEstimate) {
        // Create new estimate with materials
        if (!newEstimateForm.customer_id || !newEstimateForm.title) {
          toast.error('Customer and title are required');
          return;
        }
        const estimateData = {
          customer_id: newEstimateForm.customer_id,
          title: newEstimateForm.title,
          description: newEstimateForm.description || `Materials from Coverage Calculator (${selectedTrade})`,
          labor_items: [{ employee_id: "", employee_name: "", description: "Labor", hours: 0, hourly_rate: 0 }],
          material_items: materialItems,
          subcontractor_items: []
        };
        const res = await apiClient.post('/profitable-estimates', estimateData);
        toast.success(`Estimate created with ${materialItems.length} materials!`);
        setShowUseInEstimateDialog(false);
        navigate(`/estimates?highlight=${res.data.id}`);
      } else {
        // Add to existing estimate
        if (!selectedEstimateId) {
          toast.error('Select an estimate');
          return;
        }
        // Get existing estimate
        const estRes = await apiClient.get(`/profitable-estimates/${selectedEstimateId}`);
        const existingMaterials = estRes.data.material_items || [];
        
        // Update with new materials
        const updatedData = {
          ...estRes.data,
          material_items: [...existingMaterials, ...materialItems]
        };
        await apiClient.put(`/profitable-estimates/${selectedEstimateId}`, updatedData);
        toast.success(`Added ${materialItems.length} materials to estimate!`);
        setShowUseInEstimateDialog(false);
        navigate(`/estimates?highlight=${selectedEstimateId}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add materials to estimate');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-2 border-yellow-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="coverage-calculator-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-white">Coverage Calculator</h1>
          <p className="text-zinc-400">Calculate material quantities based on coverage rates</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleSyncToCatalog}
            variant="outline"
            className="border-primary text-primary hover:bg-primary/10"
            disabled={!selectedTrade || syncing}
            title="Sync all formulas for selected trade to Materials Catalog"
            data-testid="sync-to-catalog-btn"
          >
            {syncing ? (
              <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Syncing...</>
            ) : (
              <><Database className="h-4 w-4 mr-2" /> Sync to Catalog</>
            )}
          </Button>
          <Button
            onClick={() => {
              setEditingFormula(null);
              setFormulaForm({ name: '', description: '', trade: selectedTrade, unit_in: 'sq_ft', unit_out: 'gallons', coverage_rate: 100, waste_factor: 10, cost_per_unit: '', markup_percent: '', pricing_unit: 'per_unit', notes: '', linked_material_id: '' });
              setShowAddDialog(true);
            }}
            className="bg-yellow-500 hover:bg-yellow-600 text-black"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Custom Formula
          </Button>
        </div>
      </div>

      {/* Trade Selector */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-4">
          <Label className="text-zinc-300 mb-2 block">Select Trade</Label>
          <div className="flex flex-wrap gap-2">
            {trades.map(trade => (
              <Button
                key={trade.id}
                variant={selectedTrade === trade.id ? 'default' : 'outline'}
                onClick={() => setSelectedTrade(trade.id)}
                className={selectedTrade === trade.id ? 'bg-yellow-500 text-black' : 'border-zinc-700 text-zinc-300'}
                size="sm"
              >
                <span className="mr-1">{trade.icon}</span> {trade.name}
                <Badge className="ml-2 bg-zinc-700 text-zinc-300">{trade.formula_count}</Badge>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calculator Input */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Enter Measurements</CardTitle>
            <CardDescription className="text-zinc-400">
              Enter your project measurements to calculate material quantities
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {formulas.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">No formulas available for this trade</p>
            ) : (
              <>
                {formulas.map(formula => {
                  const calc = calculations.find(c => c.formula_id === formula.id);
                  return (
                    <div key={formula.id} className="p-3 bg-zinc-800 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-medium text-white flex items-center gap-2">
                            {formula.name}
                            {formula.is_custom && (
                              <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">Custom</Badge>
                            )}
                          </p>
                          {formula.description && (
                            <p className="text-xs text-zinc-500">{formula.description}</p>
                          )}
                          <p className="text-xs text-blue-400 mt-0.5">
                            📐 {formula.coverage_rate} {getUnitLabel(formula.unit_in)} = 1 {getUnitLabel(formula.unit_out)} • Waste: {formula.waste_factor}%
                          </p>
                          {(formula.cost_per_unit || formula.markup_percent || formula.pricing_unit) && (
                            <p className="text-xs text-green-400 mt-0.5">
                              💰 {formula.cost_per_unit ? `$${formula.cost_per_unit.toFixed(2)}` : '$--'}
                              {formula.pricing_unit && <span className="text-zinc-500"> {getPricingUnitLabel(formula.pricing_unit)}</span>}
                              {formula.markup_percent && <span> • +{formula.markup_percent}% markup</span>}
                            </p>
                          )}
                          {/* Show linked material */}
                          {formula.linked_material_id && (() => {
                            const linkedMat = materialCatalog.find(m => m.id === formula.linked_material_id);
                            return linkedMat ? (
                              <p className="text-xs text-primary mt-0.5 flex items-center gap-1">
                                <Wrench className="w-3 h-3" /> Linked: {linkedMat.name}
                              </p>
                            ) : null;
                          })()}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditDialog(formula)} title="Edit coverage & pricing">
                            <Pencil className="h-3 w-3 text-zinc-400 hover:text-yellow-400" />
                          </Button>
                          {formula.is_custom && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteFormula(formula.id)} title="Delete formula">
                              <Trash2 className="h-3 w-3 text-red-400" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={calc?.input_value || ''}
                          onChange={(e) => updateCalculation(formula.id, e.target.value)}
                          className="bg-zinc-900 border-zinc-700 text-white"
                          placeholder="0"
                        />
                        <span className="text-zinc-400 text-sm whitespace-nowrap">{getUnitLabel(formula.unit_in)}</span>
                      </div>
                      {formula.notes && (
                        <p className="text-xs text-zinc-500 mt-2 flex items-start gap-1">
                          <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          {formula.notes}
                        </p>
                      )}
                    </div>
                  );
                })}
                
                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={handleCalculate}
                    disabled={calculating}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    {calculating ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        Calculating...
                      </span>
                    ) : (
                      <>
                        <Calculator className="h-4 w-4 mr-2" /> Calculate Quantities
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={clearResults} className="border-zinc-700">
                    Clear
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Material Quantities</CardTitle>
            <CardDescription className="text-zinc-400">
              Calculated quantities with waste factor included
            </CardDescription>
          </CardHeader>
          <CardContent>
            {results.length === 0 ? (
              <div className="text-center py-12">
                <Target className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-500">Enter measurements and click Calculate</p>
              </div>
            ) : (
              <div className="space-y-3">
                {results.map((result, idx) => {
                  // Check if there's a matching catalog item - improved matching logic
                  const catalogMatch = materialCatalog.find(m => {
                    const catalogName = (m.name || '').toLowerCase().trim();
                    const formulaName = result.formula_name.toLowerCase().trim();
                    
                    // Exact match first
                    if (catalogName === formulaName) return true;
                    
                    // Check if catalog name starts with formula name or vice versa
                    if (catalogName.startsWith(formulaName) || formulaName.startsWith(catalogName)) return true;
                    
                    // Check for key material words matching (must match significant words, not just "a", "the", etc.)
                    const significantWords = formulaName.split(/\s+/).filter(w => w.length > 4);
                    const catalogWords = catalogName.split(/\s+/).filter(w => w.length > 4);
                    
                    // Must have at least one significant word match where it's a substantial match
                    return significantWords.some(fw => catalogWords.some(cw => 
                      cw === fw || (cw.length > 5 && fw.length > 5 && (cw.includes(fw) || fw.includes(cw)))
                    ));
                  });
                  
                  // Calculate estimated cost - only if we have valid unit_cost
                  const estimatedCost = catalogMatch && catalogMatch.unit_cost && !isNaN(catalogMatch.unit_cost) ? 
                    (catalogMatch.unit_cost * result.recommended_quantity * (1 + (catalogMatch.default_markup_percent || 0) / 100)) : null;
                  
                  return (
                  <div key={idx} className="p-4 bg-zinc-800 rounded-lg border border-zinc-700 relative">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-white">{result.formula_name}</p>
                      <Badge className="bg-green-500/20 text-green-400">
                        {result.waste_factor}% waste
                      </Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-xs text-zinc-500">Input</p>
                        <p className="text-lg text-white">{result.input_value.toLocaleString()}</p>
                        <p className="text-xs text-zinc-400">{getUnitLabel(result.input_unit)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500">Base Qty</p>
                        <p className="text-lg text-zinc-300">{result.base_quantity}</p>
                        <p className="text-xs text-zinc-400">{getUnitLabel(result.output_unit)}</p>
                      </div>
                      <div className="bg-yellow-500/10 rounded-lg p-2">
                        <p className="text-xs text-yellow-400">Order</p>
                        <p className="text-2xl font-bold text-yellow-500">{result.recommended_quantity}</p>
                        <p className="text-xs text-zinc-400">{getUnitLabel(result.output_unit)}</p>
                      </div>
                    </div>
                    {/* Catalog Pricing */}
                    {catalogMatch && (
                      <div className="mt-3 pt-3 border-t border-zinc-700 bg-green-500/5 -mx-4 -mb-4 p-4 rounded-b-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-green-400">💰 Catalog Match: {catalogMatch.name}</p>
                            <p className="text-xs text-zinc-400">${catalogMatch.unit_cost?.toFixed(2)}/{catalogMatch.unit} + {catalogMatch.default_markup_percent || 0}% markup</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-zinc-500">Est. Cost</p>
                            <p className="text-lg font-bold text-green-400">${estimatedCost?.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {!catalogMatch && (
                      <div className="mt-3 pt-3 border-t border-zinc-700 bg-yellow-500/5 -mx-4 -mb-4 p-4 rounded-b-lg">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-yellow-400">
                            ⚠️ No catalog match found
                          </p>
                          <Button
                            size="sm"
                            onClick={() => openAddMaterialDialog(result)}
                            className="bg-yellow-500 hover:bg-yellow-600 text-black text-xs h-7"
                            data-testid={`add-material-btn-${idx}`}
                          >
                            <Plus className="h-3 w-3 mr-1" /> Add to Catalog
                          </Button>
                        </div>
                        {result.notes && (
                          <p className="text-xs text-zinc-500 mt-2">
                            💡 {result.notes}
                          </p>
                        )}
                      </div>
                    )}
                    {/* Delete individual result */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setResults(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute top-2 right-2 h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  );
                })}
                
                {/* Summary with Total Cost */}
                <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-yellow-400 font-medium mb-2">📋 Shopping List Summary</p>
                  <div className="space-y-1">
                    {results.map((r, idx) => {
                      // Improved matching logic for shopping list
                      const match = materialCatalog.find(m => {
                        const catalogName = (m.name || '').toLowerCase().trim();
                        const formulaName = r.formula_name.toLowerCase().trim();
                        
                        if (catalogName === formulaName) return true;
                        if (catalogName.startsWith(formulaName) || formulaName.startsWith(catalogName)) return true;
                        
                        const significantWords = formulaName.split(/\s+/).filter(w => w.length > 4);
                        const catalogWords = catalogName.split(/\s+/).filter(w => w.length > 4);
                        
                        return significantWords.some(fw => catalogWords.some(cw => 
                          cw === fw || (cw.length > 5 && fw.length > 5 && (cw.includes(fw) || fw.includes(cw)))
                        ));
                      });
                      const cost = match && match.unit_cost && !isNaN(match.unit_cost) ? 
                        (match.unit_cost * r.recommended_quantity * (1 + (match.default_markup_percent || 0) / 100)) : null;
                      return (
                        <p key={idx} className="text-sm text-zinc-300 flex justify-between">
                          <span>• {r.recommended_quantity} {getUnitLabel(r.output_unit)} of {r.formula_name}</span>
                          {cost && <span className="text-green-400">${cost.toFixed(2)}</span>}
                        </p>
                      );
                    })}
                  </div>
                  {/* Total Estimated Cost */}
                  {(() => {
                    const totalCost = results.reduce((sum, r) => {
                      // Improved matching logic for total cost
                      const match = materialCatalog.find(m => {
                        const catalogName = (m.name || '').toLowerCase().trim();
                        const formulaName = r.formula_name.toLowerCase().trim();
                        
                        if (catalogName === formulaName) return true;
                        if (catalogName.startsWith(formulaName) || formulaName.startsWith(catalogName)) return true;
                        
                        const significantWords = formulaName.split(/\s+/).filter(w => w.length > 4);
                        const catalogWords = catalogName.split(/\s+/).filter(w => w.length > 4);
                        
                        return significantWords.some(fw => catalogWords.some(cw => 
                          cw === fw || (cw.length > 5 && fw.length > 5 && (cw.includes(fw) || fw.includes(cw)))
                        ));
                      });
                      if (match && match.unit_cost && !isNaN(match.unit_cost)) {
                        return sum + (match.unit_cost * r.recommended_quantity * (1 + (match.default_markup_percent || 0) / 100));
                      }
                      return sum;
                    }, 0);
                    return totalCost > 0 && (
                      <div className="mt-3 pt-3 border-t border-yellow-500/30 flex justify-between items-center">
                        <span className="text-yellow-400 font-medium">Estimated Total:</span>
                        <span className="text-2xl font-bold text-green-400">${totalCost.toFixed(2)}</span>
                      </div>
                    );
                  })()}
                </div>

                {/* Delete All Button */}
                <Button
                  variant="outline"
                  onClick={clearResults}
                  className="w-full mt-4 border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete All Results
                </Button>

                {/* Use in Estimate Button */}
                <Button
                  onClick={openUseInEstimateDialog}
                  className="w-full mt-2 bg-green-600 hover:bg-green-700 text-white"
                  data-testid="use-in-estimate-btn"
                >
                  <FileText className="w-4 h-4 mr-2" /> Use in Estimate
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Use in Estimate Dialog */}
      <Dialog open={showUseInEstimateDialog} onOpenChange={setShowUseInEstimateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-[95vw] sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Add Materials to Estimate</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Add {results.length} calculated materials to an existing or new estimate
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Toggle between existing and new estimate */}
            <div className="flex gap-2">
              <Button
                variant={!createNewEstimate ? "default" : "outline"}
                onClick={() => setCreateNewEstimate(false)}
                className={!createNewEstimate ? "bg-yellow-500 text-black" : "border-zinc-700"}
              >
                Existing Estimate
              </Button>
              <Button
                variant={createNewEstimate ? "default" : "outline"}
                onClick={() => setCreateNewEstimate(true)}
                className={createNewEstimate ? "bg-yellow-500 text-black" : "border-zinc-700"}
              >
                Create New
              </Button>
            </div>

            {!createNewEstimate ? (
              /* Select existing estimate */
              <div>
                <Label className="text-zinc-300">Select Estimate</Label>
                <Select value={selectedEstimateId} onValueChange={setSelectedEstimateId}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue placeholder="Choose an estimate..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                    {estimates.length === 0 ? (
                      <SelectItem value="none" disabled className="text-zinc-500">No draft estimates found</SelectItem>
                    ) : (
                      estimates.map(est => (
                        <SelectItem key={est.id} value={est.id} className="text-white">
                          {est.title} - {est.customer_name || 'No customer'}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              /* Create new estimate form */
              <div className="space-y-3">
                <div>
                  <Label className="text-zinc-300">Customer *</Label>
                  <Select value={newEstimateForm.customer_id} onValueChange={(v) => setNewEstimateForm(prev => ({ ...prev, customer_id: v }))}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                      <SelectValue placeholder="Select customer..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                      {customers.map(c => (
                        <SelectItem key={c.id} value={c.id} className="text-white">{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300">Estimate Title *</Label>
                  <Input
                    value={newEstimateForm.title}
                    onChange={(e) => setNewEstimateForm(prev => ({ ...prev, title: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    placeholder="e.g., Garage Floor Coating"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Description</Label>
                  <Textarea
                    value={newEstimateForm.description}
                    onChange={(e) => setNewEstimateForm(prev => ({ ...prev, description: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    placeholder="Optional description..."
                    rows={2}
                  />
                </div>
              </div>
            )}

            {/* Materials preview */}
            <div className="bg-zinc-800 rounded-lg p-3 max-h-48 overflow-y-auto">
              <p className="text-xs text-zinc-400 mb-2">Materials to add:</p>
              {results.map((r, idx) => (
                <p key={idx} className="text-sm text-zinc-300">
                  • {r.recommended_quantity} {getUnitLabel(r.output_unit)} of {r.formula_name}
                </p>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUseInEstimateDialog(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleUseInEstimate} className="bg-green-600 hover:bg-green-700 text-white">
              <Plus className="w-4 h-4 mr-2" /> Add to Estimate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Formula Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-[95vw] sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingFormula ? 'Edit Coverage Formula' : 'Add Custom Formula'}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Create a custom coverage calculation formula
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300">Formula Name *</Label>
              <Input
                value={formulaForm.name}
                onChange={(e) => setFormulaForm(prev => ({ ...prev, name: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="e.g., Premium Paint Coverage"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Description</Label>
              <Input
                value={formulaForm.description}
                onChange={(e) => setFormulaForm(prev => ({ ...prev, description: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="Optional description"
              />
            </div>
            
            {/* Link to Material from Catalog */}
            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <Label className="text-green-400 flex items-center gap-2 mb-2">
                <Wrench className="w-4 h-4" /> Link to Material Catalog
              </Label>
              <p className="text-xs text-zinc-400 mb-2">
                Link this formula to a material in your catalog to auto-sync pricing and coverage settings
              </p>
              <Select value={formulaForm.linked_material_id || "none"} onValueChange={(v) => handleLinkMaterial(v === "none" ? "" : v)}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Select material to link (optional)" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                  <SelectItem value="none">No linked material</SelectItem>
                  {materialCatalog.map(mat => (
                    <SelectItem key={mat.id} value={mat.id} className="text-white">
                      {mat.name} - ${mat.unit_cost?.toFixed(2)}/{mat.unit}
                      {mat.coverage_rate ? ` (${mat.coverage_rate} sq ft coverage)` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formulaForm.linked_material_id && (
                <p className="text-xs text-green-400 mt-2 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Formula linked to material - pricing will sync automatically
                </p>
              )}
            </div>
            <div>
              <Label className="text-zinc-300">Trade *</Label>
              <Select value={formulaForm.trade} onValueChange={(v) => setFormulaForm(prev => ({ ...prev, trade: v }))}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue placeholder="Select trade" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {trades.map(trade => (
                    <SelectItem key={trade.id} value={trade.id} className="text-white">
                      {trade.icon} {trade.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Input Unit</Label>
                <Select value={formulaForm.unit_in} onValueChange={(v) => setFormulaForm(prev => ({ ...prev, unit_in: v }))}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {unitOptions.input.map(u => (
                      <SelectItem key={u.value} value={u.value} className="text-white">{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Output Unit</Label>
                <Select value={formulaForm.unit_out} onValueChange={(v) => setFormulaForm(prev => ({ ...prev, unit_out: v }))}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                    {unitOptions.output.map(u => (
                      <SelectItem key={u.value} value={u.value} className="text-white">{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Coverage Rate *</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formulaForm.coverage_rate}
                    onChange={(e) => setFormulaForm(prev => ({ ...prev, coverage_rate: parseFloat(e.target.value) || 0 }))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="e.g., 350 or 0.8"
                  />
                  <span className="text-xs text-zinc-500 whitespace-nowrap">input per output</span>
                </div>
              </div>
              <div>
                <Label className="text-zinc-300">Waste Factor %</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formulaForm.waste_factor}
                  onChange={(e) => setFormulaForm(prev => ({ ...prev, waste_factor: parseFloat(e.target.value) || 0 }))}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>
            
            {/* Pricing Section */}
            <div className="border-t border-zinc-700 pt-4 mt-2">
              <p className="text-sm text-yellow-400 mb-3">💰 Pricing for Estimates</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-zinc-300 text-xs">Cost ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formulaForm.cost_per_unit}
                    onChange={(e) => setFormulaForm(prev => ({ ...prev, cost_per_unit: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9"
                    placeholder="25.00"
                    data-testid="formula-cost-per-unit"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Pricing Unit</Label>
                  <Select value={formulaForm.pricing_unit} onValueChange={(v) => setFormulaForm(prev => ({ ...prev, pricing_unit: v }))}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9">
                      <SelectValue placeholder="Per..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                      {unitOptions.pricing.map(u => (
                        <SelectItem key={u.value} value={u.value} className="text-white">{u.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Markup %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formulaForm.markup_percent}
                    onChange={(e) => setFormulaForm(prev => ({ ...prev, markup_percent: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9"
                    placeholder="35"
                    data-testid="formula-markup-percent"
                  />
                </div>
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                Example: $2.50 per sq ft + 30% markup = $3.25/sq ft customer price
              </p>
            </div>
            
            <div>
              <Label className="text-zinc-300">Notes</Label>
              <Textarea
                value={formulaForm.notes}
                onChange={(e) => setFormulaForm(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="Tips or special considerations"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleSaveFormula} className="bg-yellow-500 hover:bg-yellow-600 text-black">
              {editingFormula ? 'Update Formula' : 'Create Formula'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Material to Catalog Dialog */}
      <Dialog open={showAddMaterialDialog} onOpenChange={setShowAddMaterialDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-[95vw] sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Add Material to Catalog</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Add this material to your catalog for automatic pricing in future calculations
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300">Material Name *</Label>
              <Input
                value={materialForm.name}
                onChange={(e) => setMaterialForm(prev => ({ ...prev, name: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="e.g., Epoxy Floor Coating"
                data-testid="material-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Unit Cost *</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={materialForm.unit_cost}
                    onChange={(e) => setMaterialForm(prev => ({ ...prev, unit_cost: parseFloat(e.target.value) || 0 }))}
                    className="bg-zinc-800 border-zinc-700 text-white pl-7"
                    placeholder="0.00"
                    data-testid="material-cost-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300">Unit</Label>
                <Select 
                  value={materialForm.unit} 
                  onValueChange={(v) => setMaterialForm(prev => ({ ...prev, unit: v }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {unitOptions.output.map(u => (
                      <SelectItem key={u.value} value={u.value} className="text-white">{u.label}</SelectItem>
                    ))}
                    <SelectItem value="each" className="text-white">Each</SelectItem>
                    <SelectItem value="unit" className="text-white">Unit</SelectItem>
                    <SelectItem value="box" className="text-white">Box</SelectItem>
                    <SelectItem value="case" className="text-white">Case</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Default Markup %</Label>
                <Input
                  type="number"
                  value={materialForm.default_markup_percent}
                  onChange={(e) => setMaterialForm(prev => ({ ...prev, default_markup_percent: parseFloat(e.target.value) || 0 }))}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  placeholder="10"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Category</Label>
                <Select 
                  value={materialForm.category} 
                  onValueChange={(v) => setMaterialForm(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                    {/* Flooring */}
                    <SelectItem value="Concrete Coatings & Epoxy">🏗️ Concrete Coatings & Epoxy</SelectItem>
                    <SelectItem value="Polyurethane Foam Concrete Lifting">⬆️ Polyurethane Foam</SelectItem>
                    <SelectItem value="Flooring Installation">🪵 Flooring Installation</SelectItem>
                    <SelectItem value="Tile & Stone Installation">🔲 Tile & Stone</SelectItem>
                    {/* Outdoor */}
                    <SelectItem value="Landscaping & Lawn Care">🌿 Landscaping & Lawn Care</SelectItem>
                    <SelectItem value="Pool Service & Maintenance">🏊 Pool Service</SelectItem>
                    <SelectItem value="Irrigation & Sprinkler Systems">💧 Irrigation</SelectItem>
                    <SelectItem value="Fencing & Gates">🚧 Fencing & Gates</SelectItem>
                    <SelectItem value="Hardscaping & Pavers">🧱 Hardscaping & Pavers</SelectItem>
                    {/* Exterior */}
                    <SelectItem value="Roofing & Gutters">🏠 Roofing & Gutters</SelectItem>
                    <SelectItem value="Painting & Staining">🎨 Painting & Staining</SelectItem>
                    <SelectItem value="Insulation Services">🧤 Insulation Services</SelectItem>
                    <SelectItem value="General Contracting">👷 General Contracting</SelectItem>
                    <SelectItem value="Other">📦 Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Supplier</Label>
                <Input
                  value={materialForm.supplier}
                  onChange={(e) => setMaterialForm(prev => ({ ...prev, supplier: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  placeholder="e.g., Home Depot"
                />
              </div>
              <div>
                <Label className="text-zinc-300">SKU</Label>
                <Input
                  value={materialForm.sku}
                  onChange={(e) => setMaterialForm(prev => ({ ...prev, sku: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  placeholder="Optional SKU"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-300">Description</Label>
              <Textarea
                value={materialForm.description}
                onChange={(e) => setMaterialForm(prev => ({ ...prev, description: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="Optional description"
                rows={2}
              />
            </div>
            
            {/* Preview of pricing */}
            {materialForm.unit_cost > 0 && addingMaterialFor && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <p className="text-green-400 text-sm font-medium mb-1">💰 Price Preview</p>
                <p className="text-zinc-300 text-sm">
                  {addingMaterialFor.recommended_quantity} {getUnitLabel(addingMaterialFor.output_unit)} × ${materialForm.unit_cost.toFixed(2)} × {(1 + materialForm.default_markup_percent/100).toFixed(2)} = 
                  <span className="text-green-400 font-bold ml-1">
                    ${(addingMaterialFor.recommended_quantity * materialForm.unit_cost * (1 + materialForm.default_markup_percent/100)).toFixed(2)}
                  </span>
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMaterialDialog(false)} className="border-zinc-700">Cancel</Button>
            <Button 
              onClick={handleSaveMaterial} 
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              disabled={!materialForm.name || materialForm.unit_cost <= 0}
              data-testid="save-material-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Add to Catalog
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Payments Page
function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [paidInvoices, setPaidInvoices] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('payments');
  const [form, setForm] = useState({ invoice_id: "", amount: "", payment_method: "cash", payment_date: "", notes: "" });

  useEffect(() => {
    fetchPayments();
    fetchInvoices();
    fetchPaidInvoices();
  }, []);

  const fetchPayments = async () => {
    try {
      const res = await apiClient.get("/payments");
      setPayments(res.data);
    } catch (err) {
      toast.error("Failed to load payments");
    }
    setLoading(false);
  };

  const fetchInvoices = async () => {
    try {
      const res = await apiClient.get("/invoices");
      setInvoices(res.data.filter((i) => i.balance_due > 0));
    } catch (err) {}
  };

  const fetchPaidInvoices = async () => {
    try {
      const res = await apiClient.get("/invoices");
      setPaidInvoices(res.data.filter((i) => i.status === 'paid'));
    } catch (err) {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/payments", { ...form, amount: parseFloat(form.amount) });
      toast.success("Payment recorded");
      setDialogOpen(false);
      setForm({ invoice_id: "", amount: "", payment_method: "cash", payment_date: "", notes: "" });
      fetchPayments();
      fetchInvoices();
      fetchPaidInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record payment");
    }
  };

  // Group paid invoices by month
  const groupedByMonth = paidInvoices.reduce((acc, inv) => {
    const paidDate = inv.paid_at || inv.updated_at || inv.created_at;
    const date = new Date(paidDate);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    
    if (!acc[monthKey]) {
      acc[monthKey] = { label: monthLabel, invoices: [], total: 0 };
    }
    acc[monthKey].invoices.push(inv);
    acc[monthKey].total += inv.total || 0;
    return acc;
  }, {});

  // Sort months in descending order (most recent first)
  const sortedMonths = Object.keys(groupedByMonth).sort((a, b) => b.localeCompare(a));

  // Calculate totals
  const totalPayments = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalPaidInvoices = paidInvoices.reduce((sum, i) => sum + (i.total || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">PAYMENTS</h1>
          <p className="text-zinc-400 mt-1">Record and track customer payments</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-black font-heading font-bold uppercase" data-testid="add-payment-btn">
              <Plus className="w-4 h-4 mr-2" /> Record Payment
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">Record Payment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Invoice *</Label>
                <Select value={form.invoice_id} onValueChange={(v) => setForm({ ...form, invoice_id: v })} required>
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Select invoice" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {invoices.map((i) => (
                      <SelectItem key={i.id} value={i.id}>
                        {i.invoice_number} - {i.customer_name} (${i.balance_due.toFixed(2)} due)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Amount *</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required className="mt-1 bg-zinc-800 border-zinc-700" placeholder="$" />
                </div>
                <div>
                  <Label>Payment Method</Label>
                  <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                      <SelectItem value="credit_card">Credit Card</SelectItem>
                      <SelectItem value="ach">ACH</SelectItem>
                      <SelectItem value="venmo">Venmo</SelectItem>
                      <SelectItem value="zelle">Zelle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Payment Date</Label>
                <Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" rows={2} />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-primary text-black font-heading font-bold">Record Payment</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">Total Payments</p>
                <p className="text-2xl font-bold text-green-400">${totalPayments.toLocaleString()}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-400/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">Paid Invoices</p>
                <p className="text-2xl font-bold text-primary">{paidInvoices.length}</p>
              </div>
              <FileCheck className="w-8 h-8 text-primary/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">This Month</p>
                <p className="text-2xl font-bold text-cyan-400">
                  ${(groupedByMonth[sortedMonths[0]]?.total || 0).toLocaleString()}
                </p>
              </div>
              <CalendarDays className="w-8 h-8 text-cyan-400/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-700 pb-2">
        <Button 
          variant={activeTab === 'payments' ? 'default' : 'ghost'} 
          onClick={() => setActiveTab('payments')}
          className={activeTab === 'payments' ? 'bg-primary text-black' : 'text-zinc-400'}
        >
          <CreditCard className="w-4 h-4 mr-2" /> Payments ({payments.length})
        </Button>
        <Button 
          variant={activeTab === 'paid-invoices' ? 'default' : 'ghost'} 
          onClick={() => setActiveTab('paid-invoices')}
          className={activeTab === 'paid-invoices' ? 'bg-green-600 text-white' : 'text-zinc-400'}
        >
          <FileCheck className="w-4 h-4 mr-2" /> Paid Invoices ({paidInvoices.length})
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" /></div>
      ) : activeTab === 'payments' ? (
        /* Payments Tab */
        payments.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-700">
            <CardContent className="py-12 text-center">
              <CreditCard className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
              <p className="text-zinc-400">No payments recorded</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-zinc-900 border-zinc-700 overflow-hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Invoice</th>
                  <th>Customer</th>
                  <th>Method</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((pmt) => (
                  <tr key={pmt.id}>
                    <td>{new Date(pmt.payment_date).toLocaleDateString()}</td>
                    <td className="font-medium">{pmt.invoice_number}</td>
                    <td>{pmt.customer_name}</td>
                    <td className="capitalize">{pmt.payment_method.replace("_", " ")}</td>
                    <td className="tabular-nums font-bold text-green-400">${pmt.amount.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )
      ) : (
        /* Paid Invoices Tab - Grouped by Month */
        paidInvoices.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-700">
            <CardContent className="py-12 text-center">
              <FileCheck className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
              <p className="text-zinc-400">No paid invoices yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {sortedMonths.map((monthKey) => {
              const monthData = groupedByMonth[monthKey];
              return (
                <div key={monthKey}>
                  {/* Month Header */}
                  <div className="flex items-center justify-between mb-3 pb-2 border-b border-zinc-700">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="w-5 h-5 text-primary" />
                      <h3 className="font-heading text-lg font-bold uppercase">{monthData.label}</h3>
                      <Badge className="bg-zinc-800 text-zinc-300">{monthData.invoices.length} invoices</Badge>
                    </div>
                    <p className="text-xl font-bold text-green-400">${monthData.total.toLocaleString()}</p>
                  </div>
                  
                  {/* Month's Invoices */}
                  <div className="space-y-2">
                    {monthData.invoices.map((inv) => (
                      <Card key={inv.id} className="bg-zinc-800/50 border-zinc-700/50 hover:bg-zinc-800 transition-colors">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <Badge className="bg-green-900/50 text-green-400 border-green-700">
                                  <CheckCircle className="w-3 h-3 mr-1" /> PAID
                                </Badge>
                                <span className="font-medium text-white">{inv.invoice_number}</span>
                                <span className="text-zinc-400">•</span>
                                <span className="text-zinc-300">{inv.customer_name}</span>
                              </div>
                              <div className="mt-1 text-sm text-zinc-500">
                                {inv.description && <span>{inv.description} • </span>}
                                Paid on: {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString('en-US', { 
                                  weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' 
                                }) : 'N/A'}
                                {inv.payment_method && <span className="ml-2 capitalize">via {inv.payment_method.replace('_', ' ')}</span>}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-green-400">${(inv.total || 0).toLocaleString()}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

// Deposits Page
function DepositsPage() {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeposits();
  }, []);

  const fetchDeposits = async () => {
    try {
      const res = await apiClient.get("/deposits");
      setDeposits(res.data);
    } catch (err) {
      toast.error("Failed to load deposits");
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="deposits-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">DEPOSITS</h1>
          <p className="text-zinc-400 mt-1">Track bank deposits and payment batches</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" /></div>
      ) : deposits.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-700">
          <CardContent className="py-12 text-center">
            <Wallet className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
            <p className="text-zinc-400">No deposits yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {deposits.map((dep) => (
            <Card key={dep.id} className="bg-zinc-900 border-zinc-700 card-hover">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{new Date(dep.deposit_date).toLocaleDateString()}</p>
                    <p className="text-sm text-zinc-400">{dep.payment_ids.length} payments</p>
                  </div>
                  <p className="text-2xl font-heading font-bold tabular-nums text-green-400">${dep.total_amount.toLocaleString()}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Contracts Page — implementation moved to /pages/ContractsPage.jsx (Feb 2026)
// See top of file for the import.

// Employees Page (Admin Only)
function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", hourly_rate: 25, labor_burden_percent: 35, skills: "", address: "", city: "", state: "", zip_code: "" });
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", hourly_rate: 25, labor_burden_percent: 35, burdened_rate: null, skills: "", notes: "", address: "", city: "", state: "", zip_code: "" });
  const [taskForm, setTaskForm] = useState({ 
    employee_id: "", 
    title: "", 
    description: "", 
    due_date: "", 
    priority: "medium",
    send_email: true,
    job_id: ""
  });

  // Calculate burdened rate
  const calcBurdenedRate = (baseRate, burdenPct) => {
    return Math.round(baseRate * (1 + (burdenPct || 35) / 100) * 100) / 100;
  };

  useEffect(() => { 
    fetchEmployees(); 
    fetchJobs();
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await apiClient.get("/employees");
      setEmployees(res.data);
    } catch (err) {
      toast.error("Failed to load employees");
    }
    setLoading(false);
  };

  const fetchJobs = async () => {
    try {
      const res = await apiClient.get("/jobs");
      setJobs(res.data.filter(j => j.status !== 'completed' && j.status !== 'cancelled'));
    } catch (err) {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/employees", form);
      toast.success("Employee created! They can now login.");
      setDialogOpen(false);
      setForm({ name: "", email: "", password: "", phone: "", hourly_rate: 25, skills: "", address: "", city: "", state: "", zip_code: "" });
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create employee");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await apiClient.put(`/employees/${selectedEmployee.id}`, editForm);
      toast.success("Employee updated!");
      setEditMode(false);
      setDetailDialogOpen(false);
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update employee");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this employee?")) return;
    try {
      await apiClient.delete(`/employees/${id}`);
      toast.success("Employee deleted");
      setDetailDialogOpen(false);
      fetchEmployees();
    } catch (err) {
      toast.error("Failed to delete employee");
    }
  };

  const toggleActive = async (id) => {
    try {
      const res = await apiClient.put(`/employees/${id}/toggle-active`);
      toast.success(res.data.is_active ? "Employee activated" : "Employee deactivated");
      fetchEmployees();
    } catch (err) {
      toast.error("Failed to update employee");
    }
  };

  const openEmployeeDetail = (emp) => {
    setSelectedEmployee(emp);
    setEditForm({
      name: emp.name || "",
      email: emp.email || "",
      phone: emp.phone || "",
      hourly_rate: emp.hourly_rate || 25,
      labor_burden_percent: emp.labor_burden_percent || 35,
      burdened_rate: emp.burdened_rate || null,
      skills: emp.skills || "",
      notes: emp.notes || "",
      address: emp.address || "",
      city: emp.city || "",
      state: emp.state || "",
      zip_code: emp.zip_code || ""
    });
    setEditMode(false);
    setDetailDialogOpen(true);
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const taskData = {
        ...taskForm,
        job_id: taskForm.job_id === 'none' ? null : taskForm.job_id
      };
      await apiClient.post("/employee-tasks", taskData);
      toast.success(taskForm.send_email ? "Task created and email sent!" : "Task created!");
      setTaskDialogOpen(false);
      setTaskForm({ employee_id: "", title: "", description: "", due_date: "", priority: "medium", send_email: true, job_id: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create task");
    }
  };

  const openTaskDialog = (emp) => {
    setTaskForm({ ...taskForm, employee_id: emp.id });
    setTaskDialogOpen(true);
  };

  const getAssignedJobs = (empId) => {
    return jobs.filter(j => j.assigned_employees?.includes(empId));
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employees-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">EMPLOYEES</h1>
          <p className="text-zinc-400 mt-1">Manage crew members, assignments, and tasks</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-zinc-600 font-heading font-bold uppercase">
                <Send className="w-4 h-4 mr-2" /> Assign Task
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-700 max-w-[95vw] sm:max-w-lg max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase text-white">Create Task & Notify</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateTask} className="space-y-4">
                <div>
                  <Label className="text-zinc-200">Assign To *</Label>
                  <Select value={taskForm.employee_id} onValueChange={(v) => setTaskForm({ ...taskForm, employee_id: v })} required>
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {employees.filter(e => e.is_active).map((e) => (
                        <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-200">Related Job (optional)</Label>
                  <Select value={taskForm.job_id} onValueChange={(v) => setTaskForm({ ...taskForm, job_id: v })}>
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                      <SelectValue placeholder="Select job" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="none">No specific job</SelectItem>
                      {jobs.map((j) => (
                        <SelectItem key={j.id} value={j.id}>{j.title} - {j.customer_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-200">Task Title *</Label>
                  <Input value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} required className="mt-1" placeholder="e.g., Pick up materials" />
                </div>
                <div>
                  <Label className="text-zinc-200">Description</Label>
                  <Textarea value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} className="mt-1" rows={3} placeholder="Details about the task..." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-200">Due Date</Label>
                    <Input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-zinc-200">Priority</Label>
                    <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                      <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-zinc-800 rounded-lg">
                  <input 
                    type="checkbox" 
                    id="send_email" 
                    checked={taskForm.send_email} 
                    onChange={(e) => setTaskForm({ ...taskForm, send_email: e.target.checked })}
                    className="w-4 h-4 accent-primary"
                  />
                  <label htmlFor="send_email" className="text-zinc-200 cursor-pointer">
                    <Mail className="w-4 h-4 inline mr-2 text-primary" />
                    Send email notification to employee
                  </label>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-lg text-sm text-blue-300">
                  <strong>Coming Soon:</strong> SMS notifications via text message
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setTaskDialogOpen(false)} className="border-zinc-600">Cancel</Button>
                  <Button type="submit" className="bg-primary text-black font-heading font-bold">
                    {taskForm.send_email ? <><Send className="w-4 h-4 mr-2" /> Create & Send</> : "Create Task"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-black font-heading font-bold uppercase" data-testid="add-employee-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Employee
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-700">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase text-white">Add New Employee</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-200">Name *</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" placeholder="John Doe" data-testid="employee-name" />
                  </div>
                  <div>
                    <Label className="text-zinc-200">Phone</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" placeholder="(555) 123-4567" />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-200">Email *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1" placeholder="john@company.com" data-testid="employee-email" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-200">Password *</Label>
                    <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="mt-1" placeholder="Temporary password" data-testid="employee-password" />
                  </div>
                  <div>
                    <Label className="text-zinc-200">Hourly Rate ($)</Label>
                    <Input type="number" min="0" step="0.01" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: parseFloat(e.target.value) || 0 })} className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-200">Skills/Certifications</Label>
                  <Input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} className="mt-1" placeholder="e.g., CDL, Pesticide License, Equipment Operator" />
                </div>
                <div className="border-t border-zinc-700 pt-4">
                  <Label className="text-zinc-200 font-medium flex items-center gap-2 mb-3">
                    <Building2 className="w-4 h-4 text-primary" /> Address Information
                  </Label>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-zinc-400 text-sm">Street Address</Label>
                      <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" placeholder="123 Main Street" data-testid="employee-address" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-zinc-400 text-sm">City</Label>
                        <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1" placeholder="Springfield" data-testid="employee-city" />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">State</Label>
                        <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="mt-1" placeholder="IL" data-testid="employee-state" />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">ZIP Code</Label>
                        <Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className="mt-1" placeholder="62701" data-testid="employee-zip" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-zinc-800/50 p-3 rounded-sm text-sm">
                  <p className="font-medium text-primary mb-1">Employee Access</p>
                  <p className="text-zinc-400">Employees can view: Assigned Jobs, Tasks, Time Entry. They can log time and materials for their assigned jobs.</p>
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-primary text-black font-heading font-bold" data-testid="save-employee-btn">Create Employee</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/20 rounded-lg">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-zinc-400 uppercase">Total</p>
                <p className="text-2xl font-heading font-bold text-white">{employees.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-400 uppercase">Active</p>
                <p className="text-2xl font-heading font-bold text-green-400">{employees.filter(e => e.is_active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Briefcase className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-400 uppercase">On Jobs</p>
                <p className="text-2xl font-heading font-bold text-blue-400">{employees.filter(e => getAssignedJobs(e.id).length > 0).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-400 uppercase">Avg Rate</p>
                <p className="text-2xl font-heading font-bold text-orange-400">
                  ${employees.length > 0 ? (employees.reduce((s, e) => s + (e.hourly_rate || 25), 0) / employees.length).toFixed(0) : 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" /></div>
      ) : employees.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-700">
          <CardContent className="py-12 text-center">
            <HardHat className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
            <p className="text-zinc-400">No employees yet. Add your first team member!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {employees.map((emp) => (
            <Card 
              key={emp.id} 
              className={`bg-zinc-900 border-zinc-700 card-hover cursor-pointer ${!emp.is_active ? 'opacity-50' : ''}`}
              onClick={() => openEmployeeDetail(emp)}
              data-testid={`employee-card-${emp.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-lg font-bold text-primary">{emp.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="font-medium text-white">{emp.name}</p>
                      <p className="text-sm text-zinc-400">{emp.email}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-700">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEmployeeDetail(emp); }}>
                        <Edit className="w-4 h-4 mr-2" /> View / Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openTaskDialog(emp); }}>
                        <Send className="w-4 h-4 mr-2" /> Assign Task
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); toggleActive(emp.id); }}>
                        {emp.is_active ? "Deactivate" : "Activate"}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDelete(emp.id); }} className="text-red-400">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={emp.is_active ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-zinc-500/20 text-zinc-400 border-zinc-500/30"}>
                      {emp.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  {emp.phone && (
                    <p className="text-sm text-zinc-400 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {emp.phone}
                    </p>
                  )}
                  {getAssignedJobs(emp.id).length > 0 && (
                    <p className="text-sm text-blue-400">
                      <Briefcase className="w-3 h-3 inline mr-1" />
                      {getAssignedJobs(emp.id).length} active job{getAssignedJobs(emp.id).length > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Employee Detail/Edit Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-full sm:max-w-xl md:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-white flex items-center gap-2">
              <HardHat className="w-5 h-5 text-primary" />
              {editMode ? "Edit Employee" : selectedEmployee?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedEmployee && (
            <>
              {editMode ? (
                <form onSubmit={handleUpdate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-200">Name *</Label>
                      <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-zinc-200">Phone</Label>
                      <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="mt-1" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-200">Email *</Label>
                      <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} required className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-zinc-200">Base Wage ($/hr)</Label>
                      <Input type="number" min="0" step="0.5" value={editForm.hourly_rate} onChange={(e) => setEditForm({ ...editForm, hourly_rate: parseFloat(e.target.value) || 0 })} className="mt-1" />
                    </div>
                  </div>
                  
                  {/* Burdened Rate Section */}
                  <div className="bg-zinc-800/50 p-3 rounded-lg border border-zinc-700">
                    <Label className="text-zinc-200 text-sm font-medium mb-2 block">Labor Burden & True Cost</Label>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-zinc-400 text-xs">Labor Burden %</Label>
                        <Input type="number" min="0" max="100" step="1" value={editForm.labor_burden_percent || 35} onChange={(e) => setEditForm({ ...editForm, labor_burden_percent: parseFloat(e.target.value) || 35 })} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Calculated Rate</Label>
                        <div className="mt-1 bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-zinc-400">
                          ${calcBurdenedRate(editForm.hourly_rate, editForm.labor_burden_percent).toFixed(2)}
                        </div>
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Override Rate</Label>
                        <Input type="number" min="0" step="0.5" value={editForm.burdened_rate || ''} onChange={(e) => setEditForm({ ...editForm, burdened_rate: e.target.value ? parseFloat(e.target.value) : null })} className="mt-1" placeholder="Auto" />
                      </div>
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">
                      True Cost: <span className="text-green-400 font-medium">${(editForm.burdened_rate || calcBurdenedRate(editForm.hourly_rate, editForm.labor_burden_percent)).toFixed(2)}/hr</span> (used in estimates)
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-200">Skills/Certifications</Label>
                    <Input value={editForm.skills} onChange={(e) => setEditForm({ ...editForm, skills: e.target.value })} className="mt-1" placeholder="e.g., CDL, Pesticide License" />
                  </div>
                  <div className="border-t border-zinc-700 pt-4">
                    <Label className="text-zinc-200 font-medium flex items-center gap-2 mb-3">
                      <Building2 className="w-4 h-4 text-primary" /> Address Information
                    </Label>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-zinc-400 text-sm">Street Address</Label>
                        <Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="mt-1" placeholder="123 Main Street" />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-zinc-400 text-sm">City</Label>
                          <Input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} className="mt-1" placeholder="Springfield" />
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-sm">State</Label>
                          <Input value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} className="mt-1" placeholder="IL" />
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-sm">ZIP Code</Label>
                          <Input value={editForm.zip_code} onChange={(e) => setEditForm({ ...editForm, zip_code: e.target.value })} className="mt-1" placeholder="62701" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-zinc-200">Notes</Label>
                    <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="mt-1" rows={2} placeholder="Internal notes about this employee..." />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setEditMode(false)} className="border-zinc-600">Cancel</Button>
                    <Button type="submit" className="bg-primary text-black font-heading font-bold">Save Changes</Button>
                  </DialogFooter>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-2xl font-bold text-primary">{selectedEmployee.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="text-xl font-medium text-white">{selectedEmployee.name}</p>
                      <p className="text-zinc-400">{selectedEmployee.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={selectedEmployee.is_active ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}>
                          {selectedEmployee.is_active ? "Active" : "Inactive"}
                        </Badge>
                        <Badge className="bg-zinc-700 text-zinc-300">Employee</Badge>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-zinc-800 p-3 rounded-lg">
                      <p className="text-xs text-zinc-400 uppercase">Phone</p>
                      <p className="text-white font-medium">{selectedEmployee.phone || "Not set"}</p>
                    </div>
                    <div className="bg-zinc-800 p-3 rounded-lg">
                      <p className="text-xs text-zinc-400 uppercase">Active Jobs</p>
                      <p className="text-blue-400 font-medium">{getAssignedJobs(selectedEmployee.id).length}</p>
                    </div>
                    <div className="bg-zinc-800 p-3 rounded-lg">
                      <p className="text-xs text-zinc-400 uppercase">Since</p>
                      <p className="text-white font-medium">{selectedEmployee.created_at ? new Date(selectedEmployee.created_at).toLocaleDateString() : "N/A"}</p>
                    </div>
                  </div>

                  {selectedEmployee.skills && (
                    <div>
                      <p className="text-xs text-zinc-400 uppercase mb-2">Skills & Certifications</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedEmployee.skills.split(',').map((skill, i) => (
                          <Badge key={i} className="bg-zinc-800 text-zinc-300">{skill.trim()}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {(selectedEmployee.address || selectedEmployee.city || selectedEmployee.state || selectedEmployee.zip_code) && (
                    <div>
                      <p className="text-xs text-zinc-400 uppercase mb-2 flex items-center gap-1">
                        <Building2 className="w-3 h-3" /> Address
                      </p>
                      <div className="bg-zinc-800 p-3 rounded-lg">
                        {selectedEmployee.address && <p className="text-white">{selectedEmployee.address}</p>}
                        <p className="text-zinc-300">
                          {[selectedEmployee.city, selectedEmployee.state, selectedEmployee.zip_code].filter(Boolean).join(', ')}
                        </p>
                      </div>
                    </div>
                  )}

                  {getAssignedJobs(selectedEmployee.id).length > 0 && (
                    <div>
                      <p className="text-xs text-zinc-400 uppercase mb-2">Assigned Jobs</p>
                      <div className="space-y-2">
                        {getAssignedJobs(selectedEmployee.id).map(job => (
                          <div key={job.id} className="bg-zinc-800 p-3 rounded-lg flex items-center justify-between">
                            <div>
                              <p className="text-white font-medium">{job.title}</p>
                              <p className="text-sm text-zinc-400">{job.customer_name}</p>
                            </div>
                            <Badge className="bg-primary/20 text-primary border-primary/30">{job.status}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedEmployee.notes && (
                    <div>
                      <p className="text-xs text-zinc-400 uppercase mb-1">Notes</p>
                      <p className="text-zinc-300">{selectedEmployee.notes}</p>
                    </div>
                  )}

                  {/* Hidden Pay Info - Click to reveal */}
                  <details className="group">
                    <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-400 flex items-center gap-1 select-none">
                      <Shield className="w-3 h-3" /> 
                      <span>Pay Information (Admin Only)</span>
                    </summary>
                    <div className="mt-2 bg-zinc-800/50 border border-zinc-700 p-3 rounded-lg">
                      <p className="text-xs text-zinc-500 uppercase mb-1">Hourly Rate</p>
                      <p className="text-green-400 font-medium">${selectedEmployee.hourly_rate || 25}/hr</p>
                    </div>
                  </details>

                  <div className="flex gap-2 pt-4 border-t border-zinc-700">
                    <Button onClick={() => setEditMode(true)} className="bg-primary text-black font-bold">
                      <Edit className="w-4 h-4 mr-2" /> Edit
                    </Button>
                    <Button variant="outline" onClick={() => openTaskDialog(selectedEmployee)} className="border-zinc-600">
                      <Send className="w-4 h-4 mr-2" /> Assign Task
                    </Button>
                    <Button variant="outline" onClick={() => toggleActive(selectedEmployee.id)} className="border-zinc-600">
                      {selectedEmployee.is_active ? "Deactivate" : "Activate"}
                    </Button>
                    <Button variant="ghost" onClick={() => handleDelete(selectedEmployee.id)} className="text-red-400 hover:bg-red-500/20">
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Admin Employee Tasks Management Page
function AdminEmployeeTasksPage() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewTaskDialog, setViewTaskDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ 
    employee_id: "", 
    title: "", 
    description: "",
    due_date: "",
    priority: "medium",
    job_id: "",
    customer_id: "",
    send_email: true
  });
  const [activeTab, setActiveTab] = useState('all');
  // Multi-select state — Set of task ids checked in the current view
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => { 
    fetchEmployees();
    fetchTasks();
    fetchJobs();
    fetchCustomers();
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await apiClient.get("/employees");
      setEmployees(res.data.filter(e => e.is_active));
    } catch (err) {
      console.error("Failed to load employees");
    }
  };

  const fetchTasks = async () => {
    try {
      const res = await apiClient.get("/employee-tasks");
      setTasks(res.data);
    } catch (err) {
      console.error("Failed to load tasks");
    }
    setLoading(false);
  };

  const fetchJobs = async () => {
    try {
      const res = await apiClient.get("/jobs");
      setJobs(res.data.filter(j => j.status !== 'completed' && j.status !== 'cancelled'));
    } catch (err) {
      console.error("Failed to load jobs");
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await apiClient.get("/customers");
      setCustomers(res.data);
    } catch (err) {
      console.error("Failed to load customers");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/employee-tasks", form);
      toast.success("Task assigned! " + (form.send_email ? "Email notification sent." : ""));
      setDialogOpen(false);
      setForm({ employee_id: "", title: "", description: "", due_date: "", priority: "medium", job_id: "", customer_id: "", send_email: true });
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create task");
    }
  };

  const handleJobSelect = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    if (job && job.customer_id) {
      setForm({ ...form, job_id: jobId, customer_id: job.customer_id });
    } else {
      setForm({ ...form, job_id: jobId });
    }
  };

  const handleComplete = async (taskId) => {
    try {
      await apiClient.put(`/employee-tasks/${taskId}/complete`);
      toast.success("Task marked as completed");
      fetchTasks();
    } catch (err) {
      toast.error("Failed to complete task");
    }
  };

  const handleReopen = async (taskId) => {
    try {
      await apiClient.put(`/employee-tasks/${taskId}/reopen`);
      toast.success("Task reopened");
      fetchTasks();
    } catch (err) {
      toast.error("Failed to reopen task");
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await apiClient.delete(`/employee-tasks/${taskId}`);
      toast.success("Task deleted");
      fetchTasks();
    } catch (err) {
      toast.error("Failed to delete task");
    }
  };

  // ── Multi-select helpers ──────────────────────────────────────────
  const toggleSelect = (taskId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  // Reset selection whenever the filter tab changes so we never act on
  // items the admin can't currently see.
  useEffect(() => { clearSelection(); }, [activeTab]);

  const handleBulkComplete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const res = await apiClient.post('/employee-tasks/bulk-complete', { task_ids: ids });
      toast.success(`${res.data.completed} task${res.data.completed === 1 ? '' : 's'} marked complete`);
      clearSelection();
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk complete failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} task${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const res = await apiClient.post('/employee-tasks/bulk-delete', { task_ids: ids });
      toast.success(`${res.data.deleted} task${res.data.deleted === 1 ? '' : 's'} deleted`);
      clearSelection();
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleViewTask = (task) => {
    setSelectedTask(task);
    setViewTaskDialog(true);
  };

  const handleUploadAttachment = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await apiClient.post(`/employee-tasks/${selectedTask.id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Attachment uploaded');
      fetchTasks();
      // Refresh selected task
      const updatedTasks = await apiClient.get("/employee-tasks");
      const updatedTask = updatedTasks.data.find(t => t.id === selectedTask.id);
      if (updatedTask) setSelectedTask(updatedTask);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload attachment');
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await apiClient.delete(`/employee-tasks/${selectedTask.id}/attachments/${attachmentId}`);
      toast.success('Attachment deleted');
      fetchTasks();
      // Refresh selected task
      const updatedTasks = await apiClient.get("/employee-tasks");
      const updatedTask = updatedTasks.data.find(t => t.id === selectedTask.id);
      if (updatedTask) setSelectedTask(updatedTask);
    } catch (err) {
      toast.error('Failed to delete attachment');
    }
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp?.name || "Unknown";
  };

  const priorityColors = {
    low: "bg-zinc-500/20 text-zinc-400 border-zinc-500",
    medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500",
    high: "bg-orange-500/20 text-orange-400 border-orange-500",
    urgent: "bg-red-500/20 text-red-400 border-red-500"
  };

  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-400",
    completed: "bg-green-500/20 text-green-400",
    needs_return: "bg-orange-500/20 text-orange-400"
  };

  const filteredTasks = activeTab === 'all' 
    ? tasks 
    : activeTab === 'pending' 
      ? tasks.filter(t => t.status === 'pending')
      : activeTab === 'needs_return'
        ? tasks.filter(t => t.status === 'needs_return')
        : tasks.filter(t => t.status === 'completed');

  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    needs_return: tasks.filter(t => t.status === 'needs_return').length,
    overdue: tasks.filter(t => t.status === 'pending' && t.due_date && new Date(t.due_date) < new Date()).length
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-yellow-500 border-t-transparent rounded-full"></div></div>;

  return (
    <div className="space-y-6" data-testid="admin-employee-tasks-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Employee Tasks</h1>
          <p className="text-zinc-400">Assign and manage tasks for your team</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-yellow-500 hover:bg-yellow-600 text-black" data-testid="assign-task-btn">
              <Plus className="h-4 w-4 mr-2" /> Assign Task
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-700">
            <DialogHeader>
              <DialogTitle className="text-white">Assign Task to Employee</DialogTitle>
              <DialogDescription className="text-zinc-400">Employee will receive an email notification</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-zinc-200">Employee *</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({...form, employee_id: v})}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id} className="text-white">{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-200">Task Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({...form, title: e.target.value})}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="e.g., Complete safety training"
                  required
                />
              </div>
              <div>
                <Label className="text-zinc-200">Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="Task details..."
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-200">Due Date</Label>
                  <Input
                    type="date"
                    value={form.due_date}
                    onChange={(e) => setForm({...form, due_date: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-200">Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="low" className="text-white">Low</SelectItem>
                      <SelectItem value="medium" className="text-white">Medium</SelectItem>
                      <SelectItem value="high" className="text-white">High</SelectItem>
                      <SelectItem value="urgent" className="text-white">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-zinc-200">Related Job (Optional)</Label>
                <Select value={form.job_id || "none"} onValueChange={(v) => handleJobSelect(v === "none" ? "" : v)}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Select job (optional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="none" className="text-white">No job selected</SelectItem>
                    {jobs.map(job => (
                      <SelectItem key={job.id} value={job.id} className="text-white">
                        {job.title} {job.customer_name && `(${job.customer_name})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-200">Customer (Optional)</Label>
                <Select value={form.customer_id || "none"} onValueChange={(v) => setForm({...form, customer_id: v === "none" ? "" : v})}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Select customer (optional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="none" className="text-white">No customer selected</SelectItem>
                    {customers.map(cust => (
                      <SelectItem key={cust.id} value={cust.id} className="text-white">{cust.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="send_email"
                  checked={form.send_email}
                  onChange={(e) => setForm({...form, send_email: e.target.checked})}
                  className="rounded border-zinc-600"
                />
                <Label htmlFor="send_email" className="text-zinc-300 cursor-pointer">
                  Send email notification to employee
                </Label>
              </div>
              <Button type="submit" className="w-full bg-yellow-500 hover:bg-yellow-600 text-black">
                Assign Task
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <CheckCircle className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Total Tasks</p>
                <p className="text-xl font-bold text-white">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Pending</p>
                <p className="text-xl font-bold text-white">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <RotateCcw className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Needs Return</p>
                <p className="text-xl font-bold text-white">{stats.needs_return}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Completed</p>
                <p className="text-xl font-bold text-white">{stats.completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Overdue</p>
                <p className="text-xl font-bold text-white">{stats.overdue}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {['all', 'pending', 'needs_return', 'completed'].map(tab => (
          <Button
            key={tab}
            variant={activeTab === tab ? "default" : "outline"}
            onClick={() => setActiveTab(tab)}
            className={activeTab === tab ? "bg-yellow-500 text-black" : "border-zinc-700 text-zinc-400"}
          >
            {tab === 'needs_return' ? 'Needs Return' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </Button>
        ))}
      </div>

      {/* Bulk-action bar — appears once any row is selected */}
      {selectedIds.size > 0 && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-yellow-500/50 bg-yellow-500/10 px-4 py-3"
          data-testid="tasks-bulk-bar"
        >
          <div className="flex items-center gap-3 text-sm text-yellow-100">
            <CheckCircle className="w-4 h-4 text-yellow-400" />
            <span className="font-medium">{selectedIds.size} selected</span>
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs text-yellow-300 hover:text-white underline underline-offset-2"
              data-testid="tasks-bulk-clear"
            >
              clear
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleBulkComplete}
              disabled={bulkBusy}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="tasks-bulk-complete"
            >
              <CheckCircle className="w-4 h-4 mr-1.5" /> Mark Complete
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleBulkDelete}
              disabled={bulkBusy}
              className="border-red-500/50 text-red-300 hover:bg-red-500/10 hover:text-red-200"
              data-testid="tasks-bulk-delete"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete
            </Button>
          </div>
        </div>
      )}

      {/* Tasks List */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-0">
          {filteredTasks.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No tasks found</p>
              <p className="text-zinc-500 text-sm">Assign tasks to your team members!</p>
            </div>
          ) : (
            <>
              {/* Select-all row */}
              <div className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-800 bg-zinc-950/50">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-yellow-500 cursor-pointer accent-yellow-500"
                  checked={filteredTasks.length > 0 && filteredTasks.every(t => selectedIds.has(t.id))}
                  ref={(el) => {
                    if (!el) return;
                    const someSelected = filteredTasks.some(t => selectedIds.has(t.id));
                    const allSelected = filteredTasks.every(t => selectedIds.has(t.id));
                    el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedIds(new Set(filteredTasks.map(t => t.id)));
                    else clearSelection();
                  }}
                  data-testid="tasks-select-all"
                  aria-label="Select all tasks in view"
                />
                <span className="text-xs uppercase tracking-wider text-zinc-500">
                  {selectedIds.size > 0 ? `${selectedIds.size} of ${filteredTasks.length} selected` : `${filteredTasks.length} task${filteredTasks.length === 1 ? '' : 's'}`}
                </span>
              </div>
              <div className="divide-y divide-zinc-800">
              {filteredTasks.map(task => {
                const isOverdue = task.status === 'pending' && task.due_date && new Date(task.due_date) < new Date();
                const isSelected = selectedIds.has(task.id);
                return (
                  <div key={task.id} className={`p-4 hover:bg-zinc-800/50 ${task.status === 'completed' ? 'opacity-60' : ''} ${isSelected ? 'bg-yellow-500/5' : ''}`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1.5 h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-yellow-500 cursor-pointer accent-yellow-500 flex-shrink-0"
                        checked={isSelected}
                        onChange={() => toggleSelect(task.id)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`task-select-${task.id}`}
                        aria-label={`Select task ${task.title}`}
                      />
                      <div className="flex items-start justify-between flex-1 min-w-0">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`font-medium text-white ${task.status === 'completed' ? 'line-through' : ''}`}>
                            {task.title}
                          </p>
                          <span className={`px-2 py-0.5 rounded text-xs border ${priorityColors[task.priority]}`}>
                            {task.priority}
                          </span>
                          {isOverdue && (
                            <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400 border border-red-500">
                              Overdue
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded text-xs ${statusColors[task.status] || statusColors.pending}`}>
                            {task.status === 'completed' ? '✓ Completed' : task.status === 'needs_return' ? '↩ Needs Return' : '◷ Pending'}
                          </span>
                        </div>
                        <p className="text-sm text-zinc-400 mt-1">
                          Assigned to: <span className="text-white">{task.employee_name || getEmployeeName(task.employee_id)}</span>
                          {task.customer_name && <span> • Customer: <span className="text-blue-400">{task.customer_name}</span></span>}
                          {task.job_title && <span> • Job: <span className="text-primary">{task.job_title}</span></span>}
                        </p>
                        {task.description && (
                          <p className="text-sm text-zinc-500 mt-1 line-clamp-2">{task.description}</p>
                        )}
                        {task.completion_notes && (
                          <p className="text-sm text-blue-300 mt-1 bg-blue-500/10 p-2 rounded">
                            <strong>Notes:</strong> {task.completion_notes}
                          </p>
                        )}
                        {task.attachments && task.attachments.length > 0 && (
                          <div className="flex items-center gap-1 mt-1">
                            <Image className="w-4 h-4 text-zinc-400" />
                            <span className="text-xs text-zinc-400">{task.attachments.length} attachment(s)</span>
                          </div>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                          {task.due_date && (
                            <span className={isOverdue ? 'text-red-400' : ''}>
                              Due: {new Date(task.due_date).toLocaleDateString()}
                            </span>
                          )}
                          <span>Created: {new Date(task.created_at).toLocaleDateString()}</span>
                          {task.completed_at && (
                            <span className="text-green-400">
                              {task.status === 'needs_return' ? 'Marked: ' : 'Completed: '}{new Date(task.completed_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewTask(task)}
                          className="border-zinc-600 text-zinc-400"
                        >
                          <Eye className="h-4 w-4 mr-1" /> View
                        </Button>
                        {task.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => handleComplete(task.id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" /> Complete
                          </Button>
                        )}
                        {(task.status === 'completed' || task.status === 'needs_return') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReopen(task.id)}
                            className="border-zinc-600 text-zinc-400"
                          >
                            Reopen
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(task.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* View Task Dialog */}
      <Dialog open={viewTaskDialog} onOpenChange={setViewTaskDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-full sm:max-w-xl md:max-w-full sm:max-w-xl md:max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedTask && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl text-white flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  {selectedTask.title}
                </DialogTitle>
                <DialogDescription className="text-zinc-400">
                  Task Details & Attachments
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Task Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-zinc-400">Status</p>
                    <span className={`px-2 py-1 rounded text-sm ${statusColors[selectedTask.status] || statusColors.pending}`}>
                      {selectedTask.status === 'completed' ? 'Completed' : selectedTask.status === 'needs_return' ? 'Needs Return' : 'Pending'}
                    </span>
                  </div>
                  <div>
                    <p className="text-zinc-400">Priority</p>
                    <span className={`px-2 py-1 rounded text-sm border ${priorityColors[selectedTask.priority]}`}>
                      {selectedTask.priority}
                    </span>
                  </div>
                  <div>
                    <p className="text-zinc-400">Assigned To</p>
                    <p className="text-white">{selectedTask.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400">Due Date</p>
                    <p className="text-white">{selectedTask.due_date ? new Date(selectedTask.due_date).toLocaleDateString() : 'No due date'}</p>
                  </div>
                  {selectedTask.customer_name && (
                    <div>
                      <p className="text-zinc-400">Customer</p>
                      <p className="text-blue-400">{selectedTask.customer_name}</p>
                    </div>
                  )}
                  {selectedTask.job_title && (
                    <div>
                      <p className="text-zinc-400">Job</p>
                      <p className="text-primary">{selectedTask.job_title}</p>
                    </div>
                  )}
                </div>
                
                {selectedTask.description && (
                  <div>
                    <p className="text-zinc-400 text-sm">Description</p>
                    <p className="text-white bg-zinc-800 p-3 rounded mt-1">{selectedTask.description}</p>
                  </div>
                )}
                
                {selectedTask.completion_notes && (
                  <div>
                    <p className="text-zinc-400 text-sm">Completion Notes</p>
                    <p className="text-blue-300 bg-blue-500/10 p-3 rounded mt-1">{selectedTask.completion_notes}</p>
                  </div>
                )}
                
                {/* Attachments Section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-zinc-400 text-sm">Attachments ({selectedTask.attachments?.length || 0})</p>
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleUploadAttachment}
                        className="hidden"
                        id="task-attachment-upload"
                        disabled={uploading}
                      />
                      <label htmlFor="task-attachment-upload">
                        <Button 
                          as="span" 
                          size="sm" 
                          variant="outline" 
                          className="border-primary text-primary cursor-pointer"
                          disabled={uploading}
                        >
                          {uploading ? 'Uploading...' : <><Upload className="w-4 h-4 mr-1" /> Add Photo</>}
                        </Button>
                      </label>
                    </div>
                  </div>
                  
                  {selectedTask.attachments && selectedTask.attachments.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {selectedTask.attachments.map(att => (
                        <div key={att.id} className="relative group">
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/employee-tasks/${selectedTask.id}/attachments/${att.id}`}
                            alt={att.filename}
                            className="w-full h-24 object-cover rounded border border-zinc-700"
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-2 py-1 text-xs text-white">
                            {att.uploaded_by_name} ({att.uploaded_by_role})
                          </div>
                          <button
                            onClick={() => handleDeleteAttachment(att.id)}
                            className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-sm bg-zinc-800 p-3 rounded text-center">No attachments yet</p>
                  )}
                </div>
              </div>
              
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setViewTaskDialog(false)} className="border-zinc-600">
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Trial Management Page
function TrialManagementPage() {
  const { user } = useAuth();
  const [trialUsers, setTrialUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [form, setForm] = useState({
    email: "", name: "", company_name: "", phone: "", trial_days: 10
  });

  useEffect(() => {
    fetchTrialUsers();
  }, []);

  const fetchTrialUsers = async () => {
    try {
      const res = await apiClient.get("/admin/trial-users");
      setTrialUsers(res.data);
    } catch (err) {
      toast.error("Failed to load trial users");
    }
    setLoading(false);
  };

  const handleCreateTrial = async (e) => {
    e.preventDefault();
    try {
      const res = await apiClient.post("/admin/trial-users", form);
      toast.success("Trial user created!");
      setDialogOpen(false);
      // Show credentials dialog
      setSelectedUser(res.data);
      setDetailsDialogOpen(true);
      setForm({ email: "", name: "", company_name: "", phone: "", trial_days: 10 });
      fetchTrialUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create trial user");
    }
  };

  const handleViewDetails = async (userId) => {
    try {
      const res = await apiClient.get(`/admin/trial-users/${userId}`);
      setSelectedUser(res.data);
      setDetailsDialogOpen(true);
    } catch (err) {
      toast.error("Failed to load user details");
    }
  };

  const handleExtendTrial = async (userId, days = 10) => {
    try {
      await apiClient.put(`/admin/trial-users/${userId}/extend?days=${days}`);
      toast.success(`Trial extended by ${days} days`);
      fetchTrialUsers();
      if (selectedUser?.id === userId) {
        handleViewDetails(userId);
      }
    } catch (err) {
      toast.error("Failed to extend trial");
    }
  };

  const handleActivate = async (userId) => {
    try {
      await apiClient.put(`/admin/trial-users/${userId}/activate`);
      toast.success("Subscription activated!");
      fetchTrialUsers();
      if (selectedUser?.id === userId) {
        handleViewDetails(userId);
      }
    } catch (err) {
      toast.error("Failed to activate subscription");
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const res = await apiClient.put(`/admin/trial-users/${userId}/reset-password`);
      toast.success(`Password reset successfully!`);
      if (selectedUser) {
        setSelectedUser({ ...selectedUser, temp_password: res.data.new_password });
      }
    } catch (err) {
      toast.error("Failed to reset password");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure? This will delete the user and ALL their data permanently.")) return;
    try {
      await apiClient.delete(`/admin/trial-users/${userId}`);
      toast.success("User deleted");
      setDetailsDialogOpen(false);
      fetchTrialUsers();
    } catch (err) {
      toast.error("Failed to delete user");
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    } catch (clipboardErr) {
      console.log("Clipboard not available");
      toast.info("Clipboard not available in this context");
    }
  };

  const copyAllCredentials = async () => {
    if (!selectedUser) return;
    const text = `CrewSync Pro Trial Access\n\nCompany: ${selectedUser.company_name}\nLogin URL: ${window.location.origin}\nEmail: ${selectedUser.email}\nPassword: ${selectedUser.temp_password || 'Contact admin for password'}\n\nYour trial expires in ${selectedUser.days_remaining || 10} days.`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("All credentials copied!");
    } catch (clipboardErr) {
      console.log("Clipboard not available");
      toast.info("Clipboard not available in this context");
    }
  };

  const getStatusColor = (status, daysRemaining) => {
    if (status === 'active') return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (status === 'expired') return 'bg-red-500/20 text-red-400 border-red-500/30';
    if (daysRemaining <= 3) return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
  };

  const getStatusIcon = (status, daysRemaining) => {
    if (status === 'active') return <Crown className="w-4 h-4" />;
    if (status === 'expired') return <AlertCircle className="w-4 h-4" />;
    if (daysRemaining <= 3) return <Clock className="w-4 h-4" />;
    return <Zap className="w-4 h-4" />;
  };

  // Filter users based on tab
  const filteredUsers = trialUsers.filter(u => {
    if (activeTab === "all") return true;
    if (activeTab === "active") return u.subscription_status === 'trial' && u.days_remaining > 0;
    if (activeTab === "converted") return u.subscription_status === 'active';
    if (activeTab === "expired") return u.subscription_status === 'expired' || u.days_remaining === 0;
    return true;
  });

  const stats = {
    total: trialUsers.length,
    active: trialUsers.filter(u => u.subscription_status === 'trial' && u.days_remaining > 0).length,
    converted: trialUsers.filter(u => u.subscription_status === 'active').length,
    expired: trialUsers.filter(u => u.subscription_status === 'expired' || u.days_remaining === 0).length,
    expiringSoon: trialUsers.filter(u => u.subscription_status === 'trial' && u.days_remaining > 0 && u.days_remaining <= 3).length
  };

  return (
    <div className="space-y-4 lg:space-y-6 animate-fade-in" data-testid="trial-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-heading font-bold uppercase text-white flex items-center gap-2 lg:gap-3">
            <Crown className="w-6 h-6 lg:w-8 lg:h-8 text-primary" /> Trial Management
          </h1>
          <p className="text-zinc-400 mt-1 text-sm lg:text-base">Manage trial accounts for customers</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-black font-heading font-bold h-12 px-6" data-testid="create-trial-btn">
              <UserPlus className="w-5 h-5 mr-2" /> Create New Trial
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-600 max-w-[95vw] sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase flex items-center gap-2 text-white">
                <UserPlus className="w-5 h-5 text-primary" /> Create Trial Account
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTrial} className="space-y-4">
              <div>
                <Label className="text-zinc-200">Company Name *</Label>
                <Input 
                  value={form.company_name} 
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })} 
                  className="mt-1 bg-zinc-800 border-zinc-600 text-white" 
                  placeholder="e.g., ABC Contractors" 
                  required 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-200">Contact Name *</Label>
                  <Input 
                    value={form.name} 
                    onChange={(e) => setForm({ ...form, name: e.target.value })} 
                    className="mt-1 bg-zinc-800 border-zinc-600 text-white" 
                    placeholder="John Smith" 
                    required 
                  />
                </div>
                <div>
                  <Label className="text-zinc-200">Phone</Label>
                  <Input 
                    value={form.phone} 
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} 
                    className="mt-1 bg-zinc-800 border-zinc-600 text-white" 
                    placeholder="(555) 123-4567" 
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-200">Email *</Label>
                <Input 
                  type="email"
                  value={form.email} 
                  onChange={(e) => setForm({ ...form, email: e.target.value })} 
                  className="mt-1 bg-zinc-800 border-zinc-600 text-white" 
                  placeholder="john@abccontractors.com" 
                  required 
                />
              </div>
              <div>
                <Label className="text-zinc-200">Trial Period (Days)</Label>
                <Select value={String(form.trial_days)} onValueChange={(v) => setForm({ ...form, trial_days: parseInt(v) })}>
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-600">
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="10">10 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Card className="bg-zinc-800/50 border-zinc-700">
                <CardContent className="p-3">
                  <p className="text-xs text-zinc-400">A temporary password will be generated automatically. You can share the login credentials with the trial user after creation.</p>
                </CardContent>
              </Card>
              <DialogFooter>
                <Button type="submit" className="bg-primary text-black font-heading font-bold">
                  <UserPlus className="w-4 h-4 mr-2" /> Create Trial Account
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
        <Card className={`bg-zinc-900 border-zinc-700 cursor-pointer transition-all ${activeTab === 'all' ? 'ring-2 ring-primary' : 'hover:border-zinc-600'}`} onClick={() => setActiveTab('all')}>
          <CardContent className="p-3 lg:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs lg:text-sm text-zinc-400">Total</p>
                <p className="text-xl lg:text-2xl font-bold text-white">{stats.total}</p>
              </div>
              <Users className="w-6 h-6 lg:w-8 lg:h-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card className={`bg-zinc-900 border-zinc-700 cursor-pointer transition-all ${activeTab === 'active' ? 'ring-2 ring-blue-500' : 'hover:border-zinc-600'}`} onClick={() => setActiveTab('active')}>
          <CardContent className="p-3 lg:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Active Trials</p>
                <p className="text-2xl font-bold text-blue-400">{stats.active}</p>
                {stats.expiringSoon > 0 && <p className="text-xs text-orange-400 mt-1">{stats.expiringSoon} expiring soon</p>}
              </div>
              <Zap className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className={`bg-zinc-900 border-zinc-700 cursor-pointer transition-all ${activeTab === 'converted' ? 'ring-2 ring-green-500' : 'hover:border-zinc-600'}`} onClick={() => setActiveTab('converted')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Converted</p>
                <p className="text-2xl font-bold text-green-400">{stats.converted}</p>
                {stats.total > 0 && <p className="text-xs text-zinc-400 mt-1">{((stats.converted / stats.total) * 100).toFixed(0)}% rate</p>}
              </div>
              <Crown className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className={`bg-zinc-900 border-zinc-700 cursor-pointer transition-all ${activeTab === 'expired' ? 'ring-2 ring-red-500' : 'hover:border-zinc-600'}`} onClick={() => setActiveTab('expired')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Expired</p>
                <p className="text-2xl font-bold text-red-400">{stats.expired}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-primary/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-primary">Revenue Potential</p>
                <p className="text-2xl font-bold text-primary">${(stats.active * 99).toLocaleString()}/mo</p>
                <p className="text-xs text-zinc-400 mt-1">at $99/user</p>
              </div>
              <DollarSign className="w-8 h-8 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-zinc-400">Showing:</span>
        <Badge className={activeTab === 'all' ? 'bg-primary text-black' : 'bg-zinc-800 text-zinc-300 cursor-pointer'} onClick={() => setActiveTab('all')}>
          All ({stats.total})
        </Badge>
        <Badge className={activeTab === 'active' ? 'bg-blue-500 text-white' : 'bg-zinc-800 text-zinc-300 cursor-pointer'} onClick={() => setActiveTab('active')}>
          Active ({stats.active})
        </Badge>
        <Badge className={activeTab === 'converted' ? 'bg-green-500 text-white' : 'bg-zinc-800 text-zinc-300 cursor-pointer'} onClick={() => setActiveTab('converted')}>
          Converted ({stats.converted})
        </Badge>
        <Badge className={activeTab === 'expired' ? 'bg-red-500 text-white' : 'bg-zinc-800 text-zinc-300 cursor-pointer'} onClick={() => setActiveTab('expired')}>
          Expired ({stats.expired})
        </Badge>
      </div>

      {/* Trial Users List */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" /></div>
      ) : filteredUsers.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-700">
          <CardContent className="py-12 text-center">
            <UserPlus className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
            <p className="text-zinc-400 text-lg mb-2">
              {trialUsers.length === 0 ? "No trial users yet" : `No ${activeTab} trials found`}
            </p>
            <p className="text-zinc-400 text-sm">Create your first trial account to start selling!</p>
            <Button className="mt-4 bg-primary text-black" onClick={() => setDialogOpen(true)}>
              <UserPlus className="w-4 h-4 mr-2" /> Create Trial Account
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-zinc-900 border-zinc-700 overflow-hidden">
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-700 bg-zinc-800/50">
                  <th className="text-left p-4 text-xs font-semibold text-primary uppercase">Company</th>
                  <th className="text-left p-4 text-xs font-semibold text-primary uppercase">Contact</th>
                  <th className="text-left p-4 text-xs font-semibold text-primary uppercase">Email</th>
                  <th className="text-left p-4 text-xs font-semibold text-primary uppercase">Status</th>
                  <th className="text-left p-4 text-xs font-semibold text-primary uppercase">Days Left</th>
                  <th className="text-left p-4 text-xs font-semibold text-primary uppercase">Created</th>
                  <th className="text-right p-4 text-xs font-semibold text-primary uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((tu) => (
                  <tr key={tu.id} className="border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors cursor-pointer" onClick={() => handleViewDetails(tu.id)}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${getStatusColor(tu.subscription_status, tu.days_remaining)}`}>
                          {getStatusIcon(tu.subscription_status, tu.days_remaining)}
                        </div>
                        <p className="font-medium text-white">{tu.company_name}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="text-zinc-200">{tu.name}</p>
                      {tu.phone && <p className="text-xs text-zinc-400">{tu.phone}</p>}
                    </td>
                    <td className="p-4 text-zinc-300">{tu.email}</td>
                    <td className="p-4">
                      <Badge className={getStatusColor(tu.subscription_status, tu.days_remaining)}>
                        {tu.subscription_status === 'active' ? 'Paid' : tu.subscription_status === 'expired' ? 'Expired' : 'Trial'}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <span className={`font-bold text-lg ${tu.days_remaining <= 3 && tu.subscription_status !== 'active' ? 'text-orange-400' : tu.days_remaining > 0 || tu.subscription_status === 'active' ? 'text-white' : 'text-red-400'}`}>
                        {tu.subscription_status === 'active' ? '∞' : tu.days_remaining}
                      </span>
                    </td>
                    <td className="p-4 text-zinc-400 text-sm">
                      {new Date(tu.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => handleViewDetails(tu.id)} className="text-zinc-400 hover:text-white" title="View Details">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleExtendTrial(tu.id)} className="text-zinc-400 hover:text-blue-400" title="Extend 10 days">
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        {tu.subscription_status !== 'active' && (
                          <Button variant="ghost" size="sm" onClick={() => handleActivate(tu.id)} className="text-zinc-400 hover:text-green-400" title="Mark as Paid">
                            <Crown className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* User Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-600 max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2 text-white">
              <Building2 className="w-5 h-5 text-primary" /> {selectedUser?.company_name}
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              {/* Status Banner */}
              <div className={`p-3 rounded-md flex items-center justify-between ${getStatusColor(selectedUser.subscription_status, selectedUser.days_remaining || 0)}`}>
                <div className="flex items-center gap-2">
                  {getStatusIcon(selectedUser.subscription_status, selectedUser.days_remaining || 0)}
                  <span className="font-medium">
                    {selectedUser.subscription_status === 'active' ? 'Paid Subscription' : 
                     selectedUser.subscription_status === 'expired' ? 'Trial Expired' : 
                     `${selectedUser.days_remaining || 0} Days Remaining`}
                  </span>
                </div>
                {selectedUser.trial_expires_at && selectedUser.subscription_status !== 'active' && (
                  <span className="text-sm">Expires: {new Date(selectedUser.trial_expires_at).toLocaleDateString()}</span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-zinc-400 uppercase">Contact Name</p>
                  <p className="text-white font-medium">{selectedUser.name}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-400 uppercase">Phone</p>
                  <p className="text-white">{selectedUser.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-400 uppercase">Created</p>
                  <p className="text-white">{new Date(selectedUser.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-400 uppercase">Trial Length</p>
                  <p className="text-white">{selectedUser.trial_days || 10} days</p>
                </div>
              </div>
              
              {/* Login Credentials Card */}
              <Card className="bg-zinc-800 border-primary/30">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-primary uppercase font-heading flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Login Credentials
                    </p>
                    <Button variant="outline" size="sm" onClick={copyAllCredentials} className="border-primary text-primary hover:bg-primary/10">
                      <Copy className="w-3 h-3 mr-1" /> Copy All
                    </Button>
                  </div>
                  <div className="bg-zinc-900 rounded-md p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-zinc-400">Login URL</p>
                        <p className="text-white font-mono text-sm">{window.location.origin}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(window.location.origin)} className="text-zinc-400 hover:text-white">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-zinc-400">Email</p>
                        <p className="text-white font-mono text-sm">{selectedUser.email}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(selectedUser.email)} className="text-zinc-400 hover:text-white">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-zinc-400">Password</p>
                        <p className="text-primary font-mono text-sm">{selectedUser.temp_password || '••••••••'}</p>
                      </div>
                      <div className="flex gap-1">
                        {selectedUser.temp_password && (
                          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(selectedUser.temp_password)} className="text-zinc-400 hover:text-white">
                            <Copy className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleResetPassword(selectedUser.id)} title="Reset password" className="text-zinc-400 hover:text-primary">
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" size="sm" onClick={() => handleExtendTrial(selectedUser.id, 7)} className="border-zinc-600 text-white hover:bg-zinc-800">
                  +7 Days
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleExtendTrial(selectedUser.id, 14)} className="border-zinc-600 text-white hover:bg-zinc-800">
                  +14 Days
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleExtendTrial(selectedUser.id, 30)} className="border-zinc-600 text-white hover:bg-zinc-800">
                  +30 Days
                </Button>
              </div>

              <div className="flex gap-2 pt-2">
                {selectedUser.subscription_status !== 'active' && (
                  <Button onClick={() => handleActivate(selectedUser.id)} className="flex-1 bg-green-600 hover:bg-green-700 text-white">
                    <Crown className="w-4 h-4 mr-2" /> Mark as Paid
                  </Button>
                )}
                <Button variant="outline" onClick={() => handleDeleteUser(selectedUser.id)} className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============== PLATFORM OWNER EXCLUSIVE PAGE ==============
// This page contains features ONLY available to the platform owner (you)
// These features are NEVER shown to paying customers

// Data Import/Export Component for Platform Owner
function DataImportExport() {
  const [activeTab, setActiveTab] = useState('full-import');
  const [importCollection, setImportCollection] = useState('customers');
  const [exportCollection, setExportCollection] = useState('customers');
  const [importData, setImportData] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [fieldMapping, setFieldMapping] = useState({});
  const [companies, setCompanies] = useState([]);
  const [targetCompany, setTargetCompany] = useState('');
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Full App Import state
  const [fullImportData, setFullImportData] = useState(null);
  const [fullImportPreview, setFullImportPreview] = useState(null);
  const [conflictResolution, setConflictResolution] = useState('skip');
  const [fullImportResult, setFullImportResult] = useState(null);
  const [exportCompany, setExportCompany] = useState('');
  const [exportEmail, setExportEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const collections = [
    { id: 'customers', name: 'Customers', icon: Users, description: 'Customer names, contacts, addresses' },
    { id: 'jobs', name: 'Jobs', icon: Briefcase, description: 'Job titles, dates, status' },
    { id: 'employees', name: 'Employees', icon: User, description: 'Employee names, roles, pay rates' },
    { id: 'time_entries', name: 'Time Entries', icon: Clock, description: 'Hours, employees, dates' },
    { id: 'material_catalog', name: 'Materials', icon: Package, description: 'Products, pricing, categories' },
  ];

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const res = await apiClient.get('/owner/companies');
      setCompanies(res.data || []);
    } catch (err) {
      console.log('Failed to fetch companies');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        let data;

        if (file.name.endsWith('.json')) {
          data = JSON.parse(text);
        } else {
          // Parse CSV
          const lines = text.split('\n').filter(line => line.trim());
          const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
          data = lines.slice(1).map(line => {
            const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
            const record = {};
            headers.forEach((header, idx) => {
              record[header] = values[idx] || '';
            });
            return record;
          });
        }

        setImportData(data);
        setPreviewResult(null);
        setImportResult(null);
        toast.success(`Loaded ${data.length} records from ${file.name}`);
      } catch (err) {
        toast.error('Failed to parse file. Please check the format.');
      }
    };
    reader.readAsText(file);
  };

  const previewImport = async () => {
    if (!importData || importData.length === 0) {
      toast.error('Please upload a file first');
      return;
    }

    setLoading(true);
    try {
      const res = await apiClient.post('/owner/import/preview', {
        collection: importCollection,
        data: importData
      });
      setPreviewResult(res.data);
      setFieldMapping(res.data.auto_mapping || {});
      toast.success('Preview generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to preview import');
    }
    setLoading(false);
  };

  const executeImport = async () => {
    if (!targetCompany) {
      toast.error('Please select a target company');
      return;
    }

    setLoading(true);
    try {
      const res = await apiClient.post('/owner/import/execute', {
        collection: importCollection,
        data: importData,
        field_mapping: fieldMapping,
        target_company_id: targetCompany
      });
      setImportResult(res.data);
      toast.success(`Imported ${res.data.imported} ${importCollection}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    }
    setLoading(false);
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/owner/export/${exportCollection}`);
      const data = res.data.data;
      
      // Convert to CSV
      if (data.length === 0) {
        toast.info('No data to export');
        setLoading(false);
        return;
      }

      const headers = Object.keys(data[0]).filter(k => k !== '_id');
      const csv = [
        headers.join(','),
        ...data.map(row => headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportCollection}_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success(`Exported ${data.length} ${exportCollection}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
    setLoading(false);
  };

  // Full App Import handlers
  const handleFullImportFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast.error('Please upload a JSON file exported from another CrewSync app');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        
        // Validate it's a CrewSync export
        if (!data.meta || data.meta.source_app !== 'CrewSync Pro') {
          toast.error('Invalid file. Must be a CrewSync Pro export file.');
          return;
        }
        
        setFullImportData(data);
        setFullImportPreview(null);
        setFullImportResult(null);
        toast.success(`Loaded CrewSync export from "${data.meta.company_name}"`);
      } catch (err) {
        toast.error('Failed to parse JSON file');
      }
    };
    reader.readAsText(file);
  };

  const previewFullImport = async () => {
    if (!fullImportData) {
      toast.error('Please upload a file first');
      return;
    }

    setLoading(true);
    try {
      const res = await apiClient.post('/owner/full-import/preview', fullImportData);
      setFullImportPreview(res.data);
      toast.success('Preview generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to preview import');
    }
    setLoading(false);
  };

  const executeFullImport = async () => {
    if (!targetCompany) {
      toast.error('Please select a target company');
      return;
    }

    setLoading(true);
    try {
      const res = await apiClient.post('/owner/full-import/execute', {
        ...fullImportData,
        target_company_id: targetCompany,
        conflict_resolution: conflictResolution
      });
      setFullImportResult(res.data);
      toast.success(`Import complete! ${res.data.summary?.total_imported || 0} records imported.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    }
    setLoading(false);
  };

  const handleFullExport = async () => {
    if (!exportCompany) {
      toast.error('Please select a company to export');
      return;
    }

    setLoading(true);
    try {
      const res = await apiClient.get(`/owner/full-export/${exportCompany}`);
      
      // Download as JSON
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crewsync_export_${res.data.meta?.company_name?.replace(/\s+/g, '_') || 'company'}_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success(`Exported all data for ${res.data.meta?.company_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
    setLoading(false);
  };

  const handleEmailExport = async () => {
    if (!exportCompany) {
      toast.error('Please select a company to export');
      return;
    }
    if (!exportEmail || !exportEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setEmailSending(true);
    try {
      const res = await apiClient.post('/owner/full-export/email', {
        company_id: exportCompany,
        recipient_email: exportEmail
      });
      
      toast.success(`Export sent to ${exportEmail}!`);
      setExportEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    }
    setEmailSending(false);
  };

  return (
    <div className="space-y-6">
      {/* Tab Selector */}
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => setActiveTab('full-import')}
          className={activeTab === 'full-import' ? 'bg-primary text-black' : 'bg-zinc-800 text-zinc-300'}
        >
          <Database className="w-4 h-4 mr-2" /> Full App Import
        </Button>
        <Button
          onClick={() => setActiveTab('full-export')}
          className={activeTab === 'full-export' ? 'bg-primary text-black' : 'bg-zinc-800 text-zinc-300'}
        >
          <Download className="w-4 h-4 mr-2" /> Full App Export
        </Button>
        <Button
          onClick={() => setActiveTab('import')}
          className={activeTab === 'import' ? 'bg-primary text-black' : 'bg-zinc-800 text-zinc-300'}
        >
          <Upload className="w-4 h-4 mr-2" /> CSV Import
        </Button>
        <Button
          onClick={() => setActiveTab('export')}
          className={activeTab === 'export' ? 'bg-primary text-black' : 'bg-zinc-800 text-zinc-300'}
        >
          <FileText className="w-4 h-4 mr-2" /> CSV Export
        </Button>
      </div>

      {/* Full App Import Tab */}
      {activeTab === 'full-import' && (
        <div className="space-y-4">
          <Card className="bg-zinc-900 border-zinc-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-primary" /> Import from Another CrewSync App
              </CardTitle>
              <CardDescription>
                Upload a JSON export file from another deployed CrewSync application to migrate all data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Step 1: Upload JSON */}
              <div>
                <Label className="text-sm text-zinc-300">Step 1: Upload CrewSync Export File (JSON)</Label>
                <div className="mt-2 flex items-center gap-4">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleFullImportFile}
                    className="block w-full text-sm text-zinc-400
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-lg file:border-0
                      file:text-sm file:font-medium
                      file:bg-primary file:text-black
                      hover:file:bg-primary/80
                      cursor-pointer"
                    data-testid="full-import-file-input"
                  />
                  {fullImportData && (
                    <Badge className="bg-green-500/20 text-green-400 whitespace-nowrap">
                      ✓ From: {fullImportData.meta?.company_name}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Show loaded data summary */}
              {fullImportData && (
                <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
                  <p className="text-sm text-zinc-300 font-medium mb-2">Export File Contents:</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    {Object.entries(fullImportData.meta?.summary || {}).map(([key, count]) => (
                      <div key={key} className="flex items-center gap-2">
                        <span className="text-zinc-400 capitalize">{key.replace('_', ' ')}:</span>
                        <Badge className="bg-zinc-700 text-white">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Select Target Company */}
              <div>
                <Label className="text-sm text-zinc-300">Step 2: Select Target Company</Label>
                <select
                  value={targetCompany}
                  onChange={(e) => setTargetCompany(e.target.value)}
                  className="w-full mt-2 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                  data-testid="full-import-target-company"
                >
                  <option value="">Choose company to import into...</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.plan})</option>
                  ))}
                </select>
              </div>

              {/* Step 3: Conflict Resolution */}
              <div>
                <Label className="text-sm text-zinc-300">Step 3: How to Handle Conflicts?</Label>
                <p className="text-xs text-zinc-500 mb-2">When importing data that might already exist (same customer name, employee email, etc.)</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                  <button
                    onClick={() => setConflictResolution('skip')}
                    className={`p-4 rounded-lg border text-left transition-colors ${
                      conflictResolution === 'skip' 
                        ? 'border-primary bg-primary/10' 
                        : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                    }`}
                    data-testid="conflict-skip"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle className={`w-4 h-4 ${conflictResolution === 'skip' ? 'text-primary' : 'text-zinc-400'}`} />
                      <span className={`font-medium ${conflictResolution === 'skip' ? 'text-primary' : 'text-white'}`}>Skip Duplicates</span>
                    </div>
                    <p className="text-xs text-zinc-500">Don't import records that already exist. Safest option.</p>
                  </button>
                  
                  <button
                    onClick={() => setConflictResolution('create_new')}
                    className={`p-4 rounded-lg border text-left transition-colors ${
                      conflictResolution === 'create_new' 
                        ? 'border-primary bg-primary/10' 
                        : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                    }`}
                    data-testid="conflict-create-new"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Plus className={`w-4 h-4 ${conflictResolution === 'create_new' ? 'text-primary' : 'text-zinc-400'}`} />
                      <span className={`font-medium ${conflictResolution === 'create_new' ? 'text-primary' : 'text-white'}`}>Create New</span>
                    </div>
                    <p className="text-xs text-zinc-500">Always create new records, even if similar ones exist.</p>
                  </button>
                  
                  <button
                    onClick={() => setConflictResolution('overwrite')}
                    className={`p-4 rounded-lg border text-left transition-colors ${
                      conflictResolution === 'overwrite' 
                        ? 'border-red-500 bg-red-500/10' 
                        : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                    }`}
                    data-testid="conflict-overwrite"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <RefreshCw className={`w-4 h-4 ${conflictResolution === 'overwrite' ? 'text-red-400' : 'text-zinc-400'}`} />
                      <span className={`font-medium ${conflictResolution === 'overwrite' ? 'text-red-400' : 'text-white'}`}>Overwrite</span>
                    </div>
                    <p className="text-xs text-zinc-500">Replace existing records with imported data. Use with caution!</p>
                  </button>
                </div>
              </div>

              {/* Preview Button */}
              <Button
                onClick={previewFullImport}
                disabled={!fullImportData || loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="full-import-preview-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                Preview Import
              </Button>
            </CardContent>
          </Card>

          {/* Preview Results */}
          {fullImportPreview && (
            <Card className="bg-zinc-900 border-zinc-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" /> Import Preview
                </CardTitle>
                <CardDescription>
                  Source: {fullImportPreview.source_company} • Exported: {new Date(fullImportPreview.exported_at).toLocaleDateString()}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Collections Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(fullImportPreview.collections || {}).map(([key, info]) => (
                    <div key={key} className="bg-zinc-800 rounded-lg p-3 border border-zinc-700">
                      <p className="text-xs text-zinc-400 uppercase">{key.replace('_', ' ')}</p>
                      <p className="text-2xl font-bold text-white">{info.count}</p>
                    </div>
                  ))}
                </div>

                {/* Potential Conflicts Warning */}
                {fullImportPreview.potential_conflicts && Object.keys(fullImportPreview.potential_conflicts).length > 0 && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-yellow-400" />
                      <span className="font-medium text-yellow-400">Potential Conflicts Detected</span>
                    </div>
                    {Object.entries(fullImportPreview.potential_conflicts).map(([key, conflict]) => (
                      <div key={key} className="text-sm text-zinc-300">
                        <span className="capitalize">{key}</span>: {conflict.count} potential {conflict.type.replace('_', ' ')} conflicts
                        {conflict.items && conflict.items.length > 0 && (
                          <div className="ml-4 text-xs text-zinc-500">
                            e.g., {conflict.items.slice(0, 3).map(i => i.email || i.name).join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Execute Import Button */}
                <Button
                  onClick={executeFullImport}
                  disabled={!targetCompany || loading}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="full-import-execute-btn"
                >
                  {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Import All Data to {companies.find(c => c.id === targetCompany)?.name || 'Selected Company'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Import Result */}
          {fullImportResult && (
            <Card className={`border ${fullImportResult.summary?.total_errors > 0 ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {fullImportResult.summary?.total_errors > 0 ? (
                    <AlertCircle className="w-6 h-6 text-yellow-400" />
                  ) : (
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  )}
                  Import Complete
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-green-500/10 rounded-lg p-4">
                    <p className="text-3xl font-bold text-green-400">{fullImportResult.summary?.total_imported || 0}</p>
                    <p className="text-sm text-zinc-400">Imported</p>
                  </div>
                  <div className="bg-zinc-800 rounded-lg p-4">
                    <p className="text-3xl font-bold text-zinc-300">{fullImportResult.summary?.total_skipped || 0}</p>
                    <p className="text-sm text-zinc-400">Skipped</p>
                  </div>
                  <div className="bg-red-500/10 rounded-lg p-4">
                    <p className="text-3xl font-bold text-red-400">{fullImportResult.summary?.total_errors || 0}</p>
                    <p className="text-sm text-zinc-400">Errors</p>
                  </div>
                </div>

                {/* Per-collection breakdown */}
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <p className="text-sm text-zinc-300 font-medium mb-2">Import Details:</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                    {Object.entries(fullImportResult.collections || {}).map(([key, info]) => (
                      <div key={key} className="flex items-center justify-between">
                        <span className="text-zinc-400 capitalize">{key.replace('_', ' ')}</span>
                        <span className="text-white">{info.imported} <span className="text-zinc-500">({info.skipped} skipped)</span></span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Errors */}
                {fullImportResult.errors && fullImportResult.errors.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 max-h-40 overflow-y-auto">
                    <p className="text-sm text-red-400 font-medium mb-2">Errors ({fullImportResult.errors.length}):</p>
                    {fullImportResult.errors.slice(0, 20).map((err, idx) => (
                      <p key={idx} className="text-xs text-red-300">{err}</p>
                    ))}
                    {fullImportResult.errors.length > 20 && (
                      <p className="text-xs text-zinc-500 mt-2">... and {fullImportResult.errors.length - 20} more</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Full App Export Tab */}
      {activeTab === 'full-export' && (
        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-400" /> Export All Company Data
            </CardTitle>
            <CardDescription>
              Download or email all data for a company as JSON. Use this to migrate data to another CrewSync deployment.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm text-zinc-300">Select Company to Export</Label>
              <select
                value={exportCompany}
                onChange={(e) => setExportCompany(e.target.value)}
                className="w-full mt-2 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                data-testid="full-export-company"
              >
                <option value="">Choose a company...</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name} ({c.plan})</option>
                ))}
              </select>
            </div>

            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
              <p className="text-sm text-zinc-300 font-medium mb-2">What will be exported:</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-zinc-400">
                <span>• Customers</span>
                <span>• Jobs</span>
                <span>• Employees</span>
                <span>• Estimates</span>
                <span>• Invoices</span>
                <span>• Materials</span>
                <span>• Time Entries</span>
                <span>• Templates</span>
                <span>• Leads</span>
              </div>
            </div>

            {/* Download Option */}
            <Button
              onClick={handleFullExport}
              disabled={!exportCompany || loading}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="full-export-btn"
            >
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Download Full Export (JSON)
            </Button>

            {/* Email Option */}
            <div className="border-t border-zinc-700 pt-4">
              <Label className="text-sm text-zinc-300 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Or Send to Email
              </Label>
              <div className="flex gap-2 mt-2">
                <Input
                  type="email"
                  placeholder="Enter email address..."
                  value={exportEmail}
                  onChange={(e) => setExportEmail(e.target.value)}
                  className="flex-1 bg-zinc-800 border-zinc-700 text-white"
                  data-testid="export-email-input"
                />
                <Button
                  onClick={handleEmailExport}
                  disabled={!exportCompany || !exportEmail || emailSending}
                  className="bg-green-600 hover:bg-green-700 whitespace-nowrap"
                  data-testid="export-email-btn"
                >
                  {emailSending ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <><Mail className="w-4 h-4 mr-2" /> Send</>
                  )}
                </Button>
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                Export will be sent as a JSON file attachment to the specified email
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* CSV Import Tab */}
      {activeTab === 'import' && (
        <div className="space-y-4">
          <Card className="bg-zinc-900 border-zinc-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-green-400" /> Import Data from External App
              </CardTitle>
              <CardDescription>
                Upload CSV or JSON file from your existing Emergent app to migrate data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Step 1: Select Collection */}
              <div>
                <Label className="text-sm text-zinc-300">Step 1: Select Data Type</Label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
                  {collections.map(col => {
                    const Icon = col.icon;
                    return (
                      <button
                        key={col.id}
                        onClick={() => { setImportCollection(col.id); setPreviewResult(null); setImportResult(null); }}
                        className={`p-3 rounded-lg border text-left transition-colors ${
                          importCollection === col.id 
                            ? 'border-primary bg-primary/10' 
                            : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                        }`}
                      >
                        <Icon className={`w-5 h-5 mb-1 ${importCollection === col.id ? 'text-primary' : 'text-zinc-400'}`} />
                        <p className={`font-medium text-sm ${importCollection === col.id ? 'text-primary' : 'text-white'}`}>{col.name}</p>
                        <p className="text-xs text-zinc-500">{col.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 2: Upload File */}
              <div>
                <Label className="text-sm text-zinc-300">Step 2: Upload File (CSV or JSON)</Label>
                <div className="mt-2 flex items-center gap-4">
                  <input
                    type="file"
                    accept=".csv,.json"
                    onChange={handleFileUpload}
                    className="block w-full text-sm text-zinc-400
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-lg file:border-0
                      file:text-sm file:font-medium
                      file:bg-primary file:text-black
                      hover:file:bg-primary/80
                      cursor-pointer"
                  />
                  {importData && (
                    <Badge className="bg-green-500/20 text-green-400">
                      {importData.length} records loaded
                    </Badge>
                  )}
                </div>
              </div>

              {/* Step 3: Select Target Company */}
              <div>
                <Label className="text-sm text-zinc-300">Step 3: Select Target Company</Label>
                <select
                  value={targetCompany}
                  onChange={(e) => setTargetCompany(e.target.value)}
                  className="w-full mt-2 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="">Choose company to import into...</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.plan})</option>
                  ))}
                </select>
              </div>

              {/* Preview Button */}
              <Button
                onClick={previewImport}
                disabled={!importData || loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                Preview & Map Fields
              </Button>
            </CardContent>
          </Card>

          {/* Preview & Field Mapping */}
          {previewResult && (
            <Card className="bg-zinc-900 border-zinc-700">
              <CardHeader>
                <CardTitle>Field Mapping</CardTitle>
                <CardDescription>
                  Auto-detected {Object.keys(previewResult.auto_mapping).length} field mappings. 
                  Adjust if needed.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {previewResult.expected_fields.map(expectedField => (
                    <div key={expectedField} className="flex items-center gap-2">
                      <Label className="w-32 text-zinc-400 text-sm">{expectedField}:</Label>
                      <select
                        value={fieldMapping[expectedField] || ''}
                        onChange={(e) => setFieldMapping({...fieldMapping, [expectedField]: e.target.value})}
                        className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-sm"
                      >
                        <option value="">-- Not mapped --</option>
                        {previewResult.detected_fields.map(f => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>

                {/* Sample Data Preview */}
                <div className="mt-4">
                  <Label className="text-sm text-zinc-300">Sample Data Preview</Label>
                  <div className="mt-2 bg-zinc-800 rounded-lg p-3 overflow-x-auto">
                    <table className="text-sm w-full">
                      <thead>
                        <tr className="border-b border-zinc-700">
                          {previewResult.detected_fields.slice(0, 6).map(f => (
                            <th key={f} className="px-2 py-1 text-left text-zinc-400">{f}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewResult.sample_data.map((row, idx) => (
                          <tr key={idx} className="border-b border-zinc-800">
                            {previewResult.detected_fields.slice(0, 6).map(f => (
                              <td key={f} className="px-2 py-1 text-white truncate max-w-[150px]">{row[f]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Import Button */}
                <Button
                  onClick={executeImport}
                  disabled={!targetCompany || loading}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Import {previewResult.total_records} Records
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Import Result */}
          {importResult && (
            <Card className={`border ${importResult.failed > 0 ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  {importResult.failed > 0 ? (
                    <AlertCircle className="w-8 h-8 text-yellow-400" />
                  ) : (
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  )}
                  <div>
                    <p className="text-lg font-bold text-white">
                      Import Complete: {importResult.imported} of {importResult.total_records} records
                    </p>
                    {importResult.failed > 0 && (
                      <p className="text-yellow-400 text-sm">{importResult.failed} records failed</p>
                    )}
                  </div>
                </div>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="mt-3 max-h-40 overflow-y-auto">
                    {importResult.errors.map((err, idx) => (
                      <p key={idx} className="text-xs text-red-400">Row {err.row}: {err.error}</p>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Export Tab */}
      {activeTab === 'export' && (
        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-400" /> Export Data
            </CardTitle>
            <CardDescription>Download data as CSV for backup or analysis</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {collections.map(col => {
              const Icon = col.icon;
              return (
                <div key={col.id} className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-primary" />
                    <div>
                      <span className="text-white font-medium">{col.name}</span>
                      <p className="text-xs text-zinc-500">{col.description}</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    className="border-primary text-primary"
                    onClick={() => { setExportCollection(col.id); handleExport(); }}
                    disabled={loading}
                  >
                    {loading && exportCollection === col.id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <><Download className="w-4 h-4 mr-2" /> Export CSV</>
                    )}
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function PlatformOwnerPage({ section }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  
  // Verify platform owner access
  useEffect(() => {
    if (user?.role !== 'platform_admin') {
      toast.error('Access denied. Platform Owner features only.');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  if (user?.role !== 'platform_admin') {
    return null;
  }

  const sections = {
    'analytics': {
      title: 'Platform Analytics',
      icon: BarChart3,
      description: 'Cross-tenant analytics, KPIs, and business intelligence',
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-zinc-400">Total MRR</p>
                    <p className="text-3xl font-bold text-primary">$4,500</p>
                  </div>
                  <DollarSign className="w-10 h-10 text-primary/40" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-zinc-400">Active Tenants</p>
                    <p className="text-3xl font-bold text-white">12</p>
                  </div>
                  <Building2 className="w-10 h-10 text-zinc-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-zinc-400">Total Users</p>
                    <p className="text-3xl font-bold text-white">87</p>
                  </div>
                  <Users className="w-10 h-10 text-zinc-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-zinc-400">Churn Rate</p>
                    <p className="text-3xl font-bold text-green-400">2.1%</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-zinc-600" />
                </div>
              </CardContent>
            </Card>
          </div>
          <Card className="bg-zinc-900 border-zinc-700">
            <CardHeader>
              <CardTitle>Revenue Growth</CardTitle>
              <CardDescription>Monthly recurring revenue trend</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-center justify-center text-zinc-500">
                <p>Revenue chart will be implemented here</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )
    },
    'data-export': {
      title: 'Data Import/Export',
      icon: Database,
      description: 'Import data from existing apps or export for backup',
      content: <DataImportExport />
    },
    'feature-lab': {
      title: 'Feature Lab',
      icon: Beaker,
      description: 'Test experimental features before rolling out to customers',
      content: (
        <div className="space-y-4">
          <Card className="bg-zinc-900 border-zinc-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Beaker className="w-5 h-5 text-purple-400" /> Experimental Features
              </CardTitle>
              <CardDescription>Features in development - test before release</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { name: 'AI Job Pricing', status: 'beta', description: 'AI-powered job price suggestions' },
                { name: 'Voice Commands', status: 'alpha', description: 'Control the app with voice' },
                { name: 'Mobile App Sync', status: 'planned', description: 'Sync with native mobile app' },
                { name: 'GPS Tracking', status: 'beta', description: 'Track crew locations in real-time' },
              ].map(feature => (
                <div key={feature.name} className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{feature.name}</span>
                      <Badge className={
                        feature.status === 'beta' ? 'bg-blue-500/20 text-blue-400' :
                        feature.status === 'alpha' ? 'bg-purple-500/20 text-purple-400' :
                        'bg-zinc-500/20 text-zinc-400'
                      }>{feature.status}</Badge>
                    </div>
                    <p className="text-sm text-zinc-400">{feature.description}</p>
                  </div>
                  <Button variant="outline" className="border-zinc-600" disabled={feature.status === 'planned'}>
                    {feature.status === 'planned' ? 'Coming Soon' : 'Test Feature'}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )
    },
    'system-health': {
      title: 'System Health',
      icon: Activity,
      description: 'Monitor system performance and health metrics',
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-green-500/10 border-green-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-green-400 font-medium">API Server</span>
                </div>
                <p className="text-2xl font-bold text-white mt-2">Healthy</p>
                <p className="text-sm text-zinc-400">Response time: 45ms</p>
              </CardContent>
            </Card>
            <Card className="bg-green-500/10 border-green-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-green-400 font-medium">Database</span>
                </div>
                <p className="text-2xl font-bold text-white mt-2">Healthy</p>
                <p className="text-sm text-zinc-400">Connections: 12/100</p>
              </CardContent>
            </Card>
            <Card className="bg-green-500/10 border-green-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-green-400 font-medium">Email Service</span>
                </div>
                <p className="text-2xl font-bold text-white mt-2">Healthy</p>
                <p className="text-sm text-zinc-400">Resend: Connected</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )
    },
    'audit-logs': {
      title: 'Audit Logs',
      icon: Shield,
      description: 'System-wide activity and security logs',
      content: (
        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>System-wide audit trail</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[
                { action: 'User Login', user: 'coy@concretecoats.com', time: '2 min ago', type: 'auth' },
                { action: 'Invoice Created', user: 'admin@acmelandscaping.com', time: '15 min ago', type: 'billing' },
                { action: 'New Company Registered', user: 'system', time: '1 hour ago', type: 'system' },
                { action: 'Feature Toggle Changed', user: 'coy@concretecoats.com', time: '2 hours ago', type: 'admin' },
              ].map((log, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded border border-zinc-700">
                  <div className="flex items-center gap-3">
                    <Shield className={`w-4 h-4 ${
                      log.type === 'auth' ? 'text-blue-400' :
                      log.type === 'billing' ? 'text-green-400' :
                      log.type === 'system' ? 'text-purple-400' :
                      'text-yellow-400'
                    }`} />
                    <div>
                      <p className="text-white text-sm">{log.action}</p>
                      <p className="text-xs text-zinc-500">{log.user}</p>
                    </div>
                  </div>
                  <span className="text-xs text-zinc-500">{log.time}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )
    },
    'impersonate': {
      title: 'Tenant Impersonation',
      icon: Users,
      description: 'Login as any tenant for support and debugging',
      content: (
        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-400" /> Impersonate Tenant
            </CardTitle>
            <CardDescription>Login as a customer to provide support or debug issues</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-4">
              <p className="text-orange-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Use this feature responsibly. All actions are logged.
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Select Company</Label>
                <select className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white">
                  <option value="">Choose a company...</option>
                  <option value="1">Acme Landscaping</option>
                  <option value="2">Smith Concrete</option>
                  <option value="3">Green Thumb Gardens</option>
                </select>
              </div>
              <Button className="w-full bg-orange-500 text-white hover:bg-orange-600">
                <Users className="w-4 h-4 mr-2" /> Start Impersonation Session
              </Button>
            </div>
          </CardContent>
        </Card>
      )
    }
  };

  const currentSection = sections[section] || sections['analytics'];
  const Icon = currentSection.icon;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Icon className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-white uppercase tracking-tight">
                {currentSection.title}
              </h1>
              <p className="text-zinc-400 text-sm">{currentSection.description}</p>
            </div>
          </div>
        </div>
        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
          <Crown className="w-3 h-3 mr-1" /> Platform Owner Only
        </Badge>
      </div>

      {/* Content */}
      {currentSection.content}
    </div>
  );
}

// Support Page - For clients to submit requests and view their tickets
function SupportPage() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRequestDialog, setShowNewRequestDialog] = useState(false);
  const [newRequest, setNewRequest] = useState({
    request_type: 'question',
    subject: '',
    description: '',
    priority: 'medium'
  });
  const [submitting, setSubmitting] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminResponse, setAdminResponse] = useState('');
  const [responseStatus, setResponseStatus] = useState('in_progress');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await apiClient.get('/support/requests');
      setRequests(res.data);
    } catch (err) {
      toast.error("Failed to load support requests");
    }
    setLoading(false);
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!newRequest.subject || !newRequest.description) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post('/support/requests', newRequest);
      toast.success("Support request submitted! We'll respond shortly.");
      setShowNewRequestDialog(false);
      setNewRequest({ request_type: 'question', subject: '', description: '', priority: 'medium' });
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request");
    }
    setSubmitting(false);
  };

  const handleRespond = async () => {
    if (!adminResponse.trim()) {
      toast.error("Please enter a response");
      return;
    }
    try {
      await apiClient.put(`/support/requests/${selectedRequest.id}/respond`, {
        response: adminResponse,
        status: responseStatus
      });
      toast.success("Response sent!");
      setSelectedRequest(null);
      setAdminResponse('');
      fetchRequests();
    } catch (err) {
      toast.error("Failed to send response");
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm("Delete this support request?")) return;
    try {
      await apiClient.delete(`/support/requests/${requestId}`);
      toast.success("Request deleted");
      fetchRequests();
    } catch (err) {
      toast.error("Failed to delete request");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-500/20 text-blue-400';
      case 'in_progress': return 'bg-yellow-500/20 text-yellow-400';
      case 'resolved': return 'bg-green-500/20 text-green-400';
      case 'closed': return 'bg-zinc-500/20 text-zinc-400';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500/20 text-red-400';
      case 'high': return 'bg-orange-500/20 text-orange-400';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400';
      case 'low': return 'bg-green-500/20 text-green-400';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'bug_report': return '🐛';
      case 'feature_request': return '💡';
      case 'maintenance': return '🔧';
      case 'question': return '❓';
      default: return '📝';
    }
  };

  const isPlatformAdmin = user?.role === 'platform_admin';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="support-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">
            {isPlatformAdmin ? 'SUPPORT REQUESTS' : 'SUPPORT'}
          </h1>
          <p className="text-zinc-400 mt-1">
            {isPlatformAdmin ? 'Manage support requests from all companies' : 'Get help or report issues'}
          </p>
        </div>
        {!isPlatformAdmin && (
          <Button onClick={() => setShowNewRequestDialog(true)} className="bg-primary text-black font-heading font-bold">
            <Plus className="w-4 h-4 mr-2" /> New Request
          </Button>
        )}
      </div>

      {/* Stats for Platform Admin */}
      {isPlatformAdmin && (
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-zinc-900 border-zinc-700">
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-blue-400">{requests.filter(r => r.status === 'open').length}</div>
              <p className="text-zinc-400 text-sm">Open</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-700">
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-yellow-400">{requests.filter(r => r.status === 'in_progress').length}</div>
              <p className="text-zinc-400 text-sm">In Progress</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-700">
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-red-400">{requests.filter(r => r.priority === 'urgent').length}</div>
              <p className="text-zinc-400 text-sm">Urgent</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-700">
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-green-400">{requests.filter(r => r.status === 'resolved').length}</div>
              <p className="text-zinc-400 text-sm">Resolved</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Requests List */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader>
          <CardTitle className="font-heading uppercase">{isPlatformAdmin ? 'All Requests' : 'Your Requests'}</CardTitle>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
              <p className="text-zinc-400">No support requests yet</p>
              {!isPlatformAdmin && (
                <Button onClick={() => setShowNewRequestDialog(true)} className="mt-4 bg-primary text-black">
                  Submit Your First Request
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map(request => (
                <div key={request.id} className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700 hover:border-zinc-600 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xl">{getTypeIcon(request.request_type)}</span>
                        <h3 className="text-white font-medium">{request.subject}</h3>
                        <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(request.status)}`}>
                          {request.status.replace('_', ' ')}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs ${getPriorityColor(request.priority)}`}>
                          {request.priority}
                        </span>
                      </div>
                      <p className="text-zinc-400 text-sm line-clamp-2">{request.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                        {isPlatformAdmin && <span>From: {request.user_name} ({request.company_name || 'N/A'})</span>}
                        <span>Submitted: {new Date(request.created_at).toLocaleDateString()}</span>
                      </div>
                      {request.admin_response && (
                        <div className="mt-3 bg-zinc-900/50 rounded p-3 border-l-2 border-primary">
                          <p className="text-xs text-zinc-500 mb-1">Admin Response:</p>
                          <p className="text-zinc-300 text-sm">{request.admin_response}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {isPlatformAdmin && request.status !== 'closed' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => { setSelectedRequest(request); setAdminResponse(request.admin_response || ''); }}
                          className="border-primary/50 text-primary"
                        >
                          <MessageSquare className="w-4 h-4 mr-1" /> Respond
                        </Button>
                      )}
                      {isPlatformAdmin && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleDeleteRequest(request.id)}
                          className="text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Request Dialog */}
      <Dialog open={showNewRequestDialog} onOpenChange={setShowNewRequestDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-[95vw] sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-primary" /> Submit Support Request
            </DialogTitle>
            <DialogDescription>Describe your issue or question and we'll respond as soon as possible.</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmitRequest} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Request Type</Label>
                <select
                  value={newRequest.request_type}
                  onChange={(e) => setNewRequest({...newRequest, request_type: e.target.value})}
                  className="w-full mt-1 h-10 px-3 bg-zinc-800 border border-zinc-700 rounded-md text-white"
                >
                  <option value="question">❓ Question</option>
                  <option value="bug_report">🐛 Bug Report</option>
                  <option value="feature_request">💡 Feature Request</option>
                  <option value="maintenance">🔧 Maintenance Request</option>
                  <option value="other">📝 Other</option>
                </select>
              </div>
              <div>
                <Label>Priority</Label>
                <select
                  value={newRequest.priority}
                  onChange={(e) => setNewRequest({...newRequest, priority: e.target.value})}
                  className="w-full mt-1 h-10 px-3 bg-zinc-800 border border-zinc-700 rounded-md text-white"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">🚨 Urgent</option>
                </select>
              </div>
            </div>
            
            <div>
              <Label>Subject *</Label>
              <Input
                value={newRequest.subject}
                onChange={(e) => setNewRequest({...newRequest, subject: e.target.value})}
                placeholder="Brief summary of your issue"
                className="mt-1 bg-zinc-800 border-zinc-700"
                required
              />
            </div>
            
            <div>
              <Label>Description *</Label>
              <textarea
                value={newRequest.description}
                onChange={(e) => setNewRequest({...newRequest, description: e.target.value})}
                placeholder="Please describe your issue or question in detail..."
                rows={5}
                className="w-full mt-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                required
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewRequestDialog(false)} className="border-zinc-600">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-primary text-black font-bold">
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Admin Response Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-[95vw] sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">Respond to Request</DialogTitle>
            <DialogDescription>{selectedRequest?.subject}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-zinc-800/50 p-4 rounded-lg">
              <p className="text-zinc-300 text-sm">{selectedRequest?.description}</p>
              <p className="text-xs text-zinc-500 mt-2">From: {selectedRequest?.user_name} ({selectedRequest?.company_name})</p>
            </div>
            
            <div>
              <Label>Your Response</Label>
              <textarea
                value={adminResponse}
                onChange={(e) => setAdminResponse(e.target.value)}
                placeholder="Type your response..."
                rows={4}
                className="w-full mt-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>
            
            <div>
              <Label>Update Status</Label>
              <select
                value={responseStatus}
                onChange={(e) => setResponseStatus(e.target.value)}
                className="w-full mt-1 h-10 px-3 bg-zinc-800 border border-zinc-700 rounded-md text-white"
              >
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedRequest(null)} className="border-zinc-600">
                Cancel
              </Button>
              <Button onClick={handleRespond} className="bg-primary text-black font-bold">
                Send Response
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============== REMINDERS PAGE ==============
function FutureIdeasPage() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIdea, setEditingIdea] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [buildDialogOpen, setBuildDialogOpen] = useState(false);
  const [buildRequest, setBuildRequest] = useState("");
  const [buildingIdea, setBuildingIdea] = useState(null);
  
  const [form, setForm] = useState({
    title: "", description: "", category: "feature", priority: "medium", status: "idea"
  });

  const categories = [
    { value: "feature", label: "New Feature", icon: Sparkles, color: "text-purple-400" },
    { value: "improvement", label: "Improvement", icon: TrendingUp, color: "text-blue-400" },
    { value: "integration", label: "Integration", icon: Zap, color: "text-yellow-400" },
    { value: "ui-ux", label: "UI/UX", icon: Pencil, color: "text-pink-400" },
    { value: "automation", label: "Automation", icon: RefreshCw, color: "text-green-400" },
    { value: "other", label: "Other", icon: Lightbulb, color: "text-zinc-400" }
  ];

  const priorities = [
    { value: "low", label: "Low", color: "bg-zinc-600" },
    { value: "medium", label: "Medium", color: "bg-blue-600" },
    { value: "high", label: "High", color: "bg-orange-600" },
    { value: "critical", label: "Critical", color: "bg-red-600" }
  ];

  const statuses = [
    { value: "idea", label: "💡 Idea", color: "bg-zinc-700" },
    { value: "planned", label: "📋 Planned", color: "bg-blue-700" },
    { value: "in-progress", label: "🚧 In Progress", color: "bg-yellow-700" },
    { value: "completed", label: "✅ Completed", color: "bg-green-700" },
    { value: "rejected", label: "❌ Rejected", color: "bg-red-700/50" }
  ];

  useEffect(() => {
    fetchIdeas();
  }, []);

  const fetchIdeas = async () => {
    try {
      const res = await apiClient.get('/future-ideas');
      setIdeas(res.data);
    } catch (err) {
      console.log('Failed to fetch ideas');
    }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({ title: "", description: "", category: "feature", priority: "medium", status: "idea" });
    setEditingIdea(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingIdea) {
        await apiClient.put(`/future-ideas/${editingIdea.id}`, form);
        toast.success('Idea updated!');
      } else {
        await apiClient.post('/future-ideas', form);
        toast.success('Idea added!');
      }
      fetchIdeas();
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error('Failed to save idea');
    }
  };

  const handleEdit = (idea) => {
    setForm({
      title: idea.title || "",
      description: idea.description || "",
      category: idea.category || "feature",
      priority: idea.priority || "medium",
      status: idea.status || "idea"
    });
    setEditingIdea(idea);
    setDialogOpen(true);
  };

  const handleDelete = async (ideaId) => {
    if (!confirm('Delete this idea?')) return;
    try {
      await apiClient.delete(`/future-ideas/${ideaId}`);
      toast.success('Idea deleted');
      fetchIdeas();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleVote = async (ideaId) => {
    try {
      await apiClient.put(`/future-ideas/${ideaId}/vote`);
      fetchIdeas();
      toast.success('Vote recorded!');
    } catch (err) {
      toast.error('Failed to vote');
    }
  };

  const handleExport = async () => {
    try {
      const res = await apiClient.get('/future-ideas/export');
      const dataStr = JSON.stringify(res.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `future_ideas_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Exported ${res.data.count} ideas`);
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const res = await apiClient.post('/future-ideas/import', data);
      toast.success(res.data.message);
      fetchIdeas();
    } catch (err) {
      toast.error('Import failed - invalid file format');
    }
    e.target.value = '';
  };

  const handleSendToBuild = (idea) => {
    const categoryInfo = getCategoryInfo(idea.category);
    const priorityInfo = getPriorityInfo(idea.priority);
    
    // Format the idea as a build request
    const request = `Please implement this feature:

**${idea.title}**

${idea.description || 'No description provided.'}

Category: ${categoryInfo.label}
Priority: ${priorityInfo.label}`;
    
    setBuildRequest(request);
    setBuildingIdea(idea);
    setBuildDialogOpen(true);
    
    // Try to copy to clipboard
    navigator.clipboard.writeText(request).then(() => {
      toast.success('Request copied to clipboard!');
    }).catch(() => {
      // Silent fail - user can copy from dialog
    });
  };

  const handleCopyBuildRequest = () => {
    navigator.clipboard.writeText(buildRequest).then(() => {
      toast.success('Copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy. Select the text and copy manually.');
    });
  };

  // Quick copy — just copies the formatted idea to the clipboard without
  // opening the build dialog or changing the idea's status. Useful for pasting
  // ideas into the preview chat so they can be implemented later.
  const handleQuickCopy = (idea) => {
    const categoryInfo = getCategoryInfo(idea.category);
    const priorityInfo = getPriorityInfo(idea.priority);
    const text = `**${idea.title}**\n\n${idea.description || 'No description provided.'}\n\nCategory: ${categoryInfo.label}\nPriority: ${priorityInfo.label}`;
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`"${idea.title}" copied — paste it into the preview to build.`);
    }).catch(() => {
      toast.error('Copy failed — try the Build button instead.');
    });
  };

  // Copy every currently-filtered idea to the clipboard as a single formatted
  // block. Helpful for a "plan-the-session" workflow.
  const handleCopyAll = () => {
    if (filteredIdeas.length === 0) {
      toast.error('No ideas to copy.');
      return;
    }
    const lines = filteredIdeas.map((idea, i) => {
      const categoryInfo = getCategoryInfo(idea.category);
      const priorityInfo = getPriorityInfo(idea.priority);
      const statusInfo = getStatusInfo(idea.status);
      return `${i + 1}. **${idea.title}** [${priorityInfo.label} · ${categoryInfo.label} · ${statusInfo.label}]\n   ${idea.description || '(no description)'}`;
    });
    const text = `Future Ideas (${filteredIdeas.length} item${filteredIdeas.length === 1 ? '' : 's'}):\n\n${lines.join('\n\n')}`;
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${filteredIdeas.length} idea${filteredIdeas.length === 1 ? '' : 's'} copied to clipboard.`);
    }).catch(() => {
      toast.error('Copy failed.');
    });
  };

  const handleMarkAsPlanned = async () => {
    if (buildingIdea && buildingIdea.status === 'idea') {
      try {
        await apiClient.put(`/future-ideas/${buildingIdea.id}`, {
          ...buildingIdea,
          status: 'planned'
        });
        fetchIdeas();
        toast.success('Status updated to Planned');
      } catch (err) {
        console.log('Failed to update status');
      }
    }
    setBuildDialogOpen(false);
  };

  const filteredIdeas = ideas.filter(idea => {
    if (filterStatus !== "all" && idea.status !== filterStatus) return false;
    if (filterCategory !== "all" && idea.category !== filterCategory) return false;
    return true;
  });

  const getCategoryInfo = (cat) => categories.find(c => c.value === cat) || categories[5];
  const getPriorityInfo = (pri) => priorities.find(p => p.value === pri) || priorities[1];
  const getStatusInfo = (st) => statuses.find(s => s.value === st) || statuses[0];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="future-ideas-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight flex items-center gap-3">
            <Lightbulb className="w-8 h-8 text-primary" /> FUTURE IDEAS
          </h1>
          <p className="text-zinc-400 mt-1">Track feature ideas, improvements, and future additions</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleCopyAll} className="border-zinc-600 text-zinc-300" title="Copy all filtered ideas to clipboard" data-testid="copy-all-ideas-btn">
            <Copy className="w-4 h-4 mr-2" /> Copy All
          </Button>
          <Button variant="outline" onClick={handleExport} className="border-zinc-600 text-zinc-300" title="Export ideas for backup">
            <ArrowUp className="w-4 h-4 mr-2" /> Export
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept=".json" onChange={handleImport} className="hidden" />
            <Button variant="outline" className="border-zinc-600 text-zinc-300" title="Import ideas from backup" asChild>
              <span><ArrowDown className="w-4 h-4 mr-2" /> Import</span>
            </Button>
          </label>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-primary text-black hover:bg-primary/90" data-testid="add-idea-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Idea
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        {statuses.map(status => {
          const count = ideas.filter(i => i.status === status.value).length;
          return (
            <Card key={status.value} className="bg-zinc-900 border-zinc-700">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-white">{count}</p>
                <p className="text-xs text-zinc-400">{status.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {statuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-zinc-500">{filteredIdeas.length} ideas</span>
      </div>

      {/* Ideas List */}
      {filteredIdeas.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-700">
          <CardContent className="py-12 text-center">
            <Lightbulb className="w-16 h-16 mx-auto text-zinc-600 mb-4" />
            <h3 className="text-xl font-medium text-zinc-400 mb-2">No ideas yet</h3>
            <p className="text-zinc-500 mb-4">Start capturing your feature ideas and improvements</p>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-primary text-black">
              <Plus className="w-4 h-4 mr-2" /> Add Your First Idea
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredIdeas.map(idea => {
            const categoryInfo = getCategoryInfo(idea.category);
            const priorityInfo = getPriorityInfo(idea.priority);
            const statusInfo = getStatusInfo(idea.status);
            const CategoryIcon = categoryInfo.icon;
            
            return (
              <Card key={idea.id} className="bg-zinc-900 border-zinc-700 hover:border-zinc-600 transition-colors" data-testid={`idea-${idea.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Vote Button */}
                    <div className="flex flex-col items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleVote(idea.id)}
                        className="h-8 w-8 p-0 text-zinc-400 hover:text-primary hover:bg-primary/10"
                      >
                        <ArrowUp className="w-5 h-5" />
                      </Button>
                      <span className="text-sm font-bold text-primary">{idea.votes || 0}</span>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <CategoryIcon className={`w-4 h-4 ${categoryInfo.color}`} />
                        <h3 className="font-medium text-white">{idea.title}</h3>
                        <span className={`px-2 py-0.5 rounded text-xs ${priorityInfo.color} text-white`}>
                          {priorityInfo.label}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs ${statusInfo.color} text-white`}>
                          {statusInfo.label}
                        </span>
                      </div>
                      {idea.description && (
                        <p className="text-sm text-zinc-400 line-clamp-2">{idea.description}</p>
                      )}
                      <p className="text-xs text-zinc-600 mt-2">
                        Added {new Date(idea.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickCopy(idea)}
                        className="h-8 px-3 border-zinc-600 text-zinc-300 hover:bg-zinc-800 hover:text-white"
                        title="Copy formatted idea to clipboard"
                        data-testid={`copy-idea-${idea.id}`}
                      >
                        <Copy className="w-4 h-4 mr-1" /> Copy
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleSendToBuild(idea)} 
                        className="h-8 px-3 border-primary/50 text-primary hover:bg-primary/10 hover:text-primary"
                        title="Send to build - copy as request and mark as Planned"
                      >
                        <Send className="w-4 h-4 mr-1" /> Build
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(idea)} className="h-8 w-8 text-zinc-400 hover:text-white">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(idea.id)} className="h-8 w-8 text-zinc-400 hover:text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-[95vw] sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-primary" />
              {editingIdea ? 'Edit Idea' : 'Add New Idea'}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Capture your feature ideas, improvements, and future additions
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-zinc-200">Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 bg-zinc-800 border-zinc-600 text-white"
                placeholder="e.g., Material Price History Tracker"
                required
              />
            </div>
            <div>
              <Label className="text-zinc-200">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1 bg-zinc-800 border-zinc-600 text-white"
                rows={3}
                placeholder="Describe the feature, why it's useful, and any implementation ideas..."
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-200">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-200">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-200">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="border-zinc-600" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary text-black hover:bg-primary/90">
                {editingIdea ? 'Update Idea' : 'Add Idea'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Build Request Dialog */}
      <Dialog open={buildDialogOpen} onOpenChange={setBuildDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Send to Build
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Copy this request and paste it in the chat to start building
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
              <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-sans">{buildRequest}</pre>
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Lightbulb className="w-4 h-4" />
              <span>Paste this in the Emergent chat to implement the feature</span>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="border-zinc-600" onClick={() => setBuildDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="outline" className="border-zinc-600" onClick={handleCopyBuildRequest}>
              <Copy className="w-4 h-4 mr-2" /> Copy
            </Button>
            <Button className="bg-primary text-black hover:bg-primary/90" onClick={handleMarkAsPlanned}>
              <Check className="w-4 h-4 mr-2" /> Mark as Planned & Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Customer Proposal Portal (Public Page - No Auth Required)
function CustomerProposalPortal() {
  const { token } = useParams();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [signature, setSignature] = useState(null);
  const [question, setQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  useEffect(() => {
    fetchProposal();
  }, [token]);

  const fetchProposal = async () => {
    try {
      const res = await axios.get(`${API}/portal/proposal/${token}`);
      setProposal(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Proposal not found or has expired");
    }
    setLoading(false);
  };

  const handleAction = async (action, message = null, signatureData = null) => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/portal/proposal/${token}/action`, {
        action,
        message: action === 'question' ? question : message,
        signature: signatureData
      });
      
      if (action === 'accept') {
        toast.success("Proposal accepted and signed!");
        setShowSignatureDialog(false);
        // If deposit required, redirect to payment
        if (res.data.deposit_required && res.data.deposit_amount > 0) {
          await initiatePayment();
        }
      } else if (action === 'decline') {
        toast.info("Proposal declined");
      } else if (action === 'question') {
        toast.success("Question submitted!");
        setShowQuestionDialog(false);
        setQuestion('');
      }
      
      fetchProposal(); // Refresh
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
    setSubmitting(false);
  };

  const handleAcceptWithSignature = () => {
    if (!signature) {
      toast.error("Please sign the proposal before accepting");
      return;
    }
    handleAction('accept', null, signature);
  };

  const initiatePayment = async () => {
    setPaymentProcessing(true);
    try {
      const res = await axios.post(`${API}/portal/proposal/${token}/deposit/checkout`);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate payment");
      setPaymentProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="bg-zinc-900 border-red-500/30 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-bold text-white mb-2">Proposal Not Found</h1>
            <p className="text-zinc-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isExpired = proposal.status === 'expired';
  const isAccepted = proposal.status === 'accepted';
  const isDeclined = proposal.status === 'declined';
  const canAct = !isExpired && !isAccepted && !isDeclined;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="customer-portal">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 py-4 px-6 print:hidden">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_crewsync-business/artifacts/srw9khxz_CrewSync%20Pro%20Logo.png" alt="CrewSync Pro" className="h-14 w-auto" />
            <span className="text-[10px] text-zinc-500 align-top ml-0.5">™</span>
            <div>
              <h1 className="font-heading font-bold text-white uppercase">{proposal.company_name}</h1>
              <p className="text-xs text-zinc-400">Proposal Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint} className="text-zinc-300 border-zinc-600 hover:bg-zinc-800">
              <Printer className="w-4 h-4 mr-1" /> Print / Save PDF
            </Button>
            <Badge className={
              isAccepted ? "bg-green-500/20 text-green-400 border-green-500/30" :
              isDeclined ? "bg-red-500/20 text-red-400 border-red-500/30" :
              isExpired ? "bg-zinc-500/20 text-zinc-400 border-zinc-500/30" :
              "bg-primary/20 text-primary border-primary/30"
            }>
              {proposal.status === 'accepted' && proposal.deposit_paid ? 'Deposit Paid' : proposal.status.toUpperCase()}
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto py-8 px-4">
        {/* Status Banner */}
        {isAccepted && !proposal.deposit_paid && proposal.deposit_required && (
          <Card className="bg-zinc-900 border-yellow-500/50 mb-6">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-400" />
                <p className="text-yellow-200">Your proposal has been accepted! Please pay the deposit to confirm your booking.</p>
              </div>
              <Button onClick={initiatePayment} disabled={paymentProcessing} className="bg-primary text-black font-bold">
                {paymentProcessing ? 'Processing...' : `Pay $${proposal.deposit_amount?.toLocaleString()} Deposit`}
              </Button>
            </CardContent>
          </Card>
        )}

        {isAccepted && proposal.deposit_paid && (
          <Card className="bg-zinc-900 border-green-500/50 mb-6">
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-400" />
              <div>
                <p className="text-green-200 font-medium">Thank you! Your deposit has been received.</p>
                <p className="text-green-400/70 text-sm">We will be in touch shortly to schedule your project.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Proposal Details */}
        <Card className="bg-zinc-900 border-zinc-700 mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-2xl text-white">{proposal.title}</CardTitle>
            <p className="text-zinc-400">Prepared for: {proposal.customer_name}</p>
            {proposal.message && (
              <div className="mt-4 bg-zinc-800 p-4 rounded-lg border-l-4 border-primary">
                <p className="text-zinc-300 italic">"{proposal.message}"</p>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {/* What's Included Summary - No detailed breakdowns shown to customers */}
            <div className="bg-zinc-800 rounded-lg p-4">
              <h3 className="text-sm font-medium text-zinc-300 mb-3">This Proposal Includes:</h3>
              <div className="flex flex-wrap gap-3">
                {proposal.labor_items?.length > 0 && (
                  <span className="flex items-center gap-2 text-white bg-zinc-700 px-3 py-1.5 rounded-full text-sm">
                    <HardHat className="w-4 h-4 text-yellow-500" /> Professional Labor
                  </span>
                )}
                {proposal.material_items?.length > 0 && (
                  <span className="flex items-center gap-2 text-white bg-zinc-700 px-3 py-1.5 rounded-full text-sm">
                    <Wrench className="w-4 h-4 text-blue-500" /> Materials
                  </span>
                )}
                {proposal.subcontractor_items?.length > 0 && (
                  <span className="flex items-center gap-2 text-white bg-zinc-700 px-3 py-1.5 rounded-full text-sm">
                    <Truck className="w-4 h-4 text-green-500" /> Specialized Work
                  </span>
                )}
              </div>
            </div>

            {/* Total */}
            <div className="bg-zinc-800 p-6 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-xl text-zinc-300">Total Investment</span>
                <span className="text-4xl font-heading font-bold text-primary">${(proposal.estimate_total || proposal.recommended_price || 0).toLocaleString()}</span>
              </div>
              {(proposal.deposit_required || proposal.deposit_amount > 0) && (
                <div className="flex justify-between items-center mt-4 pt-4 border-t border-zinc-700">
                  <span className="text-zinc-400">Required Deposit ({proposal.deposit_percentage || 25}%)</span>
                  <span className="text-xl font-bold text-white">${(proposal.deposit_amount || 0).toLocaleString()}</span>
                </div>
              )}
            </div>

            {/* Valid Until */}
            {proposal.valid_until && (
              <p className="text-center text-zinc-500 text-sm">
                This proposal is valid until {new Date(proposal.valid_until).toLocaleDateString()}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Questions Section */}
        {proposal.questions?.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-700 mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" /> Questions & Answers
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {proposal.questions.map((q, i) => (
                <div key={i} className="bg-zinc-800 p-4 rounded-lg">
                  <p className="text-zinc-300">{q.message}</p>
                  <p className="text-xs text-zinc-500 mt-1">{new Date(q.asked_at).toLocaleString()}</p>
                  {q.answered && (
                    <div className="mt-3 pl-4 border-l-2 border-primary">
                      <p className="text-zinc-200">{q.answer}</p>
                      <p className="text-xs text-zinc-500 mt-1">{new Date(q.answered_at).toLocaleString()}</p>
                    </div>
                  )}
                  {!q.answered && (
                    <p className="text-xs text-yellow-400 mt-2">Awaiting response...</p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        {canAct && (
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => setShowSignatureDialog(true)} 
              disabled={submitting}
              className="bg-green-600 hover:bg-green-500 text-white font-heading font-bold text-lg px-8"
              data-testid="accept-proposal-btn"
            >
              <Check className="w-5 h-5 mr-2" /> Accept & Sign Proposal
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => setShowQuestionDialog(true)}
              className="border-primary text-primary hover:bg-primary/10 font-heading font-bold text-lg px-8"
              data-testid="question-btn"
            >
              <HelpCircle className="w-5 h-5 mr-2" /> Ask a Question
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => handleAction('decline')}
              disabled={submitting}
              className="border-red-500 text-red-400 hover:bg-red-500/10 font-heading font-bold text-lg px-8"
              data-testid="decline-proposal-btn"
            >
              <XCircle className="w-5 h-5 mr-2" /> Decline
            </Button>
          </div>
        )}

        {/* Show Signature if already signed */}
        {proposal.signature && (
          <Card className="bg-zinc-900 border-green-500/30 mt-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-green-400 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" /> Signed & Accepted
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-zinc-800 rounded-lg p-4">
                <img src={proposal.signature} alt="Customer Signature" className="max-h-32 mx-auto" />
                <p className="text-center text-xs text-zinc-500 mt-2">
                  Signed on {proposal.signed_at ? new Date(proposal.signed_at).toLocaleString() : 'N/A'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Signature Dialog */}
      <Dialog open={showSignatureDialog} onOpenChange={setShowSignatureDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">Sign to Accept Proposal</DialogTitle>
            <DialogDescription className="text-zinc-400">
              By signing below, you agree to the terms and pricing outlined in this proposal.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <SignaturePad 
              onSave={(sig) => setSignature(sig)} 
              onClear={() => setSignature(null)}
            />
          </div>
          <div className="bg-zinc-800 p-4 rounded-lg">
            <p className="text-sm text-zinc-400">
              <strong className="text-white">Total:</strong> ${(proposal?.estimate_total || proposal?.recommended_price || 0).toLocaleString()}
            </p>
            {(proposal?.deposit_required || proposal?.deposit_amount > 0) && (
              <p className="text-sm text-zinc-400 mt-1">
                <strong className="text-white">Deposit Due:</strong> ${(proposal?.deposit_amount || 0).toLocaleString()}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-zinc-600" onClick={() => setShowSignatureDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAcceptWithSignature} 
              disabled={!signature || submitting} 
              className="bg-green-600 hover:bg-green-500 text-white"
            >
              {submitting ? 'Processing...' : 'Accept & Sign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Question Dialog */}
      <Dialog open={showQuestionDialog} onOpenChange={setShowQuestionDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white">Ask a Question</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Have a question about this proposal? We'll get back to you as soon as possible.
            </DialogDescription>
          </DialogHeader>
          <Textarea 
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Type your question here..."
            className="bg-zinc-800 border-zinc-600 text-white"
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" className="border-zinc-600" onClick={() => setShowQuestionDialog(false)}>Cancel</Button>
            <Button onClick={() => handleAction('question')} disabled={!question.trim() || submitting} className="bg-primary text-black">
              {submitting ? 'Sending...' : 'Send Question'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-zinc-900 border-t border-zinc-800 py-6 mt-12">
        <div className="max-w-4xl mx-auto text-center flex flex-col items-center gap-2">
          <div className="flex items-center gap-1">
            <img src="https://customer-assets.emergentagent.com/job_crewsync-business/artifacts/srw9khxz_CrewSync%20Pro%20Logo.png" alt="CrewSync Pro" className="h-8 w-auto opacity-60" />
            <span className="text-[8px] text-zinc-500 opacity-60 align-top">™</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Payment Success Page
function ProposalPaymentSuccess() {
  const { token } = useParams();
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const res = await axios.get(`${API}/portal/proposal/${token}/deposit/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setStatus('success');
      } else {
        setStatus('pending');
      }
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <Card className="bg-zinc-900 border-zinc-700 max-w-md w-full">
        <CardContent className="p-8 text-center">
          <div className="flex items-center justify-center gap-1 mb-6">
            <img src="https://customer-assets.emergentagent.com/job_crewsync-business/artifacts/srw9khxz_CrewSync%20Pro%20Logo.png" alt="CrewSync Pro" className="h-12 w-auto" />
            <span className="text-[10px] text-zinc-500 align-top">™</span>
          </div>
          {status === 'checking' && (
            <>
              <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-zinc-400">Verifying your payment...</p>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold text-white mb-2">Payment Successful!</h1>
              <p className="text-zinc-400 mb-6">Your deposit has been received. We will be in touch shortly.</p>
              <Button onClick={() => window.location.href = `/proposal/${token}`} className="bg-primary text-black font-bold">
                View Proposal
              </Button>
            </>
          )}
          {status === 'pending' && (
            <>
              <Clock className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold text-white mb-2">Payment Processing</h1>
              <p className="text-zinc-400 mb-6">Your payment is being processed. Please check back shortly.</p>
              <Button onClick={checkPaymentStatus} className="bg-primary text-black font-bold">
                Check Status
              </Button>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold text-white mb-2">Something Went Wrong</h1>
              <p className="text-zinc-400 mb-6">We couldn't verify your payment. Please contact us for assistance.</p>
              <Button onClick={() => window.location.href = `/proposal/${token}`} className="bg-primary text-black font-bold">
                Return to Proposal
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Job Confirmation Page (Public - accessed via email link)
// Estimate Approval Page (Public - for customer approval)
function EstimateApprovalPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [expiredInfo, setExpiredInfo] = useState(null);
  const [estimateData, setEstimateData] = useState(null);
  const [signatureType, setSignatureType] = useState('typed');
  const [typedSignature, setTypedSignature] = useState('');
  const [drawnSignature, setDrawnSignature] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [customerName, setCustomerName] = useState('');
  // Feb 2026 — how the customer wants to pay the deposit when one is
  // required. 'stripe' launches online checkout (temporarily hidden
  // until the tenant finishes Stripe onboarding — see UI below),
  // 'mail_check' and 'on_site' finalize the approval immediately and
  // leave the deposit as "pending" on the job so the contractor can
  // log the payment manually when the check lands or the crew
  // collects on-arrival. Default is 'mail_check' while Stripe is
  // disabled so the signing flow still works.
  const [depositMethod, setDepositMethod] = useState('mail_check');
  const [submitting, setSubmitting] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  // Feb 2026 — click-to-zoom lightbox for takeoff snapshots so customers
  // can inspect the shaded measured areas before signing.
  const [snapshotZoom, setSnapshotZoom] = useState(null); // snapshot obj or null
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    fetchEstimate();
    
    // Check for payment status from Stripe redirect
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      toast.success("Payment successful! Your estimate is now approved.");
    } else if (paymentStatus === 'cancelled') {
      toast.error("Payment was cancelled. Please try again to complete your approval.");
    }

    // If the customer arrived here via the "Approve" / "Decline" button
    // inside their email, the backend redirects to this page with an
    // `action=` hint. Surface a small toast + smooth-scroll to the
    // signature area so it's obvious they still need to sign before the
    // approval is finalized. This is deliberate — GET-based auto-approve
    // was removed because email security scanners were prefetching the
    // link and silently accepting estimates on the customer's behalf.
    const action = searchParams.get('action');
    if (action === 'approve') {
      // Slight delay so the estimate data has time to render.
      setTimeout(() => {
        const el = document.getElementById('signature-block');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        toast.info('Please sign below to finish approving your estimate.');
      }, 500);
    } else if (action === 'decline') {
      setTimeout(() => {
        toast.info('You can decline this estimate using the button at the bottom of the page.');
      }, 500);
    }
  }, [token, searchParams]);

  const fetchEstimate = async () => {
    try {
      const res = await axios.get(`${API}/public/estimate-approval/${token}`);
      setEstimateData(res.data);
      setCustomerName(res.data.customer_name || '');
      
      if (res.data.status === 'approved' || res.data.status === 'declined') {
        setStatus(res.data.status);
      } else {
        setStatus('ready');
      }
    } catch (err) {
      console.error('Error fetching estimate:', err);
      // 410 = the 90-day customer link window has passed. Show the company's
      // contact details so they can ask for a fresh copy (the company also
      // gets a bell notification from the backend).
      if (err.response?.status === 410) {
        setExpiredInfo(err.response.data?.detail || {});
        setStatus('expired');
        return;
      }
      setStatus('error');
    }
  };

  // Canvas drawing — pointer events, not touch events.
  //
  // Jun 2026 fix (customer report: "while trying to use the signature box
  // it moves the entire screen up and down"): the canvas had no
  // `touch-action`, so on a phone every finger drag was treated as a page
  // scroll. React also attaches onTouchMove as a PASSIVE listener, so
  // calling preventDefault there is a no-op. Pointer events plus
  // `touch-action: none` on the element is the combination that actually
  // stops the page moving, and it covers mouse, finger and stylus in one
  // code path.
  //
  // Second bug fixed here: the canvas is 500x150 internally but stretches
  // to the card width, so raw client coords drew the line offset from
  // where the finger actually was. Coordinates are now scaled.
  const getCanvasPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasPos(e);
    // Keep receiving moves even if the finger slides outside the box.
    try { canvas.setPointerCapture(e.pointerId); } catch (_) { /* older browsers */ }
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasPos(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#FFD000';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (isDrawing) {
      const canvas = canvasRef.current;
      if (e?.pointerId !== undefined) {
        try { canvas.releasePointerCapture(e.pointerId); } catch (_) { /* no-op */ }
      }
      setDrawnSignature(canvas.toDataURL());
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setDrawnSignature('');
  };

  const handleApprove = async () => {
    if (!agreedToTerms) {
      toast.error("Please agree to the terms and conditions");
      return;
    }
    
    const signatureData = signatureType === 'typed' ? typedSignature : drawnSignature;
    if (!signatureData) {
      toast.error("Please provide your signature");
      return;
    }
    
    if (!customerName.trim()) {
      toast.error("Please enter your name");
      return;
    }

    // Explicit final confirmation before locking in acceptance. The
    // previous flow submitted immediately on button tap — with signature
    // + name + terms already filled, one accidental tap on a phone would
    // approve the estimate. This modal blocks that entire class of
    // "oops I just tapped it" mistakes while adding only one extra tap
    // for genuine acceptances.
    const total = estimateData?.estimate?.total_price ?? estimateData?.estimate?.total ?? 0;
    const depositAmt = Number(estimateData?.deposit_amount || 0);
    let depositMsg = '';
    if (estimateData?.require_deposit) {
      if (depositMethod === 'stripe') {
        depositMsg = `\n\nYou will be charged a $${depositAmt.toLocaleString()} deposit immediately after tapping OK.`;
      } else if (depositMethod === 'mail_check') {
        depositMsg = `\n\nYou've selected to mail a $${depositAmt.toLocaleString()} deposit check. The contractor will follow up with the mailing address.`;
      } else if (depositMethod === 'on_site') {
        depositMsg = `\n\nYou've selected to pay the $${depositAmt.toLocaleString()} deposit on-site when the crew arrives.`;
      }
    }
    const ok = window.confirm(
      `Approve this estimate for $${Number(total).toLocaleString()}?\n\n` +
      `By tapping OK you are digitally signing this estimate and authorizing the work to proceed.${depositMsg}\n\n` +
      `Tap Cancel if you're not ready to sign.`
    );
    if (!ok) return;
    
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/estimate-approval/${token}/approve`, {
        signature_type: signatureType,
        signature_data: signatureData,
        agreed_to_terms: agreedToTerms,
        customer_name: customerName,
        deposit_method: estimateData?.require_deposit ? depositMethod : undefined,
      });
      
      if (res.data.status === 'payment_required') {
        // Redirect to Stripe checkout
        window.location.href = res.data.checkout_url;
      } else {
        if (res.data.deposit_pending) {
          toast.success(
            depositMethod === 'mail_check'
              ? 'Estimate approved! Please mail your deposit when ready.'
              : 'Estimate approved! Our crew will collect the deposit on-site.'
          );
        } else {
          toast.success("Estimate approved successfully!");
        }
        setStatus('approved');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve estimate");
    }
    setSubmitting(false);
  };

  const handleDecline = async () => {
    if (!window.confirm("Are you sure you want to decline this estimate?")) return;
    
    const reason = prompt("Please let us know why you're declining (optional):");
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/estimate-approval/${token}/decline`, null, {
        params: { reason }
      });
      toast.success("Estimate declined");
      setStatus('declined');
    } catch (err) {
      toast.error("Failed to decline estimate");
    }
    setSubmitting(false);
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="animate-spin w-12 h-12 border-2 border-yellow-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-zinc-900 border-zinc-700">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Link Invalid or Expired</h1>
            <p className="text-zinc-400">This estimate link is no longer valid. Please contact the company for a new estimate.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-zinc-900 border-zinc-700" data-testid="estimate-link-expired">
          <CardContent className="p-8 text-center">
            <Clock className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">This link has expired</h1>
            <p className="text-zinc-400 mb-5">
              {expiredInfo?.message || 'Please get in touch and we\'ll send you an up-to-date copy.'}
            </p>
            {(expiredInfo?.company_phone || expiredInfo?.company_email) && (
              <div className="space-y-2 text-sm">
                {expiredInfo.company_phone && (
                  <a href={`tel:${expiredInfo.company_phone}`}
                    className="flex items-center justify-center gap-2 text-primary font-semibold hover:underline"
                    data-testid="expired-link-phone">
                    <Phone className="w-4 h-4" /> {expiredInfo.company_phone}
                  </a>
                )}
                {expiredInfo.company_email && (
                  <a href={`mailto:${expiredInfo.company_email}`}
                    className="flex items-center justify-center gap-2 text-primary font-semibold hover:underline"
                    data-testid="expired-link-email">
                    <Mail className="w-4 h-4" /> {expiredInfo.company_email}
                  </a>
                )}
              </div>
            )}
            {expiredInfo?.company_name && (
              <p className="text-zinc-500 text-xs mt-5">— {expiredInfo.company_name}</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'approved') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-zinc-900 border-zinc-700">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Estimate Approved!</h1>
            <p className="text-zinc-400 mb-4">Thank you for approving this estimate. The team will be in touch soon to schedule your project.</p>
            {estimateData?.company_name && (
              <p className="text-zinc-500 text-sm">— {estimateData.company_name}</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'declined') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-zinc-900 border-zinc-700">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-zinc-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Estimate Declined</h1>
            <p className="text-zinc-400">This estimate has been declined. If you change your mind, please contact us for a new estimate.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-zinc-950 py-8 px-4" data-testid="estimate-approval-page">
      <div className="max-w-2xl mx-auto">
        {/* Header with Company Branding */}
        <div className="text-center mb-8">
          {estimateData?.company_logo && (
            <div className="flex flex-col items-center">
              <img src={estimateData.company_logo} alt={estimateData.company_name} className="h-28 md:h-32 max-w-[420px] mx-auto mb-2 object-contain" />
              <span className="text-[10px] text-zinc-500">Powered by <span className="text-primary font-semibold">CrewSync Pro</span>™</span>
            </div>
          )}
          <h1 className="text-2xl font-bold text-yellow-500 mt-2">{estimateData?.company_name || 'CrewSync Pro'}</h1>
          <div className="mt-4 flex justify-center gap-2 print:hidden">
            <Button variant="outline" size="sm" onClick={handlePrint} className="text-zinc-300 border-zinc-600 hover:bg-zinc-800">
              <Printer className="w-4 h-4 mr-1" /> Print / Save PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.open(`${API}/public/estimate-approval/${token}/pdf`, '_blank')} className="text-zinc-300 border-zinc-600 hover:bg-zinc-800">
              <Download className="w-4 h-4 mr-1" /> Download PDF
            </Button>
          </div>
        </div>

        {/* Confidentiality Notice */}
        <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <p className="text-zinc-400 text-sm">{estimateData?.confidentiality_notice}</p>
          </div>
        </div>

        {/* Estimate Details */}
        <Card className="bg-zinc-900 border-zinc-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white text-xl">{estimateData?.estimate?.title}</CardTitle>
            {estimateData?.estimate?.description && (
              <CardDescription className="text-zinc-400">{estimateData?.estimate?.description}</CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {/* What's Included - All line items without individual pricing */}
            <div className="bg-zinc-800 rounded-lg p-4">
              <h3 className="text-base font-semibold text-white mb-4">What's Included in This Estimate:</h3>
              
              {/* Labor/Services - shows description + customer-facing note (process_details). Hours/rates/costs are intentionally hidden. */}
              {estimateData?.estimate?.labor_items?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm uppercase tracking-wide text-zinc-300 font-semibold mb-2 flex items-center gap-2">
                    <HardHat className="w-4 h-4 text-yellow-500" /> Services & Labor
                  </h4>
                  <ul className="space-y-3">
                    {estimateData.estimate.labor_items.map((item, idx) => (
                      <li key={idx}>
                        <div className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0" />
                          <span className="text-white font-semibold text-base">{item.description || item.name || 'Labor'}</span>
                        </div>
                        {(item.process_details || item.notes) && (
                          <p className="text-zinc-200 text-base ml-4 mt-1 leading-relaxed">{item.process_details || item.notes}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Fallback for legacy labor_descriptions */}
              {!estimateData?.estimate?.labor_items?.length && estimateData?.estimate?.labor_descriptions?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm uppercase tracking-wide text-zinc-300 font-semibold mb-2 flex items-center gap-2">
                    <HardHat className="w-4 h-4 text-yellow-500" /> Services & Labor
                  </h4>
                  <ul className="space-y-2">
                    {estimateData.estimate.labor_descriptions.map((desc, idx) => (
                      <li key={idx} className="text-white text-base flex items-start gap-2">
                        <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0" />
                        {desc}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Materials List - with names and notes */}
              {estimateData?.estimate?.material_items?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm uppercase tracking-wide text-zinc-300 font-semibold mb-2 flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-blue-500" /> Materials
                  </h4>
                  <ul className="space-y-3">
                    {estimateData.estimate.material_items.map((item, idx) => {
                      // Feb 2026 — Surface the quantity + unit the coverage
                      // calculator (or the admin) entered. Previously we
                      // rendered only `item.name || item` which meant
                      // customers saw "Bag of Sealer" instead of
                      // "12 bags × Sealer" and admins couldn't tell what
                      // the calculator had put on the estimate. Formatted
                      // "N unit × Name" only when qty > 0 so hand-typed
                      // rows without a quantity still render cleanly.
                      const _label = (typeof item === 'string')
                        ? item
                        : (item.description || item.name || 'Material');
                      const _qty = (typeof item === 'object') ? Number(item.quantity || 0) : 0;
                      const _unit = (typeof item === 'object') ? (item.unit || '') : '';
                      const _qtyLabel = _qty > 0
                        ? `${(_qty % 1 === 0) ? _qty : _qty.toFixed(2)}${_unit ? ' ' + _unit : ''}`
                        : '';
                      return (
                        <li key={idx}>
                          <div className="flex items-start gap-2">
                            <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                            <span className="text-white font-semibold text-base">
                              {_qtyLabel && (
                                <span className="text-blue-300 mr-1.5">{_qtyLabel} ×</span>
                              )}
                              {_label}
                            </span>
                          </div>
                          {(typeof item === 'object') && item.notes && (
                            <p className="text-zinc-200 text-base ml-4 mt-1 leading-relaxed">{item.notes}</p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {/* Fallback for legacy material_names */}
              {!estimateData?.estimate?.material_items?.length && estimateData?.estimate?.material_names?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm uppercase tracking-wide text-zinc-300 font-semibold mb-2 flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-blue-500" /> Materials
                  </h4>
                  <ul className="space-y-2">
                    {estimateData.estimate.material_names.map((name, idx) => (
                      <li key={idx} className="text-white text-base flex items-start gap-2">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                        {name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Equipment List - with names and notes */}
              {estimateData?.estimate?.equipment_items?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm uppercase tracking-wide text-zinc-300 font-semibold mb-2 flex items-center gap-2">
                    <Settings className="w-4 h-4 text-orange-500" /> Equipment
                  </h4>
                  <ul className="space-y-3">
                    {estimateData.estimate.equipment_items.map((item, idx) => (
                      <li key={idx}>
                        <div className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                          <span className="text-white font-semibold text-base">{item.name || item}</span>
                        </div>
                        {item.notes && (
                          <p className="text-zinc-200 text-base ml-4 mt-1 leading-relaxed">{item.notes}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Fallback for legacy equipment_names */}
              {!estimateData?.estimate?.equipment_items?.length && estimateData?.estimate?.equipment_names?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm uppercase tracking-wide text-zinc-300 font-semibold mb-2 flex items-center gap-2">
                    <Settings className="w-4 h-4 text-orange-500" /> Equipment
                  </h4>
                  <ul className="space-y-2">
                    {estimateData.estimate.equipment_names.map((name, idx) => (
                      <li key={idx} className="text-white text-base flex items-start gap-2">
                        <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                        {name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Subcontractor Work List - with names and notes */}
              {estimateData?.estimate?.subcontractor_items?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm uppercase tracking-wide text-zinc-300 font-semibold mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4 text-green-500" /> Specialized Work
                  </h4>
                  <ul className="space-y-3">
                    {estimateData.estimate.subcontractor_items.map((item, idx) => (
                      <li key={idx}>
                        <div className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                          <span className="text-white font-semibold text-base">{item.name || item}</span>
                        </div>
                        {item.notes && (
                          <p className="text-zinc-200 text-base ml-4 mt-1 leading-relaxed">{item.notes}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Fallback for legacy subcontractor_descriptions */}
              {!estimateData?.estimate?.subcontractor_items?.length && estimateData?.estimate?.subcontractor_descriptions?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm uppercase tracking-wide text-zinc-300 font-semibold mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4 text-green-500" /> Specialized Work
                  </h4>
                  <ul className="space-y-2">
                    {estimateData.estimate.subcontractor_descriptions.map((desc, idx) => (
                      <li key={idx} className="text-white text-base flex items-start gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                        {desc}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Other Items */}
              {estimateData?.estimate?.other_items_display?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm uppercase tracking-wide text-zinc-300 font-semibold mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-purple-500" /> Additional Items
                  </h4>
                  <ul className="space-y-3">
                    {estimateData.estimate.other_items_display.map((item, idx) => (
                      <li key={idx}>
                        <div className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0" />
                          <span className="text-white font-semibold text-base">{item.name || item}</span>
                        </div>
                        {item.notes && (
                          <p className="text-zinc-200 text-base ml-4 mt-1 leading-relaxed">{item.notes}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Fallback for legacy other_items */}
              {!estimateData?.estimate?.other_items_display?.length && estimateData?.estimate?.other_items?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm uppercase tracking-wide text-zinc-300 font-semibold mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-purple-500" /> Additional Items
                  </h4>
                  <ul className="space-y-2">
                    {estimateData.estimate.other_items.map((item, idx) => (
                      <li key={idx} className="text-white text-base flex items-start gap-2">
                        <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Show badges if no detailed items but flags indicate work included */}
              {!estimateData?.estimate?.labor_items?.length && 
               !estimateData?.estimate?.labor_descriptions?.length && 
               !estimateData?.estimate?.material_items?.length &&
               !estimateData?.estimate?.material_names?.length && 
               !estimateData?.estimate?.equipment_items?.length &&
               !estimateData?.estimate?.equipment_names?.length &&
               !estimateData?.estimate?.subcontractor_items?.length &&
               !estimateData?.estimate?.subcontractor_descriptions?.length && (
                <div className="flex flex-wrap gap-3">
                  {estimateData?.estimate?.includes_labor && (
                    <span className="flex items-center gap-2 text-white bg-zinc-700 px-3 py-1.5 rounded-full text-sm">
                      <HardHat className="w-4 h-4 text-yellow-500" /> Professional Labor
                    </span>
                  )}
                  {estimateData?.estimate?.includes_materials && (
                    <span className="flex items-center gap-2 text-white bg-zinc-700 px-3 py-1.5 rounded-full text-sm">
                      <Wrench className="w-4 h-4 text-blue-500" /> Materials Included
                    </span>
                  )}
                  {estimateData?.estimate?.includes_equipment && (
                    <span className="flex items-center gap-2 text-white bg-zinc-700 px-3 py-1.5 rounded-full text-sm">
                      <Settings className="w-4 h-4 text-orange-500" /> Equipment
                    </span>
                  )}
                  {estimateData?.estimate?.includes_subcontractors && (
                    <span className="flex items-center gap-2 text-white bg-zinc-700 px-3 py-1.5 rounded-full text-sm">
                      <Users className="w-4 h-4 text-green-500" /> Specialized Work
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Notes */}
            {estimateData?.estimate?.notes && (
              <div>
                <h3 className="text-base font-semibold text-white mb-2">Project Details</h3>
                <div className="text-zinc-100 text-base bg-zinc-800 p-4 rounded-lg">
                  <FormattedNotes text={estimateData.estimate.notes} />
                </div>
              </div>
            )}

            {/* Measured Areas — Please Verify (Feb 2026)
                Renders any takeoff shaded-area snapshots attached to
                the estimate. Customers can click any thumbnail to
                open a zoomed lightbox before approving. */}
            {estimateData?.estimate?.takeoff_snapshots?.length > 0 && (
              <div data-testid="approval-snapshots-section">
                <h3 className="text-base font-semibold text-white mb-2 flex items-center gap-2">
                  <Ruler className="w-4 h-4 text-primary" /> Measured Areas — Please Verify
                </h3>
                <p className="text-zinc-400 text-sm mb-3">
                  Click any image to zoom in and confirm we measured the correct areas.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {estimateData.estimate.takeoff_snapshots.map((snap) => (
                    <button
                      key={snap.id}
                      type="button"
                      onClick={() => setSnapshotZoom(snap)}
                      className="group relative bg-zinc-800 border border-zinc-700 rounded-lg overflow-hidden hover:border-primary transition text-left"
                      data-testid={`approval-snapshot-${snap.id}`}
                      title="Click to zoom"
                    >
                      <div className="aspect-[4/3] bg-zinc-900 flex items-center justify-center">
                        <img
                          src={`${BACKEND_URL}${snap.url}`}
                          alt={snap.caption || 'Measured area'}
                          loading="lazy"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <ZoomIn className="w-8 h-8 text-white drop-shadow" />
                      </div>
                      <div className="p-2">
                        <p className="text-white text-sm font-medium line-clamp-1">{snap.caption || 'Measured area'}</p>
                        <p className="text-[11px] text-zinc-400 mt-0.5">
                          {snap.total_sqft ? `${(snap.total_sqft || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} sqft` : ''}
                          {snap.total_sqft && snap.total_linear_ft ? ' · ' : ''}
                          {snap.total_linear_ft ? `${(snap.total_linear_ft || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} lft` : ''}
                          {snap.scale_text ? ` · ${snap.scale_text}` : (snap.scale_ft_per_inch ? ` · 1 in = ${snap.scale_ft_per_inch} ft` : '')}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Total Price */}
            <div className="border-t border-zinc-700 pt-4">
              <div className="flex justify-between items-center">
                <span className="text-lg text-zinc-400">Total Price</span>
                <span className="text-3xl font-bold text-green-400">${estimateData?.estimate?.total_price?.toLocaleString()}</span>
              </div>
              {estimateData?.estimate?.valid_until && (
                <p className="text-zinc-500 text-sm mt-2">Valid until: {new Date(estimateData.estimate.valid_until).toLocaleDateString()}</p>
              )}
            </div>

            {/* Deposit Info */}
            {estimateData?.require_deposit && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-400 font-medium">Deposit Required: ${estimateData.deposit_amount?.toLocaleString()}</p>
                <p className="text-zinc-400 text-sm mt-1">A deposit is required to confirm this estimate.</p>
              </div>
            )}

            {/* Save as PDF + Print — same customer-facing document that the
                admin sees, so the print output matches the PDF exactly. */}
            <div className="pt-4 border-t border-zinc-700 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button 
                variant="outline" 
                className="w-full border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                onClick={() => window.open(`${API}/public/estimate-approval/${token}/pdf`, '_blank')}
                data-testid="save-pdf-btn"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Save as PDF
              </Button>
              <Button 
                variant="outline" 
                className="w-full border-sky-500 text-sky-300 hover:bg-sky-500/10"
                onClick={() => {
                  // Feb 2026 owner directive: "Print button at the
                  // bottom of customer page within their estimate
                  // portal opens a save document instead of going to
                  // screen view to print immediately. I need this
                  // feature once I push the button Print, it prints."
                  //
                  // Prior implementation opened a new tab and called
                  // w.print() on the load event — unreliable because
                  // Chrome's built-in PDF viewer doesn't dispatch a
                  // clean 'load' event and mobile browsers often
                  // download the PDF instead of showing it inline.
                  // The reliable fix: hidden same-page iframe. Load
                  // the same PDF into it, wait for onload, then fire
                  // print() on the iframe's contentWindow so the
                  // browser's native Print dialog opens directly.
                  const pdfUrl = `${API}/public/estimate-approval/${token}/pdf`;
                  const existing = document.getElementById('estimate-print-iframe');
                  if (existing) existing.remove();
                  const iframe = document.createElement('iframe');
                  iframe.id = 'estimate-print-iframe';
                  iframe.style.position = 'fixed';
                  iframe.style.right = '0';
                  iframe.style.bottom = '0';
                  iframe.style.width = '0';
                  iframe.style.height = '0';
                  iframe.style.border = '0';
                  iframe.setAttribute('aria-hidden', 'true');
                  // Focus flag so a Safari popup blocker isn't tripped.
                  iframe.onload = () => {
                    try {
                      iframe.contentWindow.focus();
                      iframe.contentWindow.print();
                    } catch (err) {
                      // Fallback: open in a new tab and let the user
                      // hit Ctrl+P themselves. Only happens on very
                      // locked-down mobile PDF viewers.
                      window.open(pdfUrl, '_blank');
                    }
                  };
                  iframe.src = pdfUrl;
                  document.body.appendChild(iframe);
                }}
                data-testid="print-estimate-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Estimate
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Contract/Agreement Section - Only shown if admin included a contract */}
        {estimateData?.include_contract && estimateData?.contract_content && (
          <Card className="bg-zinc-900 border-zinc-700 mb-6" data-testid="contract-section">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <FileSignature className="w-5 h-5 text-purple-400" />
                Service Agreement / Contract
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Please review the following agreement carefully
                {estimateData?.contract_require_signature && (
                  <span className="text-purple-400 ml-1">(Signature required)</span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Download original .docx button if available */}
              {estimateData?.contract_docx_url && (
                <div className="mb-4 p-3 bg-purple-900/20 rounded-lg border border-purple-700/50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-400" />
                    <div>
                      <p className="text-white text-sm font-medium">Original Contract Document</p>
                      <p className="text-zinc-400 text-xs">{estimateData.contract_docx_name || 'Service Agreement.docx'}</p>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => window.open(`${API}${estimateData.contract_docx_url}`, '_blank')}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="download-contract-docx"
                  >
                    <Download className="w-4 h-4 mr-2" /> Download
                  </Button>
                </div>
              )}
              <div className="bg-zinc-800 rounded-lg p-4 max-h-96 overflow-y-auto border border-zinc-700">
                <pre className="text-zinc-300 text-sm whitespace-pre-wrap font-sans leading-relaxed">{estimateData.contract_content}</pre>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Customer Testimonials */}
        {estimateData?.testimonials && estimateData.testimonials.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-700 mb-6">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                What Our Customers Say
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {estimateData.testimonials.map((testimonial, idx) => (
                  <div key={idx} className="bg-zinc-800 rounded-lg p-4">
                    <div className="flex items-center gap-1 mb-2">
                      {[...Array(testimonial.rating || 5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      ))}
                    </div>
                    <p className="text-zinc-300 italic mb-2">"{testimonial.testimonial_text}"</p>
                    <p className="text-zinc-500 text-sm">
                      — {testimonial.customer_name}
                      {testimonial.project_type && <span>, {testimonial.project_type}</span>}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Terms & Conditions */}
        <Card className="bg-zinc-900 border-zinc-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center justify-between">
              Terms & Conditions
              <Button variant="ghost" size="sm" onClick={() => setShowTerms(!showTerms)} className="text-yellow-500">
                {showTerms ? 'Hide' : 'Show'}
              </Button>
            </CardTitle>
          </CardHeader>
          {showTerms && (
            <CardContent>
              <div className="bg-zinc-800 rounded-lg p-4 max-h-64 overflow-y-auto">
                <pre className="text-zinc-300 text-sm whitespace-pre-wrap font-sans">{estimateData?.terms_and_conditions}</pre>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Signature Section */}
        <Card id="signature-block" className="bg-zinc-900 border-zinc-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white text-lg">Your Signature</CardTitle>
            <CardDescription className="text-zinc-400">Please sign to approve this estimate</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Customer Name */}
            <div>
              <Label className="text-zinc-300">Your Full Name *</Label>
              <Input
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="Enter your full name"
              />
            </div>

            {/* Signature Type Toggle */}
            <div className="flex gap-2">
              <Button
                variant={signatureType === 'typed' ? 'default' : 'outline'}
                onClick={() => setSignatureType('typed')}
                className={signatureType === 'typed' ? 'bg-yellow-500 text-black' : 'border-zinc-600'}
              >
                Type Signature
              </Button>
              <Button
                variant={signatureType === 'drawn' ? 'default' : 'outline'}
                onClick={() => setSignatureType('drawn')}
                className={signatureType === 'drawn' ? 'bg-yellow-500 text-black' : 'border-zinc-600'}
              >
                Draw Signature
              </Button>
            </div>

            {/* Typed Signature */}
            {signatureType === 'typed' && (
              <div>
                <Label className="text-zinc-300">Type Your Signature *</Label>
                <Input
                  value={typedSignature}
                  onChange={(e) => setTypedSignature(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  placeholder="Type your name as signature"
                  style={{ fontFamily: 'cursive', fontSize: '24px' }}
                />
                {typedSignature && (
                  <div className="mt-2 p-4 bg-zinc-800 rounded-lg border border-zinc-600">
                    <p className="text-yellow-500 text-2xl" style={{ fontFamily: 'cursive' }}>{typedSignature}</p>
                  </div>
                )}
              </div>
            )}

            {/* Drawn Signature */}
            {signatureType === 'drawn' && (
              <div>
                <Label className="text-zinc-300">Draw Your Signature *</Label>
                <div className="mt-1 border border-zinc-600 rounded-lg overflow-hidden">
                  <canvas
                    ref={canvasRef}
                    width={500}
                    height={150}
                    className="bg-zinc-800 cursor-crosshair w-full touch-none select-none"
                    style={{ touchAction: 'none' }}
                    onPointerDown={startDrawing}
                    onPointerMove={draw}
                    onPointerUp={stopDrawing}
                    onPointerCancel={stopDrawing}
                    onPointerLeave={stopDrawing}
                    data-testid="estimate-signature-canvas"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={clearSignature} className="mt-2 border-zinc-600">
                  Clear Signature
                </Button>
              </div>
            )}

            {/* Terms Agreement */}
            <div className="flex items-start gap-3 pt-4">
              <input
                type="checkbox"
                id="agree-terms"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1"
              />
              <Label htmlFor="agree-terms" className="text-zinc-300 cursor-pointer">
                I have read and agree to the Terms & Conditions. I understand that by signing, I am approving this estimate and authorizing the work to proceed.
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Deposit Method Selector — Feb 2026. When the estimate
            requires a deposit, let the customer pick HOW to pay it.
            Online card launches Stripe Checkout; mail-check and
            on-site handover finalize the approval immediately and
            leave the deposit as "pending" on the job. The contractor
            then marks it collected when the check arrives / crew
            picks it up. This removes the Stripe-is-not-yet-live
            blocker from the signing flow. */}
        {estimateData?.require_deposit && Number(estimateData?.deposit_amount || 0) > 0 && (
          <Card className="bg-zinc-900 border-amber-500/40 mb-6" data-testid="deposit-method-selector">
            <CardHeader className="pb-3">
              <CardTitle className="text-amber-300 text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5" /> How would you like to pay the ${Number(estimateData.deposit_amount).toLocaleString()} deposit?
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Pick an option before you approve. All methods are accepted — choose whatever works best for you.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {/* Feb 2026 — Stripe online card payment is temporarily
                  disabled while the contractor finishes connecting
                  their Stripe account. The option is kept visible but
                  greyed out so customers know it's coming. Default
                  selection is "Mail a check" (see depositMethod init). */}
              <div
                className="flex items-start gap-3 p-3 rounded-lg border border-zinc-800 bg-zinc-900/40 opacity-50 cursor-not-allowed select-none"
                data-testid="deposit-method-stripe-disabled"
                aria-disabled="true"
              >
                <input
                  type="radio"
                  name="deposit_method"
                  value="stripe"
                  disabled
                  className="mt-1 accent-emerald-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-zinc-300 font-medium">Pay by Card (online)</p>
                    <span className="text-[10px] uppercase tracking-wide bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-full border border-zinc-700">
                      Coming soon
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5">Online card payment isn't available yet. Please pick one of the options below.</p>
                </div>
              </div>
              <label
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${depositMethod === 'mail_check' ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-700 hover:border-zinc-500'}`}
                data-testid="deposit-method-mail-check"
              >
                <input
                  type="radio"
                  name="deposit_method"
                  value="mail_check"
                  checked={depositMethod === 'mail_check'}
                  onChange={() => setDepositMethod('mail_check')}
                  className="mt-1 accent-emerald-500"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium">Mail a check</p>
                  <p className="text-xs text-zinc-500 mt-0.5">We'll follow up by email with where to send it. Your estimate is approved as soon as you sign.</p>
                </div>
              </label>
              <label
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${depositMethod === 'on_site' ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-700 hover:border-zinc-500'}`}
                data-testid="deposit-method-on-site"
              >
                <input
                  type="radio"
                  name="deposit_method"
                  value="on_site"
                  checked={depositMethod === 'on_site'}
                  onChange={() => setDepositMethod('on_site')}
                  className="mt-1 accent-emerald-500"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium">Pay on-site when the crew arrives</p>
                  <p className="text-xs text-zinc-500 mt-0.5">Hand check or cash to our crew on day one. Your estimate is approved as soon as you sign.</p>
                </div>
              </label>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons.
            Feb 2026 — Approve stays visually locked (grey, cursor-not-
            allowed) until the customer has: ticked the terms box, filled
            their legal name, AND captured a signature. Previously the
            button lit up on terms-check alone, so a customer could tap
            it without actually signing and get a confusing error toast.
            Keeping the same validation on click as a defense in depth. */}
        {(() => {
          const liveSignature = signatureType === 'typed' ? typedSignature : drawnSignature;
          const readyToApprove = agreedToTerms && !!liveSignature && !!customerName.trim();
          return (
            <>
              {!readyToApprove && (
                <div className="text-xs text-amber-300/90 mb-2 text-center" data-testid="approval-ready-hint">
                  {!customerName.trim() && 'Enter your name · '}
                  {!liveSignature && 'Sign above · '}
                  {!agreedToTerms && 'Accept the terms '}
                  to enable the Approve button.
                </div>
              )}
              <div className="flex gap-4">
                <Button
                  onClick={handleDecline}
                  variant="outline"
                  className="flex-1 border-zinc-600 text-zinc-400 hover:bg-zinc-800"
                  disabled={submitting}
                >
                  Decline Estimate
                </Button>
                <Button
                  onClick={handleApprove}
                  className={`flex-1 ${readyToApprove ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'}`}
                  disabled={submitting || !readyToApprove}
                  data-testid="approve-estimate-btn"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      {estimateData?.require_deposit
                        ? (depositMethod === 'stripe'
                            ? `Approve & Pay $${estimateData.deposit_amount?.toLocaleString()} Deposit`
                            : `Approve Estimate (deposit by ${depositMethod === 'mail_check' ? 'mail' : 'on-site'})`)
                        : 'Approve Estimate'}
                    </span>
                  )}
                </Button>
              </div>
            </>
          );
        })()}

        {/* Brand attribution — full footer banner. Customer-facing public
            pages aren't subject to Gmail's classifier, so this is where
            CrewSync's brand IS shown loud (vs. emails which stay personal-
            feeling). Drives trial signups via the customer→referral funnel. */}
        <PoweredByCrewSync variant="footer" tenantName={estimateData?.company_name} />
      </div>
      {/* Snapshot lightbox — customer zoom view of a measured area PNG */}
      {snapshotZoom && (
        <div
          className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSnapshotZoom(null)}
          data-testid="approval-snapshot-lightbox"
        >
          <button
            type="button"
            onClick={() => setSnapshotZoom(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white bg-zinc-900/60 border border-zinc-700 rounded-full p-2"
            data-testid="approval-snapshot-lightbox-close"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-6xl max-h-[95vh] w-full flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <img
              src={`${BACKEND_URL}${snapshotZoom.url}`}
              alt={snapshotZoom.caption || 'Measured area'}
              className="max-h-[85vh] max-w-full object-contain rounded"
            />
            <div className="text-center">
              <p className="text-white font-medium text-lg">{snapshotZoom.caption || 'Measured area'}</p>
              <p className="text-sm text-zinc-300 mt-1">
                {snapshotZoom.total_sqft ? `${(snapshotZoom.total_sqft || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} sqft` : ''}
                {snapshotZoom.total_sqft && snapshotZoom.total_linear_ft ? ' · ' : ''}
                {snapshotZoom.total_linear_ft ? `${(snapshotZoom.total_linear_ft || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} lft` : ''}
                {snapshotZoom.scale_text ? ` · scale ${snapshotZoom.scale_text}` : (snapshotZoom.scale_ft_per_inch ? ` · scale 1 in = ${snapshotZoom.scale_ft_per_inch} ft` : '')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function JobConfirmationPage() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading, success, already_confirmed, error
  const [jobData, setJobData] = useState(null);

  useEffect(() => {
    confirmJob();
  }, [token]);

  const confirmJob = async () => {
    try {
      const res = await axios.get(`${API}/jobs/confirm/${token}`);
      setJobData(res.data);
      if (res.data.already_confirmed) {
        setStatus('already_confirmed');
      } else {
        setStatus('success');
      }
    } catch (err) {
      console.error('Confirmation error:', err);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4" data-testid="job-confirmation-page">
      <Card className="max-w-md w-full bg-zinc-900 border-zinc-700">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <>
              <div className="animate-spin w-12 h-12 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <h1 className="text-xl font-heading font-bold text-white">Confirming Your Job...</h1>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold text-white mb-2">Job Confirmed!</h1>
              <p className="text-zinc-400 mb-4">Thank you for confirming your upcoming job.</p>
              {jobData && (
                <div className="bg-zinc-800 rounded-lg p-4 mb-4 text-left">
                  <p className="text-primary font-bold text-lg">{jobData.job_title}</p>
                  {jobData.start_date && (
                    <p className="text-zinc-300 text-sm mt-2">
                      <strong>Start Date:</strong> {new Date(jobData.start_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  )}
                </div>
              )}
              <p className="text-zinc-500 text-sm">Our team will arrive as scheduled. Please ensure the work area is accessible.</p>
            </>
          )}
          {status === 'already_confirmed' && (
            <>
              <CheckCircle className="w-16 h-16 text-blue-400 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold text-white mb-2">Already Confirmed</h1>
              <p className="text-zinc-400 mb-4">This job has already been confirmed. You're all set!</p>
              {jobData && (
                <div className="bg-zinc-800 rounded-lg p-4 text-left">
                  <p className="text-primary font-bold text-lg">{jobData.job_title}</p>
                </div>
              )}
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold text-white mb-2">Invalid Link</h1>
              <p className="text-zinc-400">This confirmation link is invalid or has expired. Please contact us if you have questions about your scheduled job.</p>
            </>
          )}
        </CardContent>
      </Card>
      <PoweredByCrewSync variant="subtle" />
    </div>
  );
}

// Password Setup Page (Public - for newly activated accounts)
function PasswordSetupPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, ready, success, error
  const [userData, setUserData] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await axios.get(`${API}/auth/setup-password/${token}`);
      setUserData(res.data);
      setStatus('ready');
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Invalid or expired link');
      setStatus('error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/setup-password`, {
        token,
        new_password: password
      });
      // Clear any stale localStorage data for fresh start
      localStorage.removeItem("original_admin_token");
      localStorage.removeItem("original_admin_user");
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setStatus('success');
      toast.success('Password set successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set password');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4" data-testid="password-setup-page">
      <Card className="max-w-md w-full bg-zinc-900 border-zinc-700">
        <CardContent className="p-8">
          {status === 'loading' && (
            <div className="text-center">
              <div className="animate-spin w-12 h-12 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <h1 className="text-xl font-heading font-bold text-white">Verifying...</h1>
            </div>
          )}
          
          {status === 'ready' && userData && (
            <>
              <div className="text-center mb-6">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h1 className="text-2xl font-heading font-bold text-white mb-2">Set Your Password</h1>
                <p className="text-zinc-400">Welcome, <strong className="text-white">{userData.name}</strong>!</p>
                <p className="text-zinc-500 text-sm">{userData.company_name}</p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Email</Label>
                  <Input 
                    type="email" 
                    value={userData.email} 
                    disabled 
                    className="mt-1 bg-zinc-800 border-zinc-700 text-zinc-400"
                  />
                </div>
                <div>
                  <Label>New Password</Label>
                  <Input 
                    type="password" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                    required
                    className="mt-1 bg-zinc-800 border-zinc-600"
                  />
                </div>
                <div>
                  <Label>Confirm Password</Label>
                  <Input 
                    type="password" 
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat password"
                    required
                    className="mt-1 bg-zinc-800 border-zinc-600"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-primary text-black font-bold"
                >
                  {submitting ? 'Setting Password...' : 'SET PASSWORD & CONTINUE'}
                </Button>
              </form>
            </>
          )}
          
          {status === 'success' && (
            <div className="text-center">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold text-white mb-2">You're All Set!</h1>
              <p className="text-zinc-400 mb-6">Your password has been set. You can now log in to your account.</p>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-primary text-black font-bold"
              >
                GO TO LOGIN
              </Button>
            </div>
          )}
          
          {status === 'error' && (
            <div className="text-center">
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold text-white mb-2">Link Invalid</h1>
              <p className="text-zinc-400">{errorMsg}</p>
              <p className="text-zinc-500 text-sm mt-4">Please contact support if you need assistance.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Main App
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <EditCustomerProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors theme="dark" />
            <Suspense fallback={<RouteLoader />}>
            <Routes>
          {/* Public Landing Page */}
          <Route path="/get-started" element={<LandingPage />} />
          <Route path="/landing" element={<LandingPage />} />
          
          <Route path="/login" element={<LoginPage />} />
          <Route path="/r/:token" element={<PublicRoutePage />} />
          <Route path="/task/:token" element={<PublicTaskPage />} />
          <Route path="/assessments/share/:token/print" element={<AssessmentPrintPage />} />
          <Route path="/assessments/share/:token" element={<PublicAssessmentPage />} />
          <Route path="/review/:token" element={<PublicReviewPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/confirm-appointment/:token" element={<ConfirmAppointmentPage />} />
          <Route path="/sign-document/:token" element={<SignDocumentPage />} />
          <Route path="/sign-policy/:docId/:token" element={<SignPolicyDocumentPage />} />
          <Route path="/sign-employee-document" element={<DocumentSigningPage />} />
          <Route path="/sign/contract/:token" element={<PublicContractSignPage />} />
          <Route path="/shopping-list-action" element={<ShoppingListActionPage />} />
          <Route path="/" element={<Navigate to="/dashboard" />} />
          
          {/* Platform Admin Routes */}
          <Route path="/platform" element={<AdminRoute><Layout><PlatformAdminPage /></Layout></AdminRoute>} />
          <Route path="/platform/companies" element={<AdminRoute><Layout><PlatformAdminPage /></Layout></AdminRoute>} />
          <Route path="/platform/billing" element={<AdminRoute><Layout><PlatformBillingPage /></Layout></AdminRoute>} />
          <Route path="/platform/revenue" element={<AdminRoute><Layout><PlatformAdminPage /></Layout></AdminRoute>} />
          <Route path="/platform/support" element={<AdminRoute><Layout><SupportPage /></Layout></AdminRoute>} />
          <Route path="/platform/code-health" element={<AdminRoute><Layout><CodeHealthPage /></Layout></AdminRoute>} />
          <Route path="/platform/frontend-errors" element={<AdminRoute><Layout><FrontendErrorsPage /></Layout></AdminRoute>} />
          <Route path="/platform/tenant-regions" element={<AdminRoute><Layout><PlatformTenantRegionsPage /></Layout></AdminRoute>} />
          <Route path="/platform/tenant-routes" element={<AdminRoute><Layout><PlatformTenantRoutesPage /></Layout></AdminRoute>} />
          <Route path="/platform/deliverability-health" element={<AdminRoute><Layout><PlatformDeliverabilityHealthPage /></Layout></AdminRoute>} />
          
          {/* Platform Owner EXCLUSIVE Routes - Only accessible by platform_admin */}
          <Route path="/owner/analytics" element={<AdminRoute><Layout><PlatformOwnerPage section="analytics" /></Layout></AdminRoute>} />
          <Route path="/owner/data-export" element={<AdminRoute><Layout><PlatformOwnerPage section="data-export" /></Layout></AdminRoute>} />
          <Route path="/owner/feature-lab" element={<AdminRoute><Layout><PlatformOwnerPage section="feature-lab" /></Layout></AdminRoute>} />
          <Route path="/owner/system-health" element={<AdminRoute><Layout><PlatformOwnerPage section="system-health" /></Layout></AdminRoute>} />
          <Route path="/owner/audit-logs" element={<AdminRoute><Layout><PlatformOwnerPage section="audit-logs" /></Layout></AdminRoute>} />
          <Route path="/owner/impersonate" element={<AdminRoute><Layout><PlatformOwnerPage section="impersonate" /></Layout></AdminRoute>} />
          
          {/* Admin Routes */}
          <Route path="/welcome" element={<AdminRoute><WelcomePage /></AdminRoute>} />
          <Route path="/dashboard" element={<AdminRoute><Layout><DashboardPage /></Layout></AdminRoute>} />
          <Route path="/calendar" element={<SharedFeatureRoute feature="calendar" featureName="Calendar & Scheduling"><Layout><CalendarPage /></Layout></SharedFeatureRoute>} />
          <Route path="/reminders" element={<AdminRoute><Layout><RemindersPage /></Layout></AdminRoute>} />
          <Route path="/customers" element={<AdminRoute><Layout><CustomersPage /></Layout></AdminRoute>} />
          <Route path="/site-assessments" element={<AdminRoute><Layout><SiteAssessmentsPage /></Layout></AdminRoute>} />
          <Route path="/assessments/:id/print" element={<AdminRoute><AssessmentPrintPage /></AdminRoute>} />
          <Route path="/customers/:id" element={<AdminRoute><Layout><CustomerDetailPage /></Layout></AdminRoute>} />
          <Route path="/leads" element={<AdminRoute><Layout><LeadsPage /></Layout></AdminRoute>} />
          <Route path="/jobs" element={<AdminRoute><Layout><JobsPage /></Layout></AdminRoute>} />
          <Route path="/upcoming-maintenance" element={<AdminRoute><Layout><UpcomingMaintenancePage /></Layout></AdminRoute>} />
          <Route path="/warranty" element={<AdminRoute><Layout><WarrantyClaimsPage /></Layout></AdminRoute>} />
          <Route path="/jobs/:id/report" element={<AdminRoute><JobReportPage /></AdminRoute>} />
          <Route path="/reports" element={<AdminRoute><Layout><ReportsPage /></Layout></AdminRoute>} />
          <Route path="/time" element={<AdminRoute><Layout><TimeTrackingPage /></Layout></AdminRoute>} />
          <Route path="/pricing" element={<ProFeatureRoute feature="profitable_estimates" featureName="Profitable Pricing"><Layout><PricingPage /></Layout></ProFeatureRoute>} />
          <Route path="/maintenance-reminders" element={<ProtectedRoute><Layout><MaintenanceRemindersPage /></Layout></ProtectedRoute>} />
          <Route path="/materials" element={<SharedFeatureRoute feature="material_catalog" featureName="Material Catalog"><Layout><MaterialsCatalogPage /></Layout></SharedFeatureRoute>} />
          <Route path="/vendors" element={<SharedFeatureRoute feature="vendor_lists" featureName="Vendors & Purchase Orders"><Layout><VendorsPage /></Layout></SharedFeatureRoute>} />
          <Route path="/equipment" element={<SharedFeatureRoute feature="equipment_management" featureName="Equipment Catalog"><Layout><EquipmentCatalogPage /></Layout></SharedFeatureRoute>} />
          <Route path="/vehicles" element={<SharedFeatureRoute feature="vehicle_service_records" featureName="Vehicle Service Records"><Layout><VehiclesPage /></Layout></SharedFeatureRoute>} />
          <Route path="/subcontractors" element={<SharedFeatureRoute feature="subcontractors_module" featureName="Subcontractors"><Layout><SubcontractorsPage /></Layout></SharedFeatureRoute>} />
          <Route path="/communications" element={<SharedFeatureRoute feature="subcontractors_module" featureName="Communications"><Layout><CommunicationsPage /></Layout></SharedFeatureRoute>} />
          <Route path="/coverage-calculator" element={<ProFeatureRoute feature="coverage_calculator" featureName="Coverage Calculator"><Layout><CoverageCalculatorPage /></Layout></ProFeatureRoute>} />
          <Route path="/estimates" element={<ProFeatureRoute feature="basic_estimates" featureName="Estimates & Proposals"><Layout><EstimatesPage /></Layout></ProFeatureRoute>} />
          <Route path="/proposals" element={<Navigate to="/estimates" replace />} />
          <Route path="/invoices" element={<AdminRoute><Layout><InvoicesPage /></Layout></AdminRoute>} />
          <Route path="/payments" element={<AdminRoute><Layout><PaymentsPage /></Layout></AdminRoute>} />
          <Route path="/deposits" element={<AdminRoute><Layout><DepositsPage /></Layout></AdminRoute>} />
          <Route path="/contracts" element={<AdminRoute><Layout><ContractsPage /></Layout></AdminRoute>} />
          <Route path="/signed-documents" element={<AdminRoute><Layout><SignedDocumentsPage /></Layout></AdminRoute>} />
          <Route path="/team" element={<AdminRoute><Layout><TeamManagementPage /></Layout></AdminRoute>} />
          <Route path="/employees" element={<Navigate to="/team" replace />} />
          <Route path="/employee-tasks" element={<Navigate to="/team?tab=tasks" replace />} />
          <Route path="/rewards" element={<Navigate to="/team?tab=rewards" replace />} />
          <Route path="/support" element={<ProtectedRoute><Layout><SupportPage /></Layout></ProtectedRoute>} />
          <Route path="/help-videos" element={<ProtectedRoute><Layout><HelpVideosPage /></Layout></ProtectedRoute>} />
          <Route path="/help" element={<ProtectedRoute><Layout><HelpCenterPage /></Layout></ProtectedRoute>} />
          <Route path="/settings/estimate-followups" element={<AdminRoute><Layout><EstimateFollowupTemplatesPage /></Layout></AdminRoute>} />
          <Route path="/settings/invoice-reminders" element={<AdminRoute><Layout><InvoiceReminderTemplatesPage /></Layout></AdminRoute>} />
          <Route path="/settings/email-deliverability" element={<AdminRoute><Layout><EmailDeliverabilityPage /></Layout></AdminRoute>} />
          <Route path="/service-regions" element={<ProtectedRoute><Layout><ServiceRegionsPage /></Layout></ProtectedRoute>} />
          <Route path="/future-ideas" element={<AdminRoute><Layout><FutureIdeasPage /></Layout></AdminRoute>} />
          <Route path="/settings" element={<AdminRoute><Layout><SettingsPage /></Layout></AdminRoute>} />
          <Route path="/trial-management" element={<AdminRoute><Layout><TrialManagementPage /></Layout></AdminRoute>} />
          <Route path="/data-import" element={<AdminRoute><Layout><DataImportPage /></Layout></AdminRoute>} />
          <Route path="/profitability" element={<AdminRoute><Layout><CompanyProfitabilityPage /></Layout></AdminRoute>} />
          <Route path="/financial-analytics" element={<AdminRoute><Layout><FinancialAnalyticsPage /></Layout></AdminRoute>} />
          <Route path="/routes" element={<SharedFeatureRoute feature="service_regions" featureName="Routes & Dispatch"><Layout><RoutesPage /></Layout></SharedFeatureRoute>} />
          <Route path="/takeoffs" element={<SharedFeatureRoute feature="takeoff_tool" featureName="Blueprint Takeoff"><Layout><TakeoffsDashboardPage /></Layout></SharedFeatureRoute>} />
          <Route path="/takeoffs/:takeoffId" element={<SharedFeatureRoute feature="takeoff_tool" featureName="Blueprint Takeoff"><TakeoffEditorPage /></SharedFeatureRoute>} />
          <Route path="/reviews" element={<AdminRoute><Layout><ReviewsAdminPage /></Layout></AdminRoute>} />
          
          {/* Employee Routes */}
          <Route path="/my-jobs" element={<ProtectedRoute><Layout><EmployeeJobsPage /></Layout></ProtectedRoute>} />
          <Route path="/my-tasks" element={<ProtectedRoute><Layout><EmployeeTasksPage /></Layout></ProtectedRoute>} />
          <Route path="/my-timecard" element={<ProtectedRoute><Layout><EmployeeTimeCardPage /></Layout></ProtectedRoute>} />
          <Route path="/my-rewards" element={<ProtectedRoute><Layout><EmployeeRewardsPage /></Layout></ProtectedRoute>} />
          
          {/* Public Customer Portal Routes (No Auth Required) */}
          <Route path="/proposal/:token" element={<CustomerProposalPortal />} />
          <Route path="/proposal/:token/success" element={<ProposalPaymentSuccess />} />
          <Route path="/confirm-job/:token" element={<JobConfirmationPage />} />
          <Route path="/estimate-approval/:token" element={<EstimateApprovalPage />} />
          <Route path="/book/:slug" element={<PublicBookingPage />} />
          <Route path="/warranty/report" element={<PublicWarrantyForm />} />
          <Route path="/get-started/pricing" element={<PublicPricingPage />} />
          <Route path="/platform/sales-checklist" element={<AdminRoute><Layout><SalesReadyChecklistPage /></Layout></AdminRoute>} />
          {/* Printable onboarding checklist — no Layout so print stays clean */}
          <Route path="/platform/onboarding-checklist/print" element={<AdminRoute><OnboardingChecklistPrintPage /></AdminRoute>} />
          <Route path="/platform/onboarding" element={<AdminRoute><Layout><PlatformOnboardingPlanPage /></Layout></AdminRoute>} />
          <Route path="/pay/:token" element={<PublicPaymentRequestPage />} />
          <Route path="/setup-password/:token" element={<PasswordSetupPage />} />
          </Routes>
            </Suspense>
          </BrowserRouter>
        </EditCustomerProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
